/**
 * MMO Game Scene
 * Main hex grid world rendering with fog of war and chunk loading
 */

import Phaser from 'phaser';
import { ServiceLocator, Services } from '../../utils/ServiceLocator';
import { MMOStateManager } from '../../managers/MMOStateManager';
import { MMONetworkManager, MMOEvents } from '../../managers/MMONetworkManager';
import { EventBus } from '../../utils/EventBus';
import { axialToPixel, pixelToAxial, hexDistance, hexNeighbors, hexKey as toHexKey, hexRing, type HexCoord, HEX_WIDTH, HEX_HEIGHT, ISO_Y_SCALE, getFootprintHexes, getPlanetFootprint, STATION_FOOTPRINT, type FootprintSize } from '../../shared/HexUtils';
import { MMOUnit } from '../../entities/mmo/MMOUnit';
import { MMOPlanet } from '../../entities/mmo/MMOPlanet';
import { MMOAsteroid } from '../../entities/mmo/MMOAsteroid';
import { MMOStation } from '../../entities/mmo/MMOStation';
import { MMOOrbitalPlatform } from '../../entities/mmo/MMOOrbitalPlatform.js';
import { MMOWreck } from '../../entities/mmo/MMOWreck';
import { MMOAmbientEntity } from '../../entities/mmo/MMOAmbientEntity';
import { MMODroppedRelic, type DroppedRelicData } from '../../entities/mmo/MMODroppedRelic';
import { MMOLootDrop } from '../../entities/mmo/MMOLootDrop';
// BuildMenuDialog removed — ships are purchased from Ship Store, not built
import { type CombatAccumulator, createEmptySideStats, accumulateCombatResult } from '../../ui/mmo/BattleReportDialog';
import { UpgradeMiningDialog } from '../../ui/mmo/UpgradeMiningDialog';
// OfflineRecapDialog is created in MMOHUDScene via 'show-offline-recap' event
import { MemoryEvents } from '../../managers/MemoryManager';
import { isMobile as detectMobile } from '../../utils/ResponsiveHelper';
import { ClickTracker } from '../../utils/ClickTracker';

const CHUNK_SIZE = 32; // 32x32 hexes per chunk (matches server ChunkCache)
const VIEWPORT_PADDING = 50; // Extra hexes to render outside viewport
const CHUNK_REQUEST_DEBOUNCE = 500; // ms

// ── Combat timing constants ──────────────────────────────────
const BEAM_WEAPON_DELAY = 250;
const MISSILE_WEAPON_DELAY = 300;
const BEAM_DAMAGE_DELAY = 100;
const MISSILE_DAMAGE_DELAY = 200;
const DAMAGE_NUMBER_DURATION = 2500;
const MAX_VISUAL_EFFECTS_PER_SIDE = 4; // Cap weapon effects to prevent event loop saturation

type AttackTarget = { entityId: string; position: HexCoord };

export class MMOGameScene extends Phaser.Scene {
  // Managers
  private mmoState!: MMOStateManager;
  private mmoNetwork!: MMONetworkManager;

  // Graphics layers
  private hexGraphics!: Phaser.GameObjects.Graphics;
  private hoverGraphics!: Phaser.GameObjects.Graphics;
  private fogGraphics!: Phaser.GameObjects.Graphics;

  // ── Screen-locked overlays (background nebula, fixed starfield, vignette) ──
  // These have setScrollFactor(0) so they stay locked to the camera viewport,
  // BUT they still get scaled by camera zoom in Phaser. When zoom drops below
  // 1.0 (e.g. 0.35x intro/min-zoom), the images visually shrink and expose a
  // dark rectangle in the middle of the screen with the unwrapped world bleeding
  // through outside. We fix that in update() by inverse-scaling them to the
  // current camera.zoom so they always cover exactly the viewport.
  private bgImage?: Phaser.GameObjects.Image;
  private starsFixedImage?: Phaser.GameObjects.Image;
  private worldStars?: Phaser.GameObjects.TileSprite;
  private vignetteImage?: Phaser.GameObjects.Image;
  private bgBaseWidth: number = 0;
  private bgBaseHeight: number = 0;
  private lastSyncedZoom: number = -1;
  private repairPurchaseOverlay: HTMLDivElement | null = null;

  // Hex hover tracking
  private lastHoveredHexKey: string = '';
  private movementOverlay!: Phaser.GameObjects.Graphics;
  private attackOverlay!: Phaser.GameObjects.Graphics;
  private mineOverlay!: Phaser.GameObjects.Graphics;
  private stackCounterGraphics!: Phaser.GameObjects.Graphics;
  private territoryGraphics!: Phaser.GameObjects.Graphics;
  private entitiesContainer!: Phaser.GameObjects.Container;

  // Camera state
  private chatInputFocused: boolean = false;
  private isDragging: boolean = false;
  private dragStartX: number = 0;
  private dragStartY: number = 0;
  private dragCameraX: number = 0;
  private dragCameraY: number = 0;
  /** True when pointerDown happened in a state where a real drag should
   *  disengage camera-follow. Consumed once the drag crosses the tap/drag
   *  threshold so a plain tap never breaks follow mode. */
  private pendingFollowDisengage: boolean = false;

  /** Width of the CommandDock's left-side column on mobile (sidebar or info
   *  panel). Updated via mmo-command-dock-shown. Used to expand the input-
   *  blocking zone so map drags don't start inside the panel. 0 when the
   *  dock is hidden or is the bottom-anchored desktop layout. */
  private commandDockLeftW: number = 0;

  // Pinch-to-zoom state
  private isPinching: boolean = false;
  private pinchStartDist: number = 0;
  private pinchStartZoom: number = 1;
  private lastCameraX: number = 0;
  private lastCameraY: number = 0;
  private cameraFollowEnabled: boolean = true;
  private cameraFollowTarget: MMOUnit | null = null;
  private visibilityHandler: (() => void) | null = null;

  // Viewport tracking
  private viewportBounds = { minQ: 0, maxQ: 0, minR: 0, maxR: 0 };
  private cullingBounds = { left: 0, right: 0, top: 0, bottom: 0 };
  private loadedChunks: Set<string> = new Set();
  private chunkRequestTimer: NodeJS.Timeout | null = null;

  // Entity tracking
  private unitSprites: Map<string, MMOUnit> = new Map();
  private previousTickUnitIds: Set<string> = new Set(); // Track units visible last tick for fog-snap
  private planetSprites: Map<string, MMOPlanet> = new Map();
  private asteroidSprites: Map<string, MMOAsteroid> = new Map();
  private stationSprites: Map<string, MMOStation> = new Map();
  private platformSprites: Map<string, MMOOrbitalPlatform> = new Map();
  private wreckSprites: Map<string, MMOWreck> = new Map();
  private ambientSprites: Map<string, MMOAmbientEntity> = new Map();
  private droppedRelicSprites: Map<string, MMODroppedRelic> = new Map();
  private lootDropSprites: Map<string, MMOLootDrop> = new Map();

  // Selection tracking
  private selectedEntity: MMOUnit | MMOPlanet | MMOAsteroid | MMOStation | MMOOrbitalPlatform | MMOWreck | MMOAmbientEntity | MMODroppedRelic | MMOLootDrop | null = null;

  // Movement mode tracking
  private isMovementMode: boolean = false;
  private movementModeActivatedAt: number = 0; // timestamp to prevent same-click race
  private movementUnit: MMOUnit | null = null;
  private blockedHexKeys: Set<string> = new Set();

  // Movement destination markers (unitId -> graphics + ETA label + cached destination pixel)
  private movementMarkers: Map<string, { gfx: Phaser.GameObjects.Graphics; eta: Phaser.GameObjects.Text; destPixel: { x: number; y: number } }> = new Map();

  // Attack mode tracking
  private isAttackMode: boolean = false;
  private attackingUnit: MMOUnit | null = null;
  private validAttackTargets: Map<string, AttackTarget> = new Map();

  // Mine-target mode tracking
  private isMineMode: boolean = false;
  private miningUnit: MMOUnit | null = null;

  // Relocation mode tracking (station/platform hex-picker)
  private isRelocationMode: boolean = false;
  private relocationModeActivatedAt: number = 0;
  private relocatingEntityId: string | null = null;
  private relocatingEntityType: 'station' | 'platform' | null = null;
  private validMineTargets: Map<string, { asteroidId: string; position: HexCoord }> = new Map();
  private activeMiningPopups: Phaser.GameObjects.Text[] = [];

  // Structure deploy mode tracking
  private isStructureDeployMode: boolean = false;
  private structureDeployType: string | null = null;
  private structureDeployActivatedAt: number = 0;

  // Engagement tracking
  private engagedUnitIds: Set<string> = new Set();

  // Combat accumulators for battle reports (keyed by sorted unit ID pair)
  private combatAccumulators: Map<string, CombatAccumulator> = new Map();

  // Screen shake cooldown to prevent excessive shaking during multi-hit combat
  private lastShakeTime: number = 0;

  // Combat animation guard — prevents expensive renderEntities() during active combat effects
  private combatAnimating: boolean = false;
  private combatAnimationTimers: Phaser.Time.TimerEvent[] = [];

  constructor() {
    super({ key: 'MMOGameScene' });
  }

  init(): void {
    console.log('[MMOGame] Initializing...');

    // Get MMO services
    this.mmoState = ServiceLocator.get<MMOStateManager>(Services.MMO_STATE);
    this.mmoNetwork = ServiceLocator.get<MMONetworkManager>(Services.MMO_NETWORK);
  }

  create(): void {
    console.log('[MMOGame] Creating scene...');

    // Initialize click tracking — wraps EventBus.emit to forward tracked events to /api/analytics/clicks
    ClickTracker.init();

    // Clean up stale entity sprites from previous scene lifecycle.
    // When Phaser restarts a scene, the display list is cleared but the scene
    // instance (and its Maps) are reused. Old sprites become orphaned (their
    // .scene ref is null) so we must clear the Maps to let renderEntities()
    // create fresh sprites for the new display list.
    this.unitSprites.forEach((s) => { try { s.destroy(); } catch (_) { /* already destroyed */ } });
    this.unitSprites.clear();
    this.planetSprites.forEach((s) => { try { s.destroy(); } catch (_) { /* already destroyed */ } });
    this.planetSprites.clear();
    this.asteroidSprites.forEach((s) => { try { s.destroy(); } catch (_) { /* already destroyed */ } });
    this.asteroidSprites.clear();
    this.stationSprites.forEach((s) => { try { s.destroy(); } catch (_) { /* already destroyed */ } });
    this.stationSprites.clear();
    this.platformSprites.forEach((s) => { try { s.destroy(); } catch (_) { /* already destroyed */ } });
    this.platformSprites.clear();
    this.wreckSprites.forEach((s) => { try { s.destroy(); } catch (_) { /* already destroyed */ } });
    this.wreckSprites.clear();
    this.ambientSprites.forEach((s) => { try { s.destroy(); } catch (_) { /* already destroyed */ } });
    this.ambientSprites.clear();
    this.lootDropSprites.forEach((s) => { try { s.destroy(); } catch (_) { /* already destroyed */ } });
    this.lootDropSprites.clear();
    this.selectedEntity = null;
    this.closeShipRepairPurchasePrompt();

    // Create background
    this.createBackground();

    // Create graphics layers
    this.hexGraphics = this.add.graphics();
    this.hexGraphics.setDepth(0);

    this.hoverGraphics = this.add.graphics();
    this.hoverGraphics.setDepth(0.5);

    this.fogGraphics = this.add.graphics();
    this.fogGraphics.setDepth(1);

    this.territoryGraphics = this.add.graphics();
    this.territoryGraphics.setDepth(0.8); // Above hex grid, below fog

    this.movementOverlay = this.add.graphics();
    this.movementOverlay.setDepth(1.5);

    this.attackOverlay = this.add.graphics();
    this.attackOverlay.setDepth(1.5);

    this.mineOverlay = this.add.graphics();
    this.mineOverlay.setDepth(1.5);

    this.stackCounterGraphics = this.add.graphics();
    this.stackCounterGraphics.setDepth(15); // Above units

    this.entitiesContainer = this.add.container(0, 0);
    this.entitiesContainer.setDepth(2);

    // Note: Left sidebar is created in MMOHUDScene, not here

    // Setup camera (infinite scrolling, no bounds)
    this.setupCamera();

    // Setup input handlers
    this.setupInput();

    // Setup network event listeners
    this.setupNetworkHandlers();

    // Setup player action handlers
    this.setupActionHandlers();

    // Register shutdown handler to clean up EventBus listeners
    this.events.on('shutdown', this.shutdown, this);

    // Initial render
    this.updateViewport();
    this.renderVisibleHexes();
    this.renderFogOfWar();
    this.renderTerritoryBorders();
    this.renderEntities();
    this.updateStackCounts();

    // Auto-select the player's ship and play zoom-in intro
    this.autoSelectAndZoomIn();

    // Show "While You Were Away" recap via HUD scene (fixed camera = correct input)
    const offlineRecap = this.mmoState.consumeOfflineRecap();
    if (offlineRecap && offlineRecap.length > 0) {
      this.time.delayedCall(800, () => {
        EventBus.emit('show-offline-recap', offlineRecap);
      });
    }

    // ── Mobile: re-flow on viewport resize / orientation change ──────────
    // Regenerates the background + re-renders visible hexes + fog so the
    // grid stays aligned when users rotate their phone.
    let lastBgW = this.cameras.main.width;
    let lastBgH = this.cameras.main.height;
    const onSceneResize = (gameSize: Phaser.Structs.Size) => {
      const w = gameSize.width;
      const h = gameSize.height;
      // Only regenerate the background texture if the size changed enough to matter
      if (Math.abs(w - lastBgW) > 4 || Math.abs(h - lastBgH) > 4) {
        lastBgW = w;
        lastBgH = h;
        try { this.createBackground(); } catch { /* non-fatal */ }
      }
      this.updateViewport();
      this.renderVisibleHexes();
      this.renderFogOfWar();
      EventBus.emit('mmo-scene-resized', { width: w, height: h });
    };
    this.scale.on('resize', onSceneResize);

    // ── Mobile: ModeIndicatorBanner cancel routing ──────────────────────
    // The banner emits this event when the user taps its "CANCEL" button.
    // We dispatch to whichever targeting mode is active (same logic as ESC).
    const onCancelCurrentMode = () => {
      if (this.isStructureDeployMode) {
        this.cancelStructureDeployMode();
      } else if (this.isRelocationMode) {
        this.cancelRelocationMode();
      } else if (this.isMovementMode) {
        this.cancelMovementMode();
      } else if (this.isAttackMode) {
        this.cancelAttackMode();
      } else if (this.isMineMode) {
        this.cancelMineMode();
      }
    };
    EventBus.on('mmo-cancel-current-mode', onCancelCurrentMode);

    // Cleanup on scene shutdown — prevents listener leaks across scene restarts
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.scale.off('resize', onSceneResize);
      EventBus.off('mmo-cancel-current-mode', onCancelCurrentMode);
    });

    console.log('[MMOGame] Scene created');
  }

  private createBackground(): void {
    const { width, height } = this.cameras.main;

    // Cache base dimensions so update() can recompute scale from current zoom.
    this.bgBaseWidth = width;
    this.bgBaseHeight = height;

    // Resize-safe: destroy any prior overlay sprites so we don't stack layers
    // every time onSceneResize fires (orientation change / DevTools resize).
    if (this.bgImage) { this.bgImage.destroy(); this.bgImage = undefined; }
    if (this.starsFixedImage) { this.starsFixedImage.destroy(); this.starsFixedImage = undefined; }
    if (this.worldStars) { this.worldStars.destroy(); this.worldStars = undefined; }
    if (this.vignetteImage) { this.vignetteImage.destroy(); this.vignetteImage = undefined; }

    // Layer 1: Radial gradient nebula — fixed to screen (matches Figma spec)
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d')!;

    // Base: dark space gradient (#0A0A1A center -> #000008 edge)
    const diag = Math.sqrt(width * width + height * height) / 2;
    const base = ctx.createRadialGradient(width * 0.5, height * 0.5, 0, width * 0.5, height * 0.5, diag);
    base.addColorStop(0, '#0A0A1A');
    base.addColorStop(1, '#000008');
    ctx.fillStyle = base;
    ctx.fillRect(0, 0, width, height);

    // Blue nebula glow (20% x, 50% y)
    const blueR = diag * 0.7;
    const blue = ctx.createRadialGradient(width * 0.2, height * 0.5, 0, width * 0.2, height * 0.5, blueR);
    blue.addColorStop(0, 'rgba(0,102,255,0.15)');
    blue.addColorStop(0.6, 'rgba(0,102,255,0)');
    ctx.fillStyle = blue;
    ctx.fillRect(0, 0, width, height);

    // Magenta nebula glow (80% x, 30% y)
    const magR = diag * 0.7;
    const magenta = ctx.createRadialGradient(width * 0.8, height * 0.3, 0, width * 0.8, height * 0.3, magR);
    magenta.addColorStop(0, 'rgba(255,0,255,0.08)');
    magenta.addColorStop(0.5, 'rgba(255,0,255,0)');
    ctx.fillStyle = magenta;
    ctx.fillRect(0, 0, width, height);

    // Cyan nebula glow (50% x, 80% y)
    const cyanR = diag * 0.5;
    const cyan = ctx.createRadialGradient(width * 0.5, height * 0.8, 0, width * 0.5, height * 0.8, cyanR);
    cyan.addColorStop(0, 'rgba(0,255,255,0.10)');
    cyan.addColorStop(0.4, 'rgba(0,255,255,0)');
    ctx.fillStyle = cyan;
    ctx.fillRect(0, 0, width, height);

    const bgKey = '__space_bg__';
    if (this.textures.exists(bgKey)) this.textures.remove(bgKey);
    this.textures.addCanvas(bgKey, canvas);
    // Release the canvas element to free memory (texture manager has its own copy)
    canvas.width = 0;
    canvas.height = 0;

    const bgImg = this.add.image(width / 2, height / 2, bgKey);
    bgImg.setScrollFactor(0);
    bgImg.setDepth(-3);
    this.bgImage = bgImg;

    // Layer 2: Figma star field — fixed to screen, stretched to fill
    const starKey = this.textures.exists('figma-stars') ? 'figma-stars' : 'overlay-stars';
    if (this.textures.exists(starKey)) {
      const starsFixed = this.add.image(width / 2, height / 2, starKey);
      starsFixed.setDisplaySize(width, height);
      starsFixed.setScrollFactor(0);
      starsFixed.setDepth(-2.5);
      starsFixed.setAlpha(0.7);
      this.starsFixedImage = starsFixed;
    }

    // Layer 3: Tiling star overlay with slight parallax for depth when panning.
    // Sized 4x viewport so it stays well past the screen edge even at min-zoom
    // (~0.35x), preventing a visible bounded rectangle when zoomed out.
    if (this.textures.exists('overlay-stars')) {
      const worldStars = this.add.tileSprite(0, 0, width * 4, height * 4, 'overlay-stars');
      worldStars.setOrigin(0.5);
      worldStars.setScrollFactor(0.06);
      worldStars.setDepth(-2);
      worldStars.setAlpha(0.4);
      this.worldStars = worldStars;
    }

    this.createVignette();

    // Force an immediate sync so the overlays match the current zoom right
    // away (e.g. autoSelectAndZoomIn() is mid-tween when first frame renders).
    this.lastSyncedZoom = -1;
    this.syncFixedOverlaysToZoom();
  }

  private createVignette(): void {
    const { width, height } = this.cameras.main;
    const cx = width / 2;
    const cy = height / 2;
    const outerRadius = Math.sqrt(cx * cx + cy * cy);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d')!;

    const gradient = ctx.createRadialGradient(cx, cy, outerRadius * 0.4, cx, cy, outerRadius);
    gradient.addColorStop(0, 'rgba(0,0,0,0)');
    gradient.addColorStop(0.5, 'rgba(0,0,0,0.15)');
    gradient.addColorStop(0.8, 'rgba(0,0,0,0.4)');
    gradient.addColorStop(1, 'rgba(0,0,0,0.7)');

    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);

    const key = '__vignette__';
    if (this.textures.exists(key)) this.textures.remove(key);
    this.textures.addCanvas(key, canvas);
    // Release the canvas element to free memory
    canvas.width = 0;
    canvas.height = 0;

    const img = this.add.image(cx, cy, key);
    img.setScrollFactor(0);
    img.setDepth(999);
    this.vignetteImage = img;
  }

  /**
   * Inverse-scale screen-locked overlays (background nebula, fixed starfield,
   * vignette) to counter the main camera zoom so they always cover the full
   * viewport regardless of zoom level.
   *
   * Why: setScrollFactor(0) keeps an object's screen position locked to the
   * camera viewport, but Phaser STILL applies camera zoom to its visual size.
   * At intro-zoom (0.35x) or after a pinch-zoom-out, the overlay textures
   * shrink to ~35% of viewport size, leaving a dark bordered rectangle in the
   * middle of the screen with the unwrapped world bleeding through outside.
   *
   * Setting scale = 1/zoom on the bg/vignette and width/height = (w/zoom,
   * h/zoom) on the stretched starfield reverses that scaling so the rendered
   * size always equals the actual viewport in screen pixels.
   *
   * Cheap: only runs the writes when zoom changes (delta > 0.001), so steady
   * state is a single subtraction per frame.
   */
  private syncFixedOverlaysToZoom(): void {
    const cam = this.cameras.main;
    if (!cam) return;
    const zoom = cam.zoom;
    if (Math.abs(zoom - this.lastSyncedZoom) < 0.001) return;
    this.lastSyncedZoom = zoom;
    const inv = 1 / Math.max(zoom, 0.001);

    if (this.bgImage) this.bgImage.setScale(inv);
    if (this.vignetteImage) this.vignetteImage.setScale(inv);
    if (this.starsFixedImage) {
      // starsFixed was stretched via setDisplaySize(bgBaseW, bgBaseH); to keep
      // covering the viewport we set the displaysize to base * inv.
      this.starsFixedImage.setDisplaySize(this.bgBaseWidth * inv, this.bgBaseHeight * inv);
    }
    // worldStars (the parallax tile sprite) is already 4x viewport, so even at
    // 0.35x zoom it still covers ~1.4x viewport — no inverse scaling needed.
  }

  private setupCamera(): void {
    const camera = this.cameras.main;

    // Find the player's ship to center on (prefer units over home planet)
    const playerUnits = this.mmoState.getPlayerUnits();
    let targetPos: { x: number; y: number };

    if (playerUnits.length > 0) {
      // Center on the player's first unit
      const unit = playerUnits[0];
      targetPos = axialToPixel(unit.position.q, unit.position.r);
      console.log(`[MMOGame] Camera setup: centering on player unit at (${unit.position.q}, ${unit.position.r})`);
    } else {
      // Fall back to home planet or origin
      const playerPosition = this.mmoState.getPlayerPosition();
      if (playerPosition) {
        targetPos = axialToPixel(playerPosition.q, playerPosition.r);
        console.log(`[MMOGame] Camera setup: centering on home planet (${playerPosition.q}, ${playerPosition.r})`);
      } else {
        targetPos = axialToPixel(0, 0);
        console.log('[MMOGame] Camera setup: no player position, defaulting to (0,0)');
      }
    }

    camera.centerOn(targetPos.x, targetPos.y);

    // Start zoomed out — autoSelectAndZoomIn() will tween to normal zoom
    camera.setZoom(0.35);

    // Enable smooth camera movement
    camera.setLerp(0.08);

    // No bounds - infinite scrolling world
    camera.removeBounds();
  }

  /**
   * After initial render, auto-select the player's ship and
   * smoothly zoom in from the zoomed-out overview to normal gameplay zoom.
   */
  private autoSelectAndZoomIn(): void {
    const camera = this.cameras.main;
    const playerUnits = this.mmoState.getPlayerUnits();
    const targetZoom = 1.8;

    // Find the player's ship to focus on
    let targetUnit: MMOUnit | undefined;
    if (playerUnits.length > 0) {
      // Try to find the sprite for the first player unit
      targetUnit = this.unitSprites.get(playerUnits[0].id);
    }

    // Pan camera to the unit's position (in case it moved since setupCamera)
    if (targetUnit) {
      const px = targetUnit.x;
      const py = targetUnit.y;
      camera.pan(px, py, 1800, 'Sine.easeInOut');
    }

    // Smooth zoom-in tween
    this.tweens.add({
      targets: camera,
      zoom: targetZoom,
      duration: 2000,
      ease: 'Cubic.easeOut',
      onUpdate: () => {
        // Re-render hexes during zoom so the grid updates smoothly
        this.updateViewport();
        this.renderVisibleHexes();
        this.renderFogOfWar();
      },
      onComplete: () => {
        // Auto-select the player's unit after zoom completes
        if (targetUnit) {
          this.selectedEntity = targetUnit;
          targetUnit.setSelected(true);
          EventBus.emit('mmo-entity-selection-resolved', { entity: targetUnit });
        }
      },
    });
  }

  private setupInput(): void {
    // Enable 2nd touch pointer for pinch-to-zoom
    this.input.addPointer(1);

    // Mouse/touch events for dragging
    this.input.on('pointerdown', this.onPointerDown, this);
    this.input.on('pointermove', this.onPointerMove, this);
    this.input.on('pointerup', this.onPointerUp, this);

    // Mouse wheel zoom
    this.input.on('wheel', this.onWheel, this);

    // Listen for chat input focus to suppress keyboard controls
    EventBus.on('chat-input-focused', () => { this.chatInputFocused = true; });
    EventBus.on('chat-input-unfocused', () => { this.chatInputFocused = false; });

    // Track CommandDock's left-side width so the input-blocking zone can be
    // precise (60px for unit sidebar, up to ~370px for the planet info panel).
    EventBus.on('mmo-command-dock-shown', (payload: { dockLeftW?: number } | undefined) => {
      this.commandDockLeftW = payload?.dockLeftW ?? 0;
    });
    EventBus.on('mmo-command-dock-hidden', () => {
      this.commandDockLeftW = 0;
    });

    // Helper: true when any text input (chat, HTML overlay, etc.) is focused
    const isInputFocused = () => this.chatInputFocused
      || document.activeElement?.tagName === 'INPUT'
      || document.activeElement?.tagName === 'TEXTAREA';

    // Keyboard controls (WASD) - suppressed when any input is focused
    const onManualCameraMove = () => { this.disengageCameraFollow(); };
    this.input.keyboard!.on('keydown-W', () => { if (!isInputFocused()) { this.cameras.main.scrollY -= 20; onManualCameraMove(); } });
    this.input.keyboard!.on('keydown-S', () => { if (!isInputFocused()) { this.cameras.main.scrollY += 20; onManualCameraMove(); } });
    this.input.keyboard!.on('keydown-A', () => { if (!isInputFocused()) { this.cameras.main.scrollX -= 20; onManualCameraMove(); } });
    this.input.keyboard!.on('keydown-D', () => { if (!isInputFocused()) { this.cameras.main.scrollX += 20; onManualCameraMove(); } });

    // Arrow key controls - suppressed when any input is focused
    this.input.keyboard!.on('keydown-UP', () => { if (!isInputFocused()) { this.cameras.main.scrollY -= 20; onManualCameraMove(); } });
    this.input.keyboard!.on('keydown-DOWN', () => { if (!isInputFocused()) { this.cameras.main.scrollY += 20; onManualCameraMove(); } });
    this.input.keyboard!.on('keydown-LEFT', () => { if (!isInputFocused()) { this.cameras.main.scrollX -= 20; onManualCameraMove(); } });
    this.input.keyboard!.on('keydown-RIGHT', () => { if (!isInputFocused()) { this.cameras.main.scrollX += 20; onManualCameraMove(); } });

    // ESC to cancel movement/attack/relocation/deploy mode, or deselect entity
    this.input.keyboard!.on('keydown-ESC', () => {
      if (this.isStructureDeployMode) {
        this.cancelStructureDeployMode();
      } else if (this.isRelocationMode) {
        this.cancelRelocationMode();
      } else if (this.isMovementMode) {
        this.cancelMovementMode();
      } else if (this.isAttackMode) {
        this.cancelAttackMode();
      } else if (this.isMineMode) {
        this.cancelMineMode();
      } else if (this.selectedEntity) {
        // Deselect the currently selected entity
        this.selectedEntity.setSelected(false);
        this.selectedEntity = null;
        EventBus.emit('mmo-entity-deselected');
      }
    });

    // When returning from a backgrounded tab, reconnect WS if needed
    this.visibilityHandler = () => {
      if (document.hidden) return;
      if (this.mmoNetwork && !this.mmoNetwork.isConnected) {
        console.log('[MMOGame] Tab restored — reconnecting WebSocket');
        this.mmoNetwork.reconnect();
      }
    };
    document.addEventListener('visibilitychange', this.visibilityHandler);
  }

  private onPointerDown(pointer: Phaser.Input.Pointer): void {
    if (pointer.button === 0) {
      // Always record the start position for proper drag distance calculation in pointerUp
      this.dragStartX = pointer.x;
      this.dragStartY = pointer.y;

      // Left click
      // Don't start dragging if clicking on UI panels.
      // Layout differs between desktop and mobile, so gating differs too:
      //   - Desktop: left sidebar (~320px), right comm panel (~320px), top bar (60px),
      //     command dock (160px from bottom).
      //   - Mobile (post-slim HUD): DOM top bar 44px + 6px safe margin. DOM FAB columns
      //     (~64px wide) down both sides near the bottom. Command dock only visible when
      //     a unit is selected, so only block its strip when something is selected.
      const { width, height } = this.cameras.main;
      const mobile = detectMobile(this);
      const isOverLeftSidebar = !mobile && pointer.x < 320;
      const isOverRightPanel = !mobile && pointer.x > width - 320;
      // Mobile top bar is 44px tall anchored 6px below safe-area top. Reserve 56px.
      const topBarBlock = mobile ? 56 : 60;
      const isOverTopBar = pointer.y < topBarBlock;
      // On mobile, only guard the command-dock strip when a unit is selected
      // (the dock is hidden otherwise and the area is usable map).
      const dockVisible = !mobile || this.selectedEntity !== null;
      // Command dock is 144px tall; on mobile it sits 20px above the safe area.
      const dockBlock = mobile ? 170 : 160;
      const isOverCommandDock = dockVisible && pointer.y > height - dockBlock;
      // Mobile LEFT-SIDE panel (unit sidebar OR planet info panel).
      // Width is reported by CommandDock via mmo-command-dock-shown; 70px
      // fallback covers the old sidebar footprint before the first event.
      const leftBlockW = Math.max(this.commandDockLeftW, dockVisible ? 70 : 0);
      const isOverLeftSidebar2 = mobile && dockVisible
        && pointer.x < leftBlockW && pointer.y > 56 && pointer.y < height - 16;
      // Mobile FAB columns now sit near the BOTTOM (not mid-height): 52px FABs +
      // a 12px padding buffer. Only block when pointer is in the bottom-right /
      // bottom-left cluster so the map stays largely tappable.
      const fabStripW = 64;
      // FABs live in the bottom ~180px (command dock's vertical slot) — block
      // the same band ONLY when dock is hidden (dockBlock covers it otherwise).
      const fabYMin = height - 200;
      const fabYMax = height;
      const isOverLeftFAB = mobile && pointer.x < fabStripW && pointer.y > fabYMin && pointer.y < fabYMax;
      const isOverRightFAB = mobile && pointer.x > width - fabStripW && pointer.y > fabYMin && pointer.y < fabYMax;
      // Persistent mini-radar in the bottom-right corner (~130px square).
      // Block pointer-drag within its footprint so tapping the radar doesn't
      // start a camera pan. The command dock now slides in from the LEFT as a
      // sidebar / info panel, so the bottom-right radar is never covered and
      // stays at its resting position — no lift offset needed.
      const radarSize   = 130;
      const radarMargin = 8;
      const radarXMin = width - radarSize - radarMargin;
      const radarYMax = height - radarMargin;
      const radarYMin = radarYMax - radarSize;
      const isOverRadar = mobile && pointer.x > radarXMin
        && pointer.y > radarYMin && pointer.y < radarYMax;

      if (isOverLeftSidebar || isOverRightPanel || isOverTopBar || isOverCommandDock
          || isOverLeftFAB || isOverRightFAB || isOverRadar || isOverLeftSidebar2) {
        return; // Let HUD scene handle this click
      }

      // ALWAYS arm the drag state on pointerDown, regardless of what's under
      // the cursor. This fixes the "can't drag over Earth / large planets"
      // UX bug: the tap-vs-drag threshold in onPointerUp (lines ~675-679)
      // still distinguishes a click on an entity (resolves to selection)
      // from a finger-drag (pans the camera). Camera-follow disengagement is
      // deferred to onPointerMove so a simple tap doesn't break follow mode.
      this.isDragging = true;
      this.dragCameraX = this.cameras.main.scrollX;
      this.dragCameraY = this.cameras.main.scrollY;
      // Track follow-disengage intent — we only actually disengage once the
      // pointer moves far enough to count as a real drag (see onPointerMove).
      this.pendingFollowDisengage = !this.isMovementMode && !this.isAttackMode
        && !this.isMineMode && !this.isRelocationMode && !this.isStructureDeployMode;
    }
  }

  private onPointerMove(pointer: Phaser.Input.Pointer): void {
    // Pinch-to-zoom: detect two active pointers
    const pointer1 = this.input.pointer1;
    const pointer2 = this.input.pointer2;
    if (pointer1.isDown && pointer2.isDown) {
      const dist = Phaser.Math.Distance.Between(pointer1.x, pointer1.y, pointer2.x, pointer2.y);
      if (!this.isPinching) {
        this.isPinching = true;
        this.isDragging = false;
        this.pinchStartDist = dist;
        this.pinchStartZoom = this.cameras.main.zoom;
      } else {
        const ratio = dist / this.pinchStartDist;
        // Mobile caps zoom-in at ~1.0 (natural hex size). Anything higher
        // shrinks the useful play area too much and amplifies any residual
        // input-coordinate drift near the screen edges.
        const maxZoomHere = detectMobile(this) ? 1.0 : 3.0;
        const newZoom = Phaser.Math.Clamp(this.pinchStartZoom * ratio, 0.2, maxZoomHere);
        this.cameras.main.setZoom(newZoom);
      }
      return;
    }

    if (this.isDragging) {
      const camera = this.cameras.main;
      const deltaX = pointer.x - this.dragStartX;
      const deltaY = pointer.y - this.dragStartY;

      // Once the drag crosses the tap/drag threshold, treat this as a REAL
      // camera pan — disengage follow and start panning. Before that point
      // we leave the camera alone so a plain tap doesn't shift the view.
      const dragDistance = Math.hypot(deltaX, deltaY);
      const isCoarsePointer = typeof window !== 'undefined' && window.matchMedia
        && window.matchMedia('(pointer: coarse)').matches;
      const dragThreshold = isCoarsePointer ? 10 : 5;
      if (dragDistance < dragThreshold) {
        return;
      }
      if (this.pendingFollowDisengage) {
        this.disengageCameraFollow();
        this.pendingFollowDisengage = false;
      }

      // panSpeed 0.625 = previous 0.5 × 1.25 — drag moves the camera 25%
      // further for the same finger/mouse travel.
      const panSpeed = 0.625;
      const effectiveZoom = Math.max(camera.zoom, 0.5);
      camera.scrollX = this.dragCameraX - (deltaX * panSpeed) / effectiveZoom;
      camera.scrollY = this.dragCameraY - (deltaY * panSpeed) / effectiveZoom;
    }

    // Update cursor when in attack or mine-target mode
    if (this.isAttackMode || this.isMineMode) {
      const camera = this.cameras.main;
      const worldPoint = camera.getWorldPoint(pointer.x, pointer.y);
      const hex = pixelToAxial(worldPoint.x, worldPoint.y);
      const hexKey = `${hex.q},${hex.r}`;

      const isValidTarget = this.isAttackMode
        ? this.resolveAttackTargetAtPointer(pointer, hex, hexKey) !== null
        : this.validMineTargets.has(hexKey);
      this.input.setDefaultCursor(isValidTarget ? 'crosshair' : 'not-allowed');
    } else {
      this.input.setDefaultCursor('default');
    }
  }

  private onPointerUp(pointer: Phaser.Input.Pointer): void {
    // Reset pinch state when fewer than 2 pointers active
    if (this.isPinching) {
      const pointer1 = this.input.pointer1;
      const pointer2 = this.input.pointer2;
      if (!pointer1.isDown || !pointer2.isDown) {
        this.isPinching = false;
      }
      return;
    }
    if (pointer.button === 0) {
      this.isDragging = false;

      // If it was a click (not a drag), handle hex selection.
      // Touch fingers jitter more than a mouse pointer, so raise the threshold
      // on coarse-pointer devices to avoid false drags that eat entity taps.
      // 14px on coarse pointers accounts for the typical finger-roll when a
      // user lifts off glass — 10px was too strict and silently ate MOVE
      // destination taps where a thumb wobbled slightly before release.
      const dragDistance = Phaser.Math.Distance.Between(this.dragStartX, this.dragStartY, pointer.x, pointer.y);
      const isCoarsePointer = typeof window !== 'undefined' && window.matchMedia
        && window.matchMedia('(pointer: coarse)').matches;
      const dragThreshold = isCoarsePointer ? 14 : 5;

      if (dragDistance < dragThreshold) {
        // Skip game scene click handling if pointer is over HUD UI areas
        const screenWidth = this.cameras.main.width;
        const screenHeight = this.cameras.main.height;
        const mobileView = screenWidth < 600;
        const isOverLeftSidebar = !mobileView && pointer.x < 320;
        const isOverRightPanel = !mobileView && pointer.x > screenWidth - 320;
        const isOverTopBar = pointer.y < 60;
        const isOverBottomDock = pointer.y > screenHeight - 160;
        // Swallow taps that land on the mobile left sidebar / info panel so
        // they don't deselect or remap.
        const isOverMobileLeftPanel = mobileView && this.commandDockLeftW > 0
          && pointer.x < this.commandDockLeftW && pointer.y > 56 && pointer.y < screenHeight - 16;
        if (isOverLeftSidebar || isOverRightPanel || isOverTopBar || isOverBottomDock || isOverMobileLeftPanel) {
          return;
        }
        // In movement, attack, mine-target, or relocation mode, route all clicks to handleHexClick
        // Guard: ignore the same click that activated the mode (prevents race condition
        // where clicking a button also selects the hex beneath it as destination).
        // Shortened from 100ms → 40ms: long enough to block the literal same
        // pointer event (touch release propagates within ~16ms), short enough
        // that fast tappers aren't silently blocked when they tap MOVE and
        // then the destination in quick succession.
        if (this.isMovementMode || this.isAttackMode || this.isMineMode || this.isRelocationMode || this.isStructureDeployMode) {
          if (this.isMovementMode && Date.now() - this.movementModeActivatedAt < 40) {
            return; // Same click that activated move mode — ignore
          }
          if (this.isRelocationMode && Date.now() - this.relocationModeActivatedAt < 40) {
            return; // Same click that activated relocation mode — ignore
          }
          this.handleHexClick(pointer);
          return;
        }

        // Convert screen to world coordinates
        const camera = this.cameras.main;
        const worldPoint = camera.getWorldPoint(pointer.x, pointer.y);

        // Check if clicking on an interactive entity using manual hit test
        const hitEntity = this.getEntityAtWorldPoint(worldPoint.x, worldPoint.y);

        if (hitEntity) {
          // Trigger entity selection event
          EventBus.emit('mmo-entity-selected', { entity: hitEntity });
        } else {
          // Deselect current entity when clicking empty space
          if (this.selectedEntity) {
            this.selectedEntity.setSelected(false);
            this.selectedEntity = null;
            EventBus.emit('mmo-entity-deselected');
          }
          // Only handle hex click if we didn't click on an entity
          this.handleHexClick(pointer);
        }
      }
    }
  }

  private onWheel(
    _pointer: Phaser.Input.Pointer,
    _gameObjects: any[],
    _deltaX: number,
    deltaY: number,
    _deltaZ: number,
  ): void {
    const camera = this.cameras.main;
    const zoomSpeed = 0.001;
    const minZoom = 0.2;
    // Mobile caps zoom-in at ~1.0 (natural hex size). See pinch handler
    // for the rationale.
    const maxZoom = detectMobile(this) ? 1.0 : 3.0;

    let newZoom = camera.zoom - deltaY * zoomSpeed;
    newZoom = Phaser.Math.Clamp(newZoom, minZoom, maxZoom);

    camera.setZoom(newZoom);
  }

  private handleHexClick(pointer: Phaser.Input.Pointer): void {
    const camera = this.cameras.main;

    // Use Phaser's built-in method to correctly convert screen to world coordinates
    const worldPoint = camera.getWorldPoint(pointer.x, pointer.y);
    const worldX = worldPoint.x;
    const worldY = worldPoint.y;

    const hex = pixelToAxial(worldX, worldY);
    const hexKey = `${hex.q},${hex.r}`;

    // If in structure deploy mode, handle target hex selection
    if (this.isStructureDeployMode) {
      if (Date.now() - this.structureDeployActivatedAt < 100) return;
      this.handleStructureDeployTarget(hex);
      return;
    }

    // If in relocation mode, handle target hex selection
    if (this.isRelocationMode) {
      this.handleRelocationTarget(hex);
      return;
    }

    // If in movement mode, handle destination selection
    if (this.isMovementMode) {
      this.handleMovementDestination(hex, hexKey);
      return;
    }

    // If in attack mode, handle target selection
    if (this.isAttackMode) {
      this.handleAttackTarget(hex, hexKey);
      return;
    }

    // If in mine-target mode, handle asteroid selection
    if (this.isMineMode) {
      this.handleMineTarget(hex, hexKey);
      return;
    }

    // Check if hex is visible
    if (!this.mmoState.isHexVisible(hexKey)) {
      return;
    }

    // Get entities at this hex
    const entities = this.mmoState.getEntitiesAt(hex);

    // Show hex info tooltip (coordinates + territory ownership)
    this.showHexInfo(hex);

    // Emit event for UI to handle
    EventBus.emit('mmo-hex-clicked', { hex, entities });
  }

  private showHexInfo(hex: HexCoord): void {
    // Check territory ownership
    const territories = this.mmoState.getTerritories();
    let ownerName = 'Neutral';
    let ownerColor = '#667788';
    for (const t of territories) {
      const dist = hexDistance(hex, { q: t.hqQ, r: t.hqR });
      if (dist <= t.radius) {
        ownerName = t.name;
        ownerColor = t.color || '#4488ff';
        break;
      }
    }

    const label = `(${hex.q}, ${hex.r}) — ${ownerName}`;
    const pixel = axialToPixel(hex.q, hex.r);
    const worldX = pixel.x;
    const worldY = pixel.y - 30;

    // Remove previous hex info if exists
    if (this._hexInfoText) { this._hexInfoText.destroy(); this._hexInfoText = null; }

    const text = this.add.text(worldX, worldY, label, {
      fontSize: '11px',
      color: ownerColor,
      fontFamily: '"Share Tech Mono", monospace',
      backgroundColor: '#000000cc',
      padding: { x: 6, y: 3 },
    });
    text.setOrigin(0.5, 1);
    text.setDepth(20);
    this._hexInfoText = text;

    // Auto-remove after 3 seconds
    this.time.delayedCall(3000, () => {
      if (this._hexInfoText === text) {
        text.destroy();
        this._hexInfoText = null;
      }
    });
  }
  private _hexInfoText: Phaser.GameObjects.Text | null = null;

  private setupNetworkHandlers(): void {
    // World state received
    EventBus.on(MMOEvents.WORLD_STATE, this.onWorldState, this);

    // Tick updates
    EventBus.on(MMOEvents.TICK_UPDATE, this.onTickUpdate, this);

    // Unit moved
    EventBus.on(MMOEvents.UNIT_MOVED, this.onUnitMoved, this);

    // Combat result
    EventBus.on(MMOEvents.COMBAT_RESULT, this.onCombatResult, this);

    EventBus.on('mmo_unit_docked', this.onUnitDocked, this);
    EventBus.on('mmo_unit_undocked', this.onUnitUndocked, this);
    EventBus.on('mmo_return_to_base_confirmed', this.onReturnToBaseConfirmed, this);
    EventBus.on('mmo_movement_rerouted', this.onMovementRerouted, this);

    // Golden asteroid events
    EventBus.on('mmo_golden_asteroid_delivered', this.onGoldenAsteroidDelivered, this);
    EventBus.on('mmo_golden_asteroid_spawned', this.onGoldenAsteroidSpawned, this);
    EventBus.on('mmo_asteroid_split', this.onAsteroidSplit, this);
    EventBus.on('mmo_asteroid_warning', this.onAsteroidWarning, this);
    EventBus.on('mmo_asteroid_destroyed', this.onAsteroidDestroyed, this);
    EventBus.on('mmo_asteroid_depleted', this.onAsteroidDepleted, this);
    EventBus.on('mmo_admin_asteroids_removed', this.onAdminAsteroidsRemoved, this);

    // Ability events
    EventBus.on(MMOEvents.ABILITY_ACTIVATED, this.onAbilityActivated, this);
    EventBus.on(MMOEvents.STATUS_EFFECT_APPLIED, this.onStatusEffectApplied, this);
    EventBus.on(MMOEvents.STATUS_EFFECT_EXPIRED, this.onStatusEffectExpired, this);

    // Alliance events
    EventBus.on(MMOEvents.ALLIANCE_CREATED, this.onAllianceCreated, this);
    EventBus.on(MMOEvents.ALLIANCE_LEFT, this.onAllianceLeft, this);

    // Loot events
    EventBus.on(MMOEvents.LOOT_CLAIMED, this.onLootClaimed, this);
    EventBus.on(MMOEvents.AMBIENT_SALVAGED, this.onAmbientSalvaged, this);

    // Mining events
    EventBus.on(MMOEvents.MINE_RESULT, this.onMineResult, this);
    EventBus.on(MMOEvents.MINE_STARTED, this.onMineStarted, this);
    EventBus.on(MMOEvents.MINE_STOPPED, this.onMineStopped, this);
    EventBus.on('component_condition_updated', this.onComponentConditionUpdated, this);
    EventBus.on('components_changed', this.onComponentsChanged, this);
    EventBus.on('show-notification', this.onShowNotification, this);

    // Cargo unloaded on dock
    EventBus.on(MMOEvents.CARGO_UNLOADED, this.onCargoUnloaded, this);

    // Repair result
    EventBus.on(MMOEvents.REPAIR_RESULT, this.onRepairResult, this);

    // Unit destroyed notification (combat + hazard kills with upgrade hints)
    EventBus.on(MMOEvents.UNIT_DESTROYED_NOTIFICATION, this.onUnitDestroyedNotification, this);

    // Server errors (e.g. mine failed, cargo full)
    EventBus.on(MMOEvents.ERROR, this.onServerError, this);

    // Player resources update
    EventBus.on(MMOEvents.PLAYER_RESOURCES, this.onPlayerResources, this);

    // Chunk data (dynamic loading)
    EventBus.on(MMOEvents.CHUNK_DATA, this.onChunkData, this);

    // Engagement events
    EventBus.on(MMOEvents.ENGAGEMENT_STARTED, this.onEngagementStarted, this);
    EventBus.on(MMOEvents.ENGAGEMENT_ENDED, this.onEngagementEnded, this);

    // Memory management events
    EventBus.on(MemoryEvents.SWEEP_TRIGGERED, this.onMemorySweep, this);
    EventBus.on(MemoryEvents.MEMORY_CRITICAL, this.onMemoryCritical, this);

    // Build queue events
    EventBus.on(MMOEvents.BUILD_QUEUED, this.onBuildQueued, this);
    EventBus.on(MMOEvents.BUILD_PROGRESS, this.onBuildProgress, this);
    EventBus.on(MMOEvents.BUILD_COMPLETE, this.onBuildComplete, this);

    // Relocation mode (station / platform hex-picker)
    EventBus.on('station-relocate-requested', this.enterStationRelocationMode, this);
    EventBus.on('platform-relocate-requested', this.enterPlatformRelocationMode, this);

    // Structure deploy mode
    EventBus.on('enter-structure-deploy-mode', this.enterStructureDeployMode, this);
    EventBus.on(MMOEvents.DEPLOY_STRUCTURE_RESULT, this.onDeployStructureResult, this);
    EventBus.on(MMOEvents.DESIGNATE_HQ_RESULT, this.onDesignateHQResult, this);
    EventBus.on(MMOEvents.SET_TAX_RATE_RESULT, this.onSetTaxRateResult, this);

    // Relic discovery (from mining or salvage)
    EventBus.on('relic-discovered', this.onRelicDiscovered, this);
    EventBus.on(MMOEvents.RELIC_PICKED_UP, this.onRelicPickedUp, this);

    // Research completed
    EventBus.on('mmo_research_completed', this.onResearchCompleted, this);

    // Territory updates
    EventBus.on(MMOEvents.TERRITORY_UPDATE, this.onTerritoryUpdate, this);
  }

  private removeNetworkHandlers(): void {
    EventBus.off(MMOEvents.WORLD_STATE, this.onWorldState, this);
    EventBus.off(MMOEvents.TICK_UPDATE, this.onTickUpdate, this);
    EventBus.off(MMOEvents.UNIT_MOVED, this.onUnitMoved, this);
    EventBus.off(MMOEvents.COMBAT_RESULT, this.onCombatResult, this);
    EventBus.off('mmo_unit_docked', this.onUnitDocked, this);
    EventBus.off('mmo_unit_undocked', this.onUnitUndocked, this);
    EventBus.off('mmo_return_to_base_confirmed', this.onReturnToBaseConfirmed, this);
    EventBus.off('mmo_movement_rerouted', this.onMovementRerouted, this);
    EventBus.off('mmo_golden_asteroid_delivered', this.onGoldenAsteroidDelivered, this);
    EventBus.off('mmo_golden_asteroid_spawned', this.onGoldenAsteroidSpawned, this);
    EventBus.off('mmo_asteroid_split', this.onAsteroidSplit, this);
    EventBus.off('mmo_asteroid_warning', this.onAsteroidWarning, this);
    EventBus.off('mmo_asteroid_destroyed', this.onAsteroidDestroyed, this);
    EventBus.off('mmo_asteroid_depleted', this.onAsteroidDepleted, this);
    EventBus.off('mmo_admin_asteroids_removed', this.onAdminAsteroidsRemoved, this);
    EventBus.off(MMOEvents.ABILITY_ACTIVATED, this.onAbilityActivated, this);
    EventBus.off(MMOEvents.STATUS_EFFECT_APPLIED, this.onStatusEffectApplied, this);
    EventBus.off(MMOEvents.STATUS_EFFECT_EXPIRED, this.onStatusEffectExpired, this);
    EventBus.off(MMOEvents.ALLIANCE_CREATED, this.onAllianceCreated, this);
    EventBus.off(MMOEvents.ALLIANCE_LEFT, this.onAllianceLeft, this);
    EventBus.off(MMOEvents.MINE_RESULT, this.onMineResult, this);
    EventBus.off(MMOEvents.MINE_STARTED, this.onMineStarted, this);
    EventBus.off(MMOEvents.MINE_STOPPED, this.onMineStopped, this);
    EventBus.off('component_condition_updated', this.onComponentConditionUpdated, this);
    EventBus.off('components_changed', this.onComponentsChanged, this);
    EventBus.off('show-notification', this.onShowNotification, this);
    EventBus.off(MMOEvents.CARGO_UNLOADED, this.onCargoUnloaded, this);
    EventBus.off(MMOEvents.REPAIR_RESULT, this.onRepairResult, this);
    EventBus.off(MMOEvents.UNIT_DESTROYED_NOTIFICATION, this.onUnitDestroyedNotification, this);
    EventBus.off(MMOEvents.ERROR, this.onServerError, this);
    EventBus.off(MMOEvents.PLAYER_RESOURCES, this.onPlayerResources, this);
    EventBus.off(MMOEvents.CHUNK_DATA, this.onChunkData, this);
    EventBus.off('chat-input-focused');
    EventBus.off('chat-input-unfocused');
    EventBus.off(MMOEvents.ENGAGEMENT_STARTED, this.onEngagementStarted, this);
    EventBus.off(MMOEvents.ENGAGEMENT_ENDED, this.onEngagementEnded, this);
    EventBus.off(MemoryEvents.SWEEP_TRIGGERED, this.onMemorySweep, this);
    EventBus.off(MemoryEvents.MEMORY_CRITICAL, this.onMemoryCritical, this);
    EventBus.off(MMOEvents.BUILD_QUEUED, this.onBuildQueued, this);
    EventBus.off(MMOEvents.BUILD_PROGRESS, this.onBuildProgress, this);
    EventBus.off(MMOEvents.BUILD_COMPLETE, this.onBuildComplete, this);
    EventBus.off('relic-discovered', this.onRelicDiscovered, this);
    EventBus.off(MMOEvents.RELIC_PICKED_UP, this.onRelicPickedUp, this);
    EventBus.off('mmo_research_completed', this.onResearchCompleted, this);
    EventBus.off(MMOEvents.LOOT_CLAIMED, this.onLootClaimed, this);
    EventBus.off(MMOEvents.AMBIENT_SALVAGED, this.onAmbientSalvaged, this);
    EventBus.off(MMOEvents.TERRITORY_UPDATE, this.onTerritoryUpdate, this);
    EventBus.off('enter-structure-deploy-mode', this.enterStructureDeployMode, this);
    EventBus.off(MMOEvents.DEPLOY_STRUCTURE_RESULT, this.onDeployStructureResult, this);
    EventBus.off(MMOEvents.DESIGNATE_HQ_RESULT, this.onDesignateHQResult, this);
    EventBus.off(MMOEvents.SET_TAX_RATE_RESULT, this.onSetTaxRateResult, this);
  }

  private setupActionHandlers(): void {
    // Listen for player actions from UI
    EventBus.on('mmo-action-move', this.onActionMove, this);
    EventBus.on('mmo-action-attack', this.onActionAttack, this);
    // Ship building removed — ships are purchased from Ship Store
    EventBus.on('mmo-action-upgrade-mining', this.onActionUpgradeMining, this);
    EventBus.on('mmo-action-mine', this.onActionMine, this);
    EventBus.on('mmo-action-stop-mining', this.onActionStopMining, this);
    EventBus.on('mmo-action-dock', this.onActionDock, this);
    EventBus.on('mmo-action-undock', this.onActionUndock, this);
    EventBus.on('mmo-action-ability', this.onActionAbility, this);
    EventBus.on('mmo-action-repair', this.onActionRepair, this);
    EventBus.on('mmo-action-goto', this.onActionGoto, this);
    EventBus.on('mmo-action-return', this.onActionReturn, this);

    // Listen for center on player ship
    EventBus.on('center-on-player-ship', this.centerOnPlayerShip, this);

    // Listen for camera follow toggle from HUD
    EventBus.on('toggle-camera-follow', this.onToggleCameraFollow, this);

    // Listen for entity selection from entities
    EventBus.on('mmo-entity-selected', this.handleEntitySelection, this);

    // Listen for entity deselection (e.g. sidebar close button)
    EventBus.on('mmo-entity-deselected', this.handleEntityDeselection, this);

    // Alliance actions from UI
    EventBus.on('mmo-alliance-leave', this.onAllianceLeaveAction, this);

    // Focus on newly purchased unit
    EventBus.on('select-purchased-unit', this.onSelectPurchasedUnit, this);

    // Fleet sidebar events
    EventBus.on('fleet-select-unit', this.onFleetSelectUnit, this);
    EventBus.on('fleet-select-docked-unit', this.onFleetSelectDockedUnit, this);
    EventBus.on('fleet-select-station', this.onFleetSelectStation, this);

    // Wreck claiming action
    EventBus.on('action-claim-wreck', this.onActionClaimWreck, this);

    // Navigation from offline recap / other dialogs
    EventBus.on('navigate-to-position', this.onNavigateToPosition, this);

  }

  private removeActionHandlers(): void {
    EventBus.off('mmo-action-move', this.onActionMove, this);
    EventBus.off('mmo-action-attack', this.onActionAttack, this);
    // Ship building removed
    EventBus.off('mmo-action-upgrade-mining', this.onActionUpgradeMining, this);
    EventBus.off('mmo-action-mine', this.onActionMine, this);
    EventBus.off('mmo-action-stop-mining', this.onActionStopMining, this);
    EventBus.off('mmo-action-dock', this.onActionDock, this);
    EventBus.off('mmo-action-undock', this.onActionUndock, this);
    EventBus.off('mmo-action-ability', this.onActionAbility, this);
    EventBus.off('mmo-action-repair', this.onActionRepair, this);
    EventBus.off('mmo-action-goto', this.onActionGoto, this);
    EventBus.off('mmo-action-return', this.onActionReturn, this);
    EventBus.off('center-on-player-ship', this.centerOnPlayerShip, this);
    EventBus.off('toggle-camera-follow', this.onToggleCameraFollow, this);
    EventBus.off('mmo-alliance-leave', this.onAllianceLeaveAction, this);
    EventBus.off('mmo-entity-selected', this.handleEntitySelection, this);
    EventBus.off('mmo-entity-deselected', this.handleEntityDeselection, this);
    EventBus.off('select-purchased-unit', this.onSelectPurchasedUnit, this);
    EventBus.off('action-claim-wreck', this.onActionClaimWreck, this);
    EventBus.off('navigate-to-position', this.onNavigateToPosition, this);
    EventBus.off('fleet-select-unit', this.onFleetSelectUnit, this);
    EventBus.off('fleet-select-docked-unit', this.onFleetSelectDockedUnit, this);
    EventBus.off('fleet-select-station', this.onFleetSelectStation, this);
  }

  private onActionMove(data: { entity: any }): void {
    console.log('[MMOGame] ===== MOVE ACTION REQUESTED =====');
    console.log('[MMOGame] Entity:', data.entity);
    console.log('[MMOGame] Entity ID:', data.entity.id);

    // Verify it's a unit
    if (!('unitType' in data.entity) && !('type' in data.entity)) {
      console.warn('[MMOGame] Move action called on non-unit entity');
      return;
    }

    // Get the unit sprite
    console.log('[MMOGame] Looking for unit sprite with ID:', data.entity.id);
    console.log('[MMOGame] Available unit sprites:', Array.from(this.unitSprites.keys()));
    const unitSprite = this.unitSprites.get(data.entity.id);
    if (!unitSprite) {
      console.warn('[MMOGame] Unit sprite not found:', data.entity.id);
      console.warn('[MMOGame] Available sprites:', this.unitSprites.size);
      return;
    }

    console.log('[MMOGame] Unit sprite found, entering movement mode...');
    // Enter movement mode
    this.enterMovementMode(unitSprite);
  }

  private onActionAttack(data: { entity: any }): void {
    console.log('[MMOGame] Attack action requested:', data.entity);

    // Verify it's a unit
    if (!('unitType' in data.entity) && !('type' in data.entity)) {
      console.warn('[MMOGame] Attack action called on non-unit entity');
      return;
    }

    // Check if unit can attack before entering attack mode
    // Use server-provided stats (not incomplete static UNIT_STATS table)
    const entityAttack = data.entity.attack ?? data.entity.attackRange ?? 1;
    if (entityAttack === 0) {
      this.showNotification('This ship cannot attack');
      return;
    }

    // Get the unit sprite
    const unitSprite = this.unitSprites.get(data.entity.id);
    if (!unitSprite) {
      console.warn('[MMOGame] Unit sprite not found:', data.entity.id);
      return;
    }

    // Enter attack mode
    this.enterAttackMode(unitSprite);
  }

  // onActionBuild removed — ships are purchased from Ship Store

  private onActionUpgradeMining(data: { entity: any }): void {
    console.log('[MMOGame] Upgrade mining action requested:', data.entity);

    // Verify it's an asteroid
    if (!('resourceRichness' in data.entity) || !('miningLevel' in data.entity)) {
      console.warn('[MMOGame] Upgrade mining action called on non-asteroid entity');
      return;
    }

    // Check ownership - only upgrade on own asteroids
    const playerId = this.mmoState.getUserId();
    if (!playerId || data.entity.ownerId !== playerId) {
      console.warn('[MMOGame] Cannot upgrade mining on asteroid not owned by player');
      return;
    }

    // Open upgrade mining dialog
    new UpgradeMiningDialog(
      this,
      this.mmoState,
      this.mmoNetwork,
      data.entity.id,
      `Asteroid ${data.entity.id.substring(0, 8)}`,
      data.entity.miningLevel,
    );
  }

  private onActionMine(data: { entity: any }): void {
    const playerId = this.mmoState.getUserId();
    if (!playerId) return;

    // If the entity is a unit (triggered from CommandDock MINE button),
    // find mineable asteroids at the unit's hex and adjacent hexes
    if ('attackRange' in data.entity || 'hexCoord' in data.entity) {
      const unitPos = data.entity.hexCoord || data.entity.position;
      const unitId = data.entity.id;
      if (!unitPos || !unitId) return;

      const unitSprite = this.unitSprites.get(unitId);
      if (!unitSprite) return;

      const mineableAsteroids = this.findMineableAsteroids(unitPos);

      if (mineableAsteroids.size === 0) {
        this.showNotification('No mineable asteroid nearby');
        return;
      }

      if (mineableAsteroids.size === 1) {
        const [target] = mineableAsteroids.values();
        this.mmoNetwork.sendMineAsteroid(unitId, target.asteroidId);
        return;
      }

      // 2+ asteroids — enter mine-target picker mode
      this.enterMineMode(unitSprite, mineableAsteroids);
      return;
    }

    // Legacy path: entity is an asteroid (triggered from asteroid selection)
    if (!('resourceRichness' in data.entity)) {
      return;
    }

    const asteroid = data.entity;

    // Find the player's unit on the same hex or adjacent to the asteroid
    const asteroidPos = asteroid.hexCoord || asteroid.position;
    const playerUnits = this.mmoState.getPlayerUnits();
    const nearbyUnit = playerUnits.find(u =>
      hexDistance(u.position, asteroidPos) <= 1
    );

    if (!nearbyUnit) {
      this.showNotification('Move adjacent to the asteroid to mine');
      return;
    }

    if (this.isAsteroidDepleted(data.entity)) {
      this.showNotification('This asteroid is empty — unable to mine');
      return;
    }

    this.mmoNetwork.sendMineAsteroid(nearbyUnit.id, asteroid.id);
  }

  private onActionStopMining(data: { entity: any }): void {
    const unitId = data.entity?.id;
    if (!unitId) return;
    this.mmoNetwork.sendStopMining(unitId);
  }

  private isAsteroidDepleted(entity: any): boolean {
    // Golden/special asteroids use rewardValue, not mineralComposition
    const type = entity.asteroidType;
    if (type === 'golden' || type === 'special') {
      return (entity.rewardValue ?? 0) <= 0;
    }
    if (entity.mineralComposition && typeof entity.mineralComposition === 'object') {
      const remaining = Object.values(entity.mineralComposition as Record<string, number>)
        .reduce((sum: number, v: number) => sum + (Number(v) || 0), 0);
      if (remaining <= 0) return true;
    }
    if (entity.maxStorage > 0 && entity.storedResources >= entity.maxStorage) return true;
    return false;
  }

  private onLootClaimed(payload: { lootId: string; component?: any; minerals?: Record<string, number>; gold?: number }): void {
    if (!payload || !payload.lootId) return;

    // Remove loot drop from state and scene
    this.mmoState.removeLootDrop(payload.lootId);
    const sprite = this.lootDropSprites.get(payload.lootId);
    if (sprite) {
      try { sprite.destroy(); } catch (_) { /* already destroyed */ }
      this.lootDropSprites.delete(payload.lootId);
    }

    // Deselect if this was the selected entity
    if (this.selectedEntity && this.selectedEntity.id === payload.lootId) {
      this.selectedEntity = null;
      EventBus.emit('mmo-entity-deselected');
    }

    // Show floating notification
    const parts: string[] = ['Loot claimed!'];
    if (payload.component) parts.push(`Component: ${payload.component.definitionId}`);
    if (payload.minerals && Object.keys(payload.minerals).length > 0) {
      const totalMinerals = Object.values(payload.minerals).reduce((a, b) => a + b, 0);
      parts.push(`${totalMinerals} minerals`);
    }
    if (payload.gold && payload.gold > 0) parts.push(`${payload.gold.toFixed(2)} gold`);
    console.log(`[MMOGame] ${parts.join(' | ')}`);

    // Emit notification for HUD toast
    EventBus.emit('show-notification', { text: parts.join(' | '), color: '#FFAA44', duration: 4000 });
  }

  private onAmbientSalvaged(payload: { entityId: string; component?: any; minerals?: Record<string, number>; entityDespawned?: boolean }): void {
    if (!payload || !payload.entityId) return;

    // Remove ambient entity from state and scene
    if (payload.entityDespawned) {
      this.mmoState.removeAmbientEntity(payload.entityId);
      const sprite = this.ambientSprites.get(payload.entityId);
      if (sprite) {
        try { sprite.destroy(); } catch (_) { /* already destroyed */ }
        this.ambientSprites.delete(payload.entityId);
      }
    }

    // Deselect if this was the selected entity
    if (this.selectedEntity && this.selectedEntity.id === payload.entityId) {
      this.selectedEntity = null;
      EventBus.emit('mmo-entity-deselected');
    }

    // Show floating notification
    const parts: string[] = ['Salvage complete!'];
    if (payload.component) parts.push(`Component: ${payload.component.name} (${payload.component.rarity})`);
    if (payload.minerals && Object.keys(payload.minerals).length > 0) {
      const totalMinerals = Object.values(payload.minerals).reduce((a, b) => a + b, 0);
      parts.push(`${totalMinerals} minerals`);
    }
    console.log(`[MMOGame] ${parts.join(' | ')}`);
    EventBus.emit('show-notification', { text: parts.join(' | '), color: '#F1C40F', duration: 4000 });
  }

  private onMineResult(payload: {
    unitId?: string; asteroidId?: string; minerals: Record<string, number>;
    asteroidDepleted: boolean; depletedAsteroidId?: string;
    cargoUsed?: number; cargoCapacity?: number;
    mineralComposition?: Record<string, number>; rewardValue?: number | null;
    storedResources?: number; maxStorage?: number;
    yieldBonus?: number; laserTier?: string; conditionPercent?: number;
    respawnedAsteroid?: { id: string; position: { q: number; r: number }; resourceRichness: number; maxStorage: number; mineralComposition: Record<string, number> } | null;
  }): void {
    if (!payload || !payload.minerals) return;

    if (payload.asteroidId) {
      const asteroidSprite = this.asteroidSprites.get(payload.asteroidId);

      if (payload.asteroidDepleted) {
        // Asteroid is fully depleted — play destruction effect and remove it
        if (asteroidSprite) {
          this.playAsteroidDepletionEffect(asteroidSprite.x, asteroidSprite.y);
          asteroidSprite.destroy();
          this.asteroidSprites.delete(payload.asteroidId);
        }
        this.mmoState.removeAsteroid(payload.asteroidId);

        // Clear selection if this asteroid was selected
        if (this.selectedEntity && this.selectedEntity.id === payload.asteroidId) {
          this.selectedEntity = null;
          EventBus.emit('mmo-entity-deselected');
        }
      } else if (asteroidSprite) {
        asteroidSprite.playMineEffect();

        // Draw a mining beam from the unit to the asteroid
        if (payload.unitId) {
          const unitSprite = this.unitSprites.get(payload.unitId);
          if (unitSprite) {
            const MINERAL_SPARKLE_COLORS: Record<string, number> = {
              stellar_reward: 0x00ff88, min_darkmat: 0x6600cc, min_iridium: 0xf0f0ff,
              min_rhodium: 0xffd700, min_palladium: 0xced8f6, min_titanium: 0xc0c0d0,
              min_platinum: 0xe5e4e2, min_iron: 0x8a8a8a, min_copper: 0xb87333,
            };
            const firstMineral = Object.keys(payload.minerals).find(k => payload.minerals[k] > 0);
            const sparkle = firstMineral ? MINERAL_SPARKLE_COLORS[firstMineral] : undefined;
            this.playMiningBeam(unitSprite, asteroidSprite, sparkle);
          }
        }

        if (payload.mineralComposition !== undefined) {
          asteroidSprite.mineralComposition = payload.mineralComposition || null;
        }
        if (payload.rewardValue !== undefined) {
          asteroidSprite.rewardValue = payload.rewardValue;
        }
        if (payload.storedResources !== undefined) {
          asteroidSprite.storedResources = payload.storedResources;
        }
        if (payload.maxStorage !== undefined && payload.maxStorage > 0) {
          asteroidSprite.maxStorage = payload.maxStorage;
        }
        if (this.selectedEntity && this.selectedEntity.id === payload.asteroidId) {
          EventBus.emit('mmo-entity-selected', { entity: this.selectedEntity });
        }
      }

      if (!payload.asteroidDepleted) {
        const stateUpdate: any = { id: payload.asteroidId };
        if (payload.mineralComposition !== undefined) stateUpdate.mineralComposition = payload.mineralComposition || null;
        if (payload.rewardValue !== undefined) stateUpdate.rewardValue = payload.rewardValue;
        if (payload.storedResources !== undefined) stateUpdate.storedResources = payload.storedResources;
        if (payload.maxStorage !== undefined && payload.maxStorage > 0) stateUpdate.maxStorage = payload.maxStorage;
        this.mmoState.updateAsteroid(stateUpdate);
      }

      // If a new asteroid was respawned, add it to the state so it renders
      if (payload.respawnedAsteroid) {
        const ra = payload.respawnedAsteroid;
        this.mmoState.addAsteroid({
          id: ra.id,
          ownerId: null,
          position: ra.position,
          miningLevel: 0,
          resourceRichness: ra.resourceRichness,
          maxStorage: ra.maxStorage,
          storedResources: 0,
          mineralComposition: ra.mineralComposition,
          asteroidType: 'regular',
          rewardValue: null,
        });
      }
    }

    // Update the mining unit's cargo and laser condition immediately
    let oldCondition = 100;
    if (payload.unitId != null && payload.cargoUsed != null) {
      const sprite = this.unitSprites.get(payload.unitId);
      if (sprite) {
        oldCondition = sprite.laserConditionPercent ?? 100;
        sprite.cargoUsed = payload.cargoUsed;
        if (payload.cargoCapacity != null) sprite.cargoCapacity = payload.cargoCapacity;
        if (payload.conditionPercent !== undefined) sprite.laserConditionPercent = payload.conditionPercent;
        sprite.drawVisuals();
      }
      // Refresh CommandDock if this unit is selected
      if (this.selectedEntity && this.selectedEntity.id === payload.unitId) {
        EventBus.emit('mmo-entity-selected', { entity: this.selectedEntity });
      }

      // Notify when cargo is full
      const cap = payload.cargoCapacity ?? sprite?.cargoCapacity ?? 0;
      if (cap > 0 && payload.cargoUsed >= cap) {
        this.showResourceToast('Cargo Full', '#ff4444', [
          { label: `${payload.cargoUsed}/${cap} cargo used`, color: '#ff8866' },
          { label: 'Dock at a planet or station to deposit', color: '#aabbcc' },
        ], 0, true);
      }

      // Warn at condition milestones (notification toast)
      const newCond = payload.conditionPercent ?? 100;
      if (newCond < oldCondition && this.isConditionMilestone(oldCondition, newCond)) {
        const icon = newCond <= 10 ? '\u2757' : '\u26A0';
        this.showNotification(`${icon} Mining laser condition: ${newCond}%`);
      }
    }

    const mineralNames: Record<string, string> = {
      stellar_reward: '$STELLAR',
      gold_reward: 'Gold',
      min_darkmat: 'Dark Matter',
      min_iridium: 'Iridium',
      min_rhodium: 'Rhodium',
      min_palladium: 'Palladium',
      min_titanium: 'Titanium',
      min_platinum: 'Platinum',
      min_iron: 'Iron',
      min_copper: 'Copper',
      // Crypto tokens from special asteroids
      GUSDC: 'USDC', GUSDT: 'USDT', GALA: 'GALA', GISD: 'ISD', GSOL: 'SOL',
    };

    const mineralColors: Record<string, string> = {
      stellar_reward: '#00ff88',
      gold_reward: '#FFD700',
      min_darkmat: '#6600cc',
      min_iridium: '#f0f0ff',
      min_rhodium: '#ffd700',
      min_palladium: '#ced8f6',
      min_titanium: '#c0c0d0',
      min_platinum: '#e5e4e2',
      min_iron: '#8a8a8a',
      min_copper: '#b87333',
      GUSDC: '#2775ca', GUSDT: '#26a17b', GALA: '#FFD700', GISD: '#00ff88', GSOL: '#9945ff',
    };

    // Sort by rarity: $STELLAR first, then legendary down to common
    const RARITY_ORDER = ['stellar_reward', 'min_darkmat', 'min_iridium', 'min_rhodium', 'min_palladium', 'min_titanium', 'min_platinum', 'min_iron', 'min_copper'];
    const entries = Object.entries(payload.minerals).filter(([, qty]) => qty > 0);
    entries.sort((a, b) => {
      const ai = RARITY_ORDER.indexOf(a[0]);
      const bi = RARITY_ORDER.indexOf(b[0]);
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    });

    if (entries.length > 0 && payload.asteroidId) {
      const asteroidSprite = this.asteroidSprites.get(payload.asteroidId);
      if (asteroidSprite) {
        const cond = payload.conditionPercent ?? 100;
        const effectiveBonus = (payload.yieldBonus && payload.yieldBonus > 0)
          ? payload.yieldBonus * (cond / 100) : 0;
        const bonusPct = effectiveBonus > 0 ? Math.round(effectiveBonus * 100) : 0;
        let shownBonus = false;
        const CRYPTO_TOKENS = ['GUSDC', 'GUSDT', 'GALA', 'GISD', 'GSOL'];
        for (const [id, qty] of entries) {
          const n = Number(qty);
          let label: string;
          if (id === 'stellar_reward') {
            label = `+${Math.floor(n)} $STELLAR`;
          } else if (id === 'gold_reward') {
            label = `+$${n.toFixed(2)} USDC`;
          } else if (id.startsWith('crypto:') || CRYPTO_TOKENS.includes(id)) {
            // Crypto tokens from crypto asteroids
            const tokenName = id.startsWith('crypto:') ? id.split(':')[1] : (mineralNames[id] || id);
            const isStable = ['USDC', 'USDT', 'GUSDC', 'GUSDT'].includes(tokenName);
            label = isStable ? `+$${n.toFixed(2)} ${tokenName}` : `+${n.toLocaleString()} ${tokenName}`;
          } else {
            label = `+${Math.floor(n)} ${mineralNames[id] || id}`;
          }
          if (bonusPct > 0 && !shownBonus) {
            label += ` (+${bonusPct}%)`;
            shownBonus = true;
          }
          this.spawnMiningPopup(asteroidSprite.x, asteroidSprite.y, label, mineralColors[id] || '#ffffff');
        }
        // Show condition popup only at milestones: 50%, every 10% down to 10%, then every 1% below 10%
        if (cond < oldCondition && this.isConditionMilestone(oldCondition, cond)) {
          const condColor = cond > 50 ? '#44ff44' : cond > 25 ? '#ffaa00' : '#ff4444';
          this.spawnMiningPopup(asteroidSprite.x, asteroidSprite.y, `Laser: ${cond}%`, condColor);
        }
      }
    }

    if ((payload as any).relicFound) {
      const p = payload as any;
      const relic = p.relic;
      const tierColors: Record<number, string> = { 1: '#ffffff', 2: '#44ff44', 3: '#4488ff', 4: '#aa44ff', 5: '#ffcc00' };
      const tierColor = tierColors[relic?.tier] || '#ffffff';
      const msg = p.relicDropped
        ? `Relic dropped on map (cargo full)!`
        : `Relic discovered: ${p.relicName || relic?.name} (T${p.relicTier || relic?.tier})`;
      this.showResourceToast('Relic Found!', tierColor, [
        { label: msg, color: '#aa88ff' },
        { label: `Weight: ${p.relicWeight || 10} cargo`, color: '#8899bb' },
      ]);
      // Also emit the legacy event for the existing notification
      if (relic) {
        EventBus.emit('relic-discovered', relic);
      }
    }
  }

  private onMineStarted(payload: {
    unitId?: string; asteroidId?: string; miningRate?: number;
    laserName?: string; laserTier?: string; maxMineralRarity?: string; yieldBonus?: number;
    conditionPercent?: number;
  }): void {
    if (!payload?.unitId || !payload?.asteroidId) return;

    // Update both state manager AND sprite so CommandDock sees the change
    const unit = this.mmoState.getUnit(payload.unitId);
    if (unit) unit.miningAsteroidId = payload.asteroidId;

    const unitSprite = this.unitSprites.get(payload.unitId);
    if (unitSprite) {
      unitSprite.miningAsteroidId = payload.asteroidId;
      if (payload.maxMineralRarity) unitSprite.maxMineralRarity = payload.maxMineralRarity;
      if (payload.laserTier) unitSprite.laserTier = payload.laserTier;
      if (payload.yieldBonus !== undefined) unitSprite.yieldBonus = payload.yieldBonus;
      if (payload.conditionPercent !== undefined) unitSprite.laserConditionPercent = payload.conditionPercent;
    }

    // Refresh CommandDock if this unit is selected
    if (this.selectedEntity && this.selectedEntity.id === payload.unitId) {
      EventBus.emit('mmo-entity-selected', { entity: this.selectedEntity });
    }

    const asteroidSprite = this.asteroidSprites.get(payload.asteroidId);
    if (unitSprite && asteroidSprite) {
      this.playMiningBeam(unitSprite, asteroidSprite);
      asteroidSprite.showMiningIndicator();
    }

    this.showNotification('Mining started');
  }

  private onMineStopped(payload: { unitId?: string; reason?: string }): void {
    if (!payload?.unitId) return;

    // Update both state manager AND sprite
    const unit = this.mmoState.getUnit(payload.unitId);
    if (unit) unit.miningAsteroidId = null;

    const unitSprite = this.unitSprites.get(payload.unitId);
    if (unitSprite) unitSprite.miningAsteroidId = null;

    // Refresh CommandDock if this unit is selected
    if (this.selectedEntity && this.selectedEntity.id === payload.unitId) {
      EventBus.emit('mmo-entity-selected', { entity: this.selectedEntity });
    }

    // Clear mining indicator on any asteroid this unit was mining
    for (const [, asteroidSprite] of this.asteroidSprites) {
      asteroidSprite.hideMiningIndicator();
    }

    if (payload.reason === 'cargo_full') {
      this.showNotification('Mining stopped — cargo full');
    } else if (payload.reason === 'asteroid_depleted') {
      this.showNotification('Mining stopped — asteroid depleted');
    } else if (payload.reason === 'laser_tier_insufficient') {
      this.showNotification('Mining stopped — laser upgrade required for remaining minerals');
    } else if (payload.reason === 'laser_broken') {
      this.showNotification('Mining stopped — laser broken (0% condition). Repair needed!');
    } else if (payload.reason === 'asteroid_split') {
      // Split notification is handled by onAsteroidSplit — no duplicate toast
    } else if (payload.reason !== 'player_toggle' && payload.reason !== 'player_stop') {
      this.showNotification('Mining stopped');
    }
  }

  private async onComponentsChanged(): Promise<void> {
    try {
      const monetization = ServiceLocator.get<any>(Services.MMO_MONETIZATION);
      if (!monetization) return;

      const [owned, defs] = await Promise.all([
        monetization.fetchPlayerComponents(),
        monetization.fetchAvailableComponents(),
      ]);

      const defMap = new Map<string, any>();
      for (const d of defs) defMap.set(d.id, d);

      // Build a map of shipId → equipped mining laser stats
      const laserByShip = new Map<string, { tier: string; conditionPercent: number; yieldBonus: number; maxMineralRarity: string }>();
      for (const inst of owned) {
        if (inst.status !== 'equipped' || !inst.shipId) continue;
        const def = defMap.get(inst.definitionId);
        if (!def) continue;
        const stats = def.stats || {};
        if (stats.miningYieldBonus !== undefined || stats.maxMineralRarity !== undefined) {
          laserByShip.set(inst.shipId, {
            tier: def.tier || inst.currentTier || 'mk1',
            conditionPercent: inst.conditionPercent ?? 100,
            yieldBonus: stats.miningYieldBonus || 0,
            maxMineralRarity: stats.maxMineralRarity || 'common',
          });
        }
      }

      // Update all player unit sprites
      for (const [unitId, sprite] of this.unitSprites) {
        if (sprite.ownerId !== this.mmoState.getUserId()) continue;
        const laser = laserByShip.get(unitId);
        if (laser) {
          sprite.laserTier = laser.tier;
          sprite.laserConditionPercent = laser.conditionPercent;
          sprite.yieldBonus = laser.yieldBonus;
          sprite.maxMineralRarity = laser.maxMineralRarity;
        } else {
          sprite.laserTier = 'mk0';
          sprite.laserConditionPercent = 100;
          sprite.yieldBonus = 0;
          sprite.maxMineralRarity = 'common';
        }
        sprite.drawVisuals();
      }

      // Refresh CommandDock if a player unit is selected
      if (this.selectedEntity && 'ownerId' in this.selectedEntity &&
          this.selectedEntity.ownerId === this.mmoState.getUserId()) {
        EventBus.emit('mmo-entity-selected', { entity: this.selectedEntity });
      }
    } catch (err) {
      // Non-critical — don't break the game loop
    }
  }

  private onShowNotification(message: string | { text: string; color?: string; duration?: number }): void {
    if (!message) return;
    if (typeof message === 'string') {
      this.showNotification(message);
    } else if (message.text) {
      this.showNotification(message.text, message.color, message.duration);
    }
  }

  private onComponentConditionUpdated(payload: { unitId: string; conditionPercent: number }): void {
    if (!payload?.unitId) return;
    const sprite = this.unitSprites.get(payload.unitId);
    if (sprite) {
      sprite.laserConditionPercent = payload.conditionPercent;
      sprite.drawVisuals();
    }
    if (this.selectedEntity && this.selectedEntity.id === payload.unitId) {
      EventBus.emit('mmo-entity-selected', { entity: this.selectedEntity });
    }
  }

  /**
   * Centered screen toast for resource events (mining, cargo unload, etc.)
   * Shows a header + color-coded mineral lines, centered on screen with
   * a dark backdrop. Scales in on appear, fades out after delay.
   */
  private showResourceToast(
    headerText: string,
    headerColor: string,
    lines: { label: string; color: string }[],
    delay: number = 3000,
    persistent: boolean = false,
  ): void {
    const camera = this.cameras.main;
    const centerX = camera.width / 2;
    const centerY = camera.height / 2;

    const container = this.add.container(centerX, centerY);
    container.setDepth(1000);
    container.setScrollFactor(0);

    // Header
    const header = this.add.text(0, 0, headerText, {
      fontSize: '20px',
      fontStyle: 'bold',
      color: headerColor,
      fontFamily: "'Orbitron', Arial, sans-serif",
    });
    header.setOrigin(0.5, 0);
    container.add(header);

    // Divider line under header
    const divider = this.add.graphics();
    divider.lineStyle(1, Phaser.Display.Color.HexStringToColor(headerColor).color, 0.4);
    divider.lineBetween(-100, header.height + 4, 100, header.height + 4);
    container.add(divider);

    // Resource lines
    let yOffset = header.height + 14;
    for (const line of lines) {
      const text = this.add.text(0, yOffset, line.label, {
        fontSize: '16px',
        fontStyle: 'bold',
        color: line.color,
        fontFamily: "'Share Tech Mono', monospace",
      });
      text.setOrigin(0.5, 0);
      container.add(text);
      yOffset += text.height + 4;
    }

    if (persistent) {
      yOffset += 6;
      const dismissText = this.add.text(0, yOffset, '[ click to dismiss ]', {
        fontSize: '12px',
        color: '#667788',
        fontFamily: "'Share Tech Mono', monospace",
      });
      dismissText.setOrigin(0.5, 0);
      container.add(dismissText);
      yOffset += dismissText.height + 4;
    }

    // Measure content for background
    const allTexts = container.list.filter(
      (c): c is Phaser.GameObjects.Text => c instanceof Phaser.GameObjects.Text,
    );
    const maxW = Math.max(...allTexts.map(t => t.width));
    const totalH = yOffset;
    const padX = 24;
    const padY = 16;

    // Dark backdrop with subtle border
    const bg = this.add.graphics();
    bg.fillStyle(0x0a0e1e, 0.85);
    bg.fillRoundedRect(-maxW / 2 - padX, -padY, maxW + padX * 2, totalH + padY * 2, 10);
    bg.lineStyle(1, Phaser.Display.Color.HexStringToColor(headerColor).color, 0.3);
    bg.strokeRoundedRect(-maxW / 2 - padX, -padY, maxW + padX * 2, totalH + padY * 2, 10);
    container.addAt(bg, 0);

    // Center the container vertically on the content
    container.setY(centerY - totalH / 2);

    // Scale-in animation
    container.setScale(0.8);
    container.setAlpha(0);
    this.tweens.add({
      targets: container,
      scaleX: 1,
      scaleY: 1,
      alpha: 1,
      duration: 200,
      ease: 'Back.easeOut',
    });

    if (persistent) {
      const dismissHandler = () => {
        this.input.off('pointerdown', dismissHandler);
        this.tweens.add({
          targets: container,
          alpha: 0,
          scaleX: 0.95,
          scaleY: 0.95,
          duration: 300,
          onComplete: () => container.destroy(true),
        });
      };
      this.time.delayedCall(400, () => {
        this.input.on('pointerdown', dismissHandler);
      });
    } else {
      this.tweens.add({
        targets: container,
        alpha: 0,
        scaleX: 0.95,
        scaleY: 0.95,
        duration: 600,
        delay,
        onComplete: () => container.destroy(true),
      });
    }
  }


  /**
   * Show the mining progress arc only on the asteroid the currently selected unit
   * is mining. This avoids confusing overlapping arcs when multiple ships mine
   * the same asteroid.
   */
  private updateMiningProgressArcs(): void {
    let activeAsteroidId: string | null = null;
    let progress = 0;

    if (this.selectedEntity instanceof MMOUnit && this.selectedEntity.miningAsteroidId) {
      activeAsteroidId = this.selectedEntity.miningAsteroidId;
      progress = this.selectedEntity.miningProgress;
    }

    for (const [id, asteroidSprite] of this.asteroidSprites) {
      if (id === activeAsteroidId) {
        asteroidSprite.updateMiningProgress(progress);
      } else {
        asteroidSprite.updateMiningProgress(0);
      }
    }
  }

  /**
   * Spawn a floating damage-text style popup above an asteroid showing mined minerals.
   * New popups push existing ones upward. Max 5 visible at once.
   */
  /**
   * Returns true if the condition drop from oldCond to newCond crosses a display milestone.
   * Milestones: 50%, then every 10% (40,30,20,10), then every 1% below 10%.
   */
  private isConditionMilestone(oldCond: number, newCond: number): boolean {
    if (newCond >= oldCond) return false;
    const thresholds = [50, 40, 30, 20, 10];
    for (const t of thresholds) {
      if (oldCond > t && newCond <= t) return true;
    }
    if (newCond < 10) return true;
    return false;
  }

  private spawnMiningPopup(worldX: number, worldY: number, label: string, color: string): void {
    const MAX_POPUPS = 5;
    const POPUP_SPACING = 18;

    // Push existing popups upward
    for (const existing of this.activeMiningPopups) {
      if (existing.active) {
        this.tweens.add({
          targets: existing,
          y: existing.y - POPUP_SPACING,
          duration: 200,
          ease: 'Power1',
        });
      }
    }

    // Remove oldest if at max
    while (this.activeMiningPopups.length >= MAX_POPUPS) {
      const oldest = this.activeMiningPopups.shift();
      if (oldest) {
        this.tweens.add({
          targets: oldest,
          alpha: 0,
          duration: 150,
          onComplete: () => oldest.destroy(),
        });
      }
    }

    const popup = this.add.text(worldX, worldY - 30, label, {
      fontSize: '14px',
      fontFamily: 'Arial',
      fontStyle: 'bold',
      color,
      stroke: '#000000',
      strokeThickness: 3,
    });
    popup.setOrigin(0.5, 1);
    popup.setDepth(500);
    popup.setAlpha(0);

    this.activeMiningPopups.push(popup);

    // Animate in: fade+rise, then hold, then fade out
    this.tweens.add({
      targets: popup,
      alpha: 1,
      y: worldY - 40,
      duration: 300,
      ease: 'Power2',
      onComplete: () => {
        this.tweens.add({
          targets: popup,
          alpha: 0,
          y: popup.y - 10,
          duration: 600,
          delay: 1800,
          ease: 'Power1',
          onComplete: () => {
            const idx = this.activeMiningPopups.indexOf(popup);
            if (idx >= 0) this.activeMiningPopups.splice(idx, 1);
            popup.destroy();
          },
        });
      },
    });
  }

  /** Handle cargo unloaded on dock */
  private onCargoUnloaded(payload: { unitId: string; minerals: Record<string, number>; stellarValue: number; cryptoPayouts: number; totalCargoFreed?: number }): void {
    if (!payload) return;

    const mineralNames: Record<string, string> = {
      min_darkmat: 'Dark Matter', min_iridium: 'Iridium', min_rhodium: 'Rhodium',
      min_palladium: 'Palladium', min_titanium: 'Titanium', min_platinum: 'Platinum',
      min_iron: 'Iron', min_copper: 'Copper',
    };
    const mineralColors: Record<string, string> = {
      min_darkmat: '#6600cc', min_iridium: '#f0f0ff', min_rhodium: '#ffd700',
      min_palladium: '#ced8f6', min_titanium: '#c0c0d0', min_platinum: '#e5e4e2',
      min_iron: '#8a8a8a', min_copper: '#b87333',
    };
    const RARITY_ORDER = ['min_darkmat', 'min_iridium', 'min_rhodium', 'min_palladium', 'min_titanium', 'min_platinum', 'min_iron', 'min_copper'];

    const mineralEntries = Object.entries(payload.minerals || {}).filter(([, qty]) => qty > 0);
    mineralEntries.sort((a, b) => {
      const ai = RARITY_ORDER.indexOf(a[0]);
      const bi = RARITY_ORDER.indexOf(b[0]);
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    });
    const hasMinerals = mineralEntries.length > 0;
    const hasStellar = (payload.stellarValue || 0) > 0;
    const relicsDeposited = (payload as any).relicsDeposited || 0;

    if (!hasMinerals && !hasStellar && relicsDeposited === 0) return;

    // Calculate total: prefer server's authoritative count, fall back to client-side
    let totalUnits = 0;
    const stellarCargoUnits = hasStellar ? Math.floor(payload.stellarValue / 0.01) : 0;
    if (payload.totalCargoFreed && payload.totalCargoFreed > 0) {
      totalUnits = payload.totalCargoFreed;
    } else {
      totalUnits = stellarCargoUnits;
      for (const [, qty] of mineralEntries) totalUnits += Number(qty);
    }

    const lines: { label: string; color: string }[] = [];

    // Total deposited shown FIRST
    if (totalUnits > 0) {
      lines.push({ label: `── Total Deposited: ${totalUnits} cargo ──`, color: '#ffffff' });
    }

    if (hasStellar) {
      lines.push({ label: `$STELLAR: ${stellarCargoUnits}`, color: '#00ff88' });
    }

    for (const [id, qty] of mineralEntries) {
      const n = Number(qty);
      lines.push({
        label: `${mineralNames[id] || id}: ${Math.floor(n)}`,
        color: mineralColors[id] || '#ffffff',
      });
    }

    if (relicsDeposited > 0) {
      lines.push({ label: `Relics Deposited: ${relicsDeposited}`, color: '#aa88ff' });
    }

    this.showResourceToast('Cargo Unloaded', '#00ccff', lines, 0, true);

    EventBus.emit('mmo-log-cargo-unloaded', payload);
  }

  private onActionRepair(data: { entity: any }): void {
    if (!data.entity?.id) return;
    this.mmoNetwork.sendRepairUnit(data.entity.id);
  }

  private onActionGoto(data: { entity: any; targetHex: { q: number; r: number } }): void {
    if (!data.entity?.id || !data.targetHex) return;
    this.mmoNetwork.sendMoveUnit(data.entity.id, data.targetHex);
    this.showNotification(`Navigating to (${data.targetHex.q}, ${data.targetHex.r})`);
    EventBus.emit('mmo-movement-queued', {
      unitId: data.entity.id,
      destination: data.targetHex,
    });
  }

  private onActionReturn(data: { entity: any }): void {
    if (!data.entity?.id) return;
    this.mmoNetwork.sendReturnToBase(data.entity.id);
    this.showNotification('Finding nearest dock...');
  }

  private onReturnToBaseConfirmed(payload: {
    unitId: string;
    targetId?: string;
    targetType?: string;
    targetPos?: HexCoord;
    distance?: number;
  }): void {
    if (!payload?.unitId) return;
    const distance = typeof payload.distance === 'number' ? Math.max(0, Math.round(payload.distance)) : null;
    const target = payload.targetType === 'platform' ? 'platform' : 'station';
    const eta = distance !== null ? ` (${distance} hex${distance === 1 ? '' : 'es'} out)` : '';
    this.showNotification(`Return queued to nearest ${target}${eta}. Auto-docking on arrival.`);
  }

  private onRepairResult(payload: {
    unitId: string;
    hpHealed: number;
    shieldGenRepaired: boolean;
    ironConsumed: number;
    autoMined: number;
    currentHp: number;
    maxHp: number;
    shields: number;
    maxShields: number;
    shieldGeneratorDamaged: boolean;
    cargoUsed: number;
    asteroidId?: string;
    asteroidMineralComposition?: Record<string, number>;
  }): void {
    if (!payload) return;

    const lines: { label: string; color: string }[] = [];
    if (payload.hpHealed > 0) {
      lines.push({ label: `+${payload.hpHealed} HP restored`, color: '#44ff44' });
    }
    if (payload.shieldGenRepaired) {
      lines.push({ label: 'Shield generator repaired', color: '#4499ff' });
    }
    const cargoIron = payload.ironConsumed - payload.autoMined;
    if (cargoIron > 0) {
      lines.push({ label: `Used ${cargoIron} iron from cargo`, color: '#8a8a8a' });
    }
    if (payload.autoMined > 0) {
      lines.push({ label: `Mined ${payload.autoMined} iron from asteroid`, color: '#b87333' });
    }

    if (lines.length > 0) {
      this.showResourceToast('Ship Repaired', '#44ff44', lines, 0, true);
    } else {
      this.showNotification('No repairs performed');
    }

    // Update state manager so data stays in sync
    this.mmoState.updateUnit({
      id: payload.unitId,
      currentHp: payload.currentHp,
      maxHp: payload.maxHp,
      shields: payload.shields,
      maxShields: payload.maxShields,
      cargoUsed: payload.cargoUsed,
    });

    // Update unit sprite visuals in real-time
    const sprite = this.unitSprites.get(payload.unitId);
    if (sprite) {
      sprite.currentHp = payload.currentHp;
      sprite.maxHp = payload.maxHp;
      sprite.currentShields = payload.shields;
      sprite.maxShields = payload.maxShields;
      sprite.cargoUsed = payload.cargoUsed;
      sprite.drawVisuals();
    }

    // Refresh sidebar if this unit is selected
    if (this.selectedEntity && this.selectedEntity.id === payload.unitId) {
      EventBus.emit('mmo-entity-selected', { entity: this.selectedEntity });
    }

    // Update asteroid sprite if repair auto-mined from it
    if (payload.asteroidId && payload.asteroidMineralComposition) {
      const asteroidSprite = this.asteroidSprites.get(payload.asteroidId);
      if (asteroidSprite) {
        asteroidSprite.mineralComposition = payload.asteroidMineralComposition;
        const remaining = Object.values(payload.asteroidMineralComposition)
          .reduce((s, v) => s + (Number(v) || 0), 0);
        asteroidSprite.storedResources = remaining;
        asteroidSprite.drawVisuals();
      }
      this.mmoState.updateAsteroid({
        id: payload.asteroidId,
        mineralComposition: payload.asteroidMineralComposition,
      });
      if (this.selectedEntity && this.selectedEntity.id === payload.asteroidId) {
        EventBus.emit('mmo-entity-selected', { entity: this.selectedEntity });
      }
    }
  }

  private onUnitDocked(payload: { unitId: string; planetId: string; message: string }): void {
    console.log('[MMOGame] Unit docked:', payload.unitId, 'at', payload.planetId);

    // Update state manager
    this.mmoState.updateUnit({ id: payload.unitId, dockedAtPlanetId: payload.planetId });

    // Update unit sprite — hide it from the map completely
    const unitSprite = this.unitSprites.get(payload.unitId);
    if (unitSprite) {
      unitSprite.dockedAtPlanetId = payload.planetId;
      unitSprite.enterDockedState();
    }

    // Switch selection to the planet/station where docking occurred
    const planetSprite = this.planetSprites.get(payload.planetId);
    const stationSprite = this.stationSprites.get(payload.planetId);
    const dockTarget = planetSprite || stationSprite;

    if (dockTarget) {
      // Deselect previous entity
      if (this.selectedEntity && this.selectedEntity !== dockTarget) {
        this.selectedEntity.setSelected(false);
      }
      this.selectedEntity = dockTarget;
      dockTarget.setSelected(true);
      EventBus.emit('mmo-entity-selection-resolved', { entity: dockTarget });
    } else if (this.selectedEntity === unitSprite) {
      // No planet/station sprite found — just deselect
      this.selectedEntity = null;
      EventBus.emit('mmo-deselect');
    }
  }

  private onUnitUndocked(payload: { unitId: string; message: string; newPosition?: { q: number; r: number } }): void {
    console.log('[MMOGame] Unit undocked:', payload.unitId, 'newPosition:', payload.newPosition);

    // Update state manager with new position if provided
    const stateUpdate: any = { id: payload.unitId, dockedAtPlanetId: null };
    if (payload.newPosition) {
      stateUpdate.position = payload.newPosition;
    }
    this.mmoState.updateUnit(stateUpdate);

    // Update unit sprite — show it on the map again
    const unitSprite = this.unitSprites.get(payload.unitId);
    if (unitSprite) {
      unitSprite.dockedAtPlanetId = null;

      // Update sprite position before making it visible
      if (payload.newPosition) {
        unitSprite.hexCoord = payload.newPosition;
        unitSprite.updateVisualPosition();
      }

      unitSprite.exitDockedState();

      // Force CommandDock to rebuild with undocked commands
      const myId = this.mmoState.getUserId();
      if (myId && unitSprite.ownerId === myId) {
        if (this.selectedEntity && this.selectedEntity !== unitSprite) {
          this.selectedEntity.setSelected(false);
        }
        this.selectedEntity = unitSprite;
        unitSprite.setSelected(true);
        // Force rebuild without deselect/reselect dance — avoids competing show/hide tweens
        EventBus.emit('mmo-entity-selection-resolved', { entity: unitSprite, forceRebuild: true });
        // Pan camera to the undocked unit so the player can see it
        this.panCameraToUnit(unitSprite);
      }
    }

    // If a planet/station is currently selected and the undocked unit is NOT ours, refresh docked tabs
    if ((this.selectedEntity instanceof MMOPlanet || this.selectedEntity instanceof MMOStation)) {
      EventBus.emit('mmo-entity-selection-resolved', { entity: this.selectedEntity });
    }
  }

  private onGoldenAsteroidDelivered(payload: {
    playerId: string; unitId: string; oldAsteroidId: string;
    count: number; variant?: string; rewardStellar?: number;
  }): void {
    console.log('[MMOGame] Golden asteroid delivered:', payload.oldAsteroidId, 'by', payload.unitId);

    // Remove the delivered asteroid from state and scene
    this.mmoState.removeAsteroid(payload.oldAsteroidId);
    const sprite = this.asteroidSprites.get(payload.oldAsteroidId);
    if (sprite) {
      try { sprite.destroy(); } catch (_) { /* already destroyed */ }
      this.asteroidSprites.delete(payload.oldAsteroidId);
    }

    // Show delivery popup only for the local player
    const myId = this.mmoState.getUserId();
    if (myId && payload.playerId === myId) {
      this.showDeliveryPopup(payload.variant, payload.rewardStellar, payload.count);
    }
  }

  private onGoldenAsteroidSpawned(payload: { asteroidId: string }): void {
    console.log('[MMOGame] New golden asteroid spawned:', payload.asteroidId);
    // The new asteroid will appear in the next tick update via chunk data
  }

  private onAsteroidSplit(payload: {
    originalAsteroidId: string;
    originalPosition: { q: number; r: number };
    trigger: string;
    fragments: Array<{
      id: string;
      position: { q: number; r: number };
      asteroidType: string;
      variant?: string;
      resourceRichness?: number;
      maxStorage?: number;
      mineralComposition?: Record<string, number>;
      rewardValue?: number;
    }>;
    shrapnelDamage: Array<{ unitId: string; damage: number; killed: boolean }>;
    mineralsTaxed: number;
  }): void {
    const origPos = payload.originalPosition;

    // Remove original asteroid sprite
    const originalSprite = this.asteroidSprites.get(payload.originalAsteroidId);
    if (originalSprite) {
      try { originalSprite.destroy(); } catch (_) { /* already destroyed */ }
      this.asteroidSprites.delete(payload.originalAsteroidId);
    }
    this.mmoState.removeAsteroid(payload.originalAsteroidId);

    // Clear selection if this asteroid was selected
    if (this.selectedEntity && this.selectedEntity.id === payload.originalAsteroidId) {
      this.selectedEntity = null;
      EventBus.emit('mmo-entity-deselected');
    }

    // Explosion at original position
    this.renderExplosion(origPos, '#ffaa00');

    // Create real asteroid sprites at original position, then tween to target hex
    for (const frag of payload.fragments) {
      const fragData = {
        id: frag.id,
        ownerId: null,
        position: origPos,
        miningLevel: 0,
        storedResources: 0,
        maxStorage: frag.maxStorage || 50,
        resourceRichness: frag.resourceRichness || 50,
        asteroidType: (frag.asteroidType || 'regular') as 'regular' | 'golden' | 'special',
        variant: frag.variant as any,
        rewardValue: frag.rewardValue ?? null,
        mineralComposition: frag.mineralComposition || null,
      };

      // Register in state at the FINAL position so tick updates don't duplicate
      this.mmoState.addAsteroid({ ...fragData, position: frag.position });

      // Render sprite at the ORIGINAL position, starting small and scaling up as it flies out
      const asteroidEntity = new MMOAsteroid(this, fragData);
      this.add.existing(asteroidEntity);
      this.asteroidSprites.set(frag.id, asteroidEntity);
      asteroidEntity.setDepth(5);
      asteroidEntity.setScale(0.3);
      asteroidEntity.setAlpha(0.6);

      // Tween from original hex to destination hex with scale-up
      const toPixel = axialToPixel(frag.position.q, frag.position.r);
      this.tweens.add({
        targets: asteroidEntity,
        x: toPixel.x,
        y: toPixel.y,
        scaleX: 1,
        scaleY: 1,
        alpha: 1,
        duration: 650,
        ease: 'Back.easeOut',
        onComplete: () => {
          asteroidEntity.hexCoord = frag.position;
        },
      });
    }

    // Fling debris particles outward from explosion centre
    const origPixel = axialToPixel(origPos.q, origPos.r);
    const debrisCount = 12;
    for (let i = 0; i < debrisCount; i++) {
      const angle = (Math.PI * 2 * i) / debrisCount + Math.random() * 0.5;
      const dist = 40 + Math.random() * 40;
      const size = 1.5 + Math.random() * 2;
      const debris = this.add.circle(origPixel.x, origPixel.y, size, 0xaaaaaa, 0.9);
      debris.setDepth(101);
      this.tweens.add({
        targets: debris,
        x: origPixel.x + Math.cos(angle) * dist,
        y: origPixel.y + Math.sin(angle) * dist,
        alpha: 0,
        duration: 500 + Math.random() * 300,
        ease: 'Quad.easeOut',
        onComplete: () => debris.destroy(),
      });
    }

    // Render shrapnel damage on affected units: flash + damage number + debris + camera shake
    const playerId = this.mmoState.getUserId();
    let playerShipHit = false;

    for (const dmg of payload.shrapnelDamage) {
      const unitSprite = this.unitSprites.get(dmg.unitId);
      if (unitSprite) {
        if (unitSprite.ownerId === playerId) playerShipHit = true;

        this.renderFlashEffect(unitSprite.hexCoord, 0xff8800);
        this.renderDamageNumber(unitSprite.hexCoord, dmg.damage, '#ffaa00');

        // Small debris shards converging on the ship from explosion origin
        const unitPixel = axialToPixel(unitSprite.hexCoord.q, unitSprite.hexCoord.r);
        for (let i = 0; i < 5; i++) {
          const a = Math.random() * Math.PI * 2;
          const shard = this.add.circle(
            unitPixel.x + Math.cos(a) * 25,
            unitPixel.y + Math.sin(a) * 25,
            1.5, 0xff6600, 0.8,
          );
          shard.setDepth(102);
          this.tweens.add({
            targets: shard,
            x: unitPixel.x + Math.cos(a) * 4,
            y: unitPixel.y + Math.sin(a) * 4,
            alpha: 0,
            duration: 350,
            ease: 'Quad.easeIn',
            onComplete: () => shard.destroy(),
          });
        }

        // Hull hit flash — brief alpha flicker on the unit container
        const origAlpha = unitSprite.alpha;
        this.tweens.add({
          targets: unitSprite,
          alpha: 0.3,
          duration: 80,
          yoyo: true,
          repeat: 2,
          onComplete: () => { if (unitSprite.scene) unitSprite.setAlpha(origAlpha); },
        });

        // If unit killed by shrapnel, show explosion after damage numbers
        if (dmg.killed) {
          this.time.delayedCall(400, () => {
            this.renderExplosion(unitSprite.hexCoord, '#ff4400');
          });
        }
      }
    }

    // Camera shake if the player's own ship was hit
    if (playerShipHit) {
      this.throttledShake(250, 0.008);
    }

    // Contextual notification
    const triggerText = payload.trigger === 'overcrowding'
      ? 'Overcrowding caused asteroid to fracture'
      : 'Nearby explosion fractured asteroid';
    const taxText = payload.mineralsTaxed > 0 ? ` (${Math.round(payload.mineralsTaxed)} minerals lost)` : '';
    this.showNotification(`${triggerText} into ${payload.fragments.length} pieces!${taxText}`);
  }

  private onAsteroidWarning(payload: { asteroidId: string; ticksRemaining: number }): void {
    const asteroid = this.asteroidSprites.get(payload.asteroidId);
    if (asteroid) {
      asteroid.setOvercrowdingWarning(true, payload.ticksRemaining);
      this.time.delayedCall(2000, () => {
        if (asteroid.scene) {
          asteroid.setOvercrowdingWarning(false);
        }
      });
    }
  }

  private onAsteroidDestroyed(payload: { position: { q: number; r: number }; variant: string; treasuryDeducted: number }): void {
    console.log(`[MMOGame] Asteroid destroyed at (${payload.position.q},${payload.position.r}), ${payload.treasuryDeducted} $STELLAR to treasury`);
    // The destroyed asteroid will be removed in the next tick update
  }

  private onAsteroidDepleted(payload: { asteroidId: string; position: { q: number; r: number } }): void {
    if (!payload?.asteroidId) return;

    // Remove from state manager
    this.mmoState.removeAsteroid(payload.asteroidId);

    // Remove sprite with a fade-out effect
    const sprite = this.asteroidSprites.get(payload.asteroidId);
    if (sprite) {
      this.tweens.add({
        targets: sprite,
        alpha: 0,
        scaleX: 0.3,
        scaleY: 0.3,
        duration: 600,
        ease: 'Power2',
        onComplete: () => {
          sprite.destroy();
          this.asteroidSprites.delete(payload.asteroidId);
        },
      });
    } else {
      this.asteroidSprites.delete(payload.asteroidId);
    }

    // Clear selection if this asteroid was selected
    if (this.selectedEntity && this.selectedEntity.id === payload.asteroidId) {
      this.selectedEntity = null;
      EventBus.emit('mmo-entity-deselected');
    }

    // Rebuild blocked hexes since the asteroid is gone
    this.blockedHexKeys = this.buildClientBlockedHexes();
  }

  private onAdminAsteroidsRemoved(payload: { asteroids: Array<{ id: string; position: { q: number; r: number } }> }): void {
    if (!payload?.asteroids) return;
    for (const ast of payload.asteroids) {
      this.mmoState.removeAsteroid(ast.id);
      const sprite = this.asteroidSprites.get(ast.id);
      const pos = sprite
        ? { x: sprite.x, y: sprite.y }
        : axialToPixel(ast.position.q, ast.position.r);
      if (sprite) {
        sprite.destroy();
        this.asteroidSprites.delete(ast.id);
      }
      this.playExplosionEffect(pos.x, pos.y);
    }
  }

  private playExplosionEffect(x: number, y: number): void {
    const colors = [0xff6600, 0xffaa00, 0xffff00, 0xffffff];
    // Flash ring
    const flash = this.add.circle(x, y, 5, 0xffff00, 0.8);
    flash.setDepth(1000);
    this.tweens.add({
      targets: flash, scaleX: 8, scaleY: 8, alpha: 0,
      duration: 400, ease: 'Power2',
      onComplete: () => flash.destroy(),
    });
    // 12 particle dots
    for (let i = 0; i < 12; i++) {
      const angle = (Math.PI * 2 * i) / 12 + (Math.random() - 0.5) * 0.5;
      const speed = 40 + Math.random() * 60;
      const color = colors[Math.floor(Math.random() * colors.length)];
      const dot = this.add.circle(x, y, 3 + Math.random() * 3, color, 1);
      dot.setDepth(1000);
      this.tweens.add({
        targets: dot,
        x: x + Math.cos(angle) * speed,
        y: y + Math.sin(angle) * speed,
        alpha: 0, scaleX: 0.2, scaleY: 0.2,
        duration: 300 + Math.random() * 300, ease: 'Power2',
        onComplete: () => dot.destroy(),
      });
    }
  }

  private playAsteroidDepletionEffect(x: number, y: number): void {
    const colors = [0x8a8a8a, 0xb87333, 0xc0c0d0, 0x666666];

    // Shrinking ring
    const ring = this.add.circle(x, y, 30, 0xaaaaaa, 0.6);
    ring.setDepth(1000);
    this.tweens.add({
      targets: ring, scaleX: 0.1, scaleY: 0.1, alpha: 0,
      duration: 500, ease: 'Power2',
      onComplete: () => ring.destroy(),
    });

    // Scatter particles outward
    for (let i = 0; i < 16; i++) {
      const angle = (Math.PI * 2 * i) / 16 + (Math.random() - 0.5) * 0.4;
      const speed = 30 + Math.random() * 50;
      const color = colors[Math.floor(Math.random() * colors.length)];
      const dot = this.add.circle(x, y, 2 + Math.random() * 2, color, 0.9);
      dot.setDepth(1000);
      this.tweens.add({
        targets: dot,
        x: x + Math.cos(angle) * speed,
        y: y + Math.sin(angle) * speed,
        alpha: 0, scaleX: 0.1, scaleY: 0.1,
        duration: 400 + Math.random() * 300, ease: 'Power2',
        onComplete: () => dot.destroy(),
      });
    }
  }

  /**
   * Draw a mining beam from a unit to an asteroid — a bright line with traveling
   * energy dots that fades out after ~800ms, giving clear visual feedback that
   * mining just happened.
   */
  private playMiningBeam(
    unitSprite: { x: number; y: number },
    asteroidSprite: { x: number; y: number; asteroidType?: string },
    sparkleColor?: number,
  ): void {
    const isGolden = asteroidSprite.asteroidType === 'golden';
    const beamColor = isGolden ? 0xFFD700 : 0x00ccff;
    const dotColor = isGolden ? 0xffffaa : 0x88eeff;

    const ux = unitSprite.x;
    const uy = unitSprite.y;
    const ax = asteroidSprite.x;
    const ay = asteroidSprite.y;

    // Main beam line
    const beam = this.add.graphics();
    beam.setDepth(999);
    beam.lineStyle(3, beamColor, 0.7);
    beam.lineBetween(ux, uy, ax, ay);

    // Thinner inner beam for glow effect
    const innerBeam = this.add.graphics();
    innerBeam.setDepth(999);
    innerBeam.lineStyle(1.5, 0xffffff, 0.5);
    innerBeam.lineBetween(ux, uy, ax, ay);

    // Fade the beams out
    this.tweens.add({
      targets: [beam, innerBeam],
      alpha: 0,
      duration: 800,
      ease: 'Power2',
      onComplete: () => { beam.destroy(); innerBeam.destroy(); },
    });

    // Traveling energy dots along the beam (unit → asteroid)
    const dotCount = 4;
    for (let i = 0; i < dotCount; i++) {
      const delay = i * 80;
      const dot = this.add.circle(ux, uy, 3, dotColor, 0.9);
      dot.setDepth(1000);

      this.tweens.add({
        targets: dot,
        x: ax,
        y: ay,
        alpha: 0,
        scaleX: 0.3,
        scaleY: 0.3,
        duration: 350,
        delay,
        ease: 'Quad.easeIn',
        onComplete: () => dot.destroy(),
      });
    }

    // Flash at the unit (mining origin)
    const flash = this.add.circle(ux, uy, 6, beamColor, 0.6);
    flash.setDepth(1000);
    this.tweens.add({
      targets: flash,
      scaleX: 2.5,
      scaleY: 2.5,
      alpha: 0,
      duration: 300,
      ease: 'Power2',
      onComplete: () => flash.destroy(),
    });

    // Impact sparkle at the asteroid — 4 particles burst outward in the mineral color
    const impactColor = sparkleColor ?? (isGolden ? 0xFFD700 : 0x00ccff);
    const sparkleDelay = 280;
    for (let i = 0; i < 4; i++) {
      const angle = (Math.PI * 2 * i) / 4 + (Math.random() - 0.5) * 0.8;
      const dist = 12 + Math.random() * 14;
      const spark = this.add.circle(ax, ay, 2.5 + Math.random() * 1.5, impactColor, 0.95);
      spark.setDepth(1001);
      this.tweens.add({
        targets: spark,
        x: ax + Math.cos(angle) * dist,
        y: ay + Math.sin(angle) * dist,
        alpha: 0,
        scaleX: 0.2,
        scaleY: 0.2,
        duration: 300 + Math.random() * 200,
        delay: sparkleDelay,
        ease: 'Power2',
        onComplete: () => spark.destroy(),
      });
    }
  }

  private onAllianceCreated(payload: any): void {
    console.log('[MMOGame] Alliance created:', payload);
    if (payload && payload.alliance) {
      const alliance = payload.alliance;
      this.mmoState.setAlliance({
        id: alliance.id,
        name: alliance.name,
        tag: alliance.tag,
        leaderId: alliance.leaderId || alliance.leader_user_id,
        memberCount: 1,
      });
      // Notify HUD to refresh alliance panel
      EventBus.emit('mmo-alliance-updated');
    }
  }

  private onPlayerResources(payload: any): void {
    if (payload && typeof payload.credits === 'number') {
      this.mmoState.setResources(payload.credits);
    }
  }

  private onAllianceLeft(payload: any): void {
    console.log('[MMOGame] Left alliance:', payload);
    this.mmoState.setAlliance(null);
    EventBus.emit('mmo-alliance-updated');
  }

  // ── Build queue handlers ─────────────────────────────────────

  private onBuildQueued(payload: any): void {
    console.log('[MMOGame] Build queued:', payload);
    this.mmoState.updatePendingBuild({
      buildId: payload.buildId,
      planetId: payload.planetId,
      unitType: payload.unitType,
      remainingSeconds: payload.remainingSeconds,
      totalSeconds: payload.totalSeconds ?? 15,
    });
    // Emit event so CommandDock can refresh its PRODUCING tab
    EventBus.emit('build-queue-updated');
  }

  private onBuildProgress(payload: any): void {
    this.mmoState.updatePendingBuild({
      buildId: payload.buildId,
      planetId: payload.planetId,
      unitType: payload.unitType,
      remainingSeconds: payload.remainingSeconds,
      totalSeconds: payload.totalSeconds ?? 15,
    });
    // Emit event so CommandDock can update countdown
    EventBus.emit('build-queue-updated');
  }

  private onBuildComplete(payload: any): void {
    console.log('[MMOGame] Build complete:', payload);
    this.mmoState.removePendingBuild(payload.buildId);
    EventBus.emit('build-queue-updated');

    // The unit will arrive via the next tick update (it's now in the DB),
    // but also try to render immediately if we have the data
    if (payload.unitId && payload.position) {
      this.mmoState.updateUnit({
        id: payload.unitId,
        type: payload.unitType,
        ownerId: this.mmoState.getUserId() || '',
        position: payload.position,
        currentHp: 100,
        maxHp: 100,
        shields: 0,
        maxShields: 0,
        movementOrder: null,
        speed: 1,
        attackRange: 1,
        attack: 10,
        defense: 10,
      } as any);
      this.renderEntities();
    }
  }

  private onAllianceLeaveAction(): void {
    console.log('[MMOGame] Leave alliance action');
    this.mmoNetwork.sendAllianceLeave();
  }

  private onActionDock(data: { entity: any; planetId?: string; stationId?: string; alienUnitId?: string }): void {
    console.log('[MMOGame] Dock action requested:', data.entity.id,
      data.alienUnitId ? `at alien ${data.alienUnitId}` :
      data.stationId ? `at station ${data.stationId}` :
      `at planet ${data.planetId}`);

    // Verify it's a unit
    if (!('unitType' in data.entity) && !('type' in data.entity)) {
      console.warn('[MMOGame] Dock action called on non-unit entity');
      return;
    }

    // Alien Leviathan docking path
    if (data.alienUnitId) {
      this.mmoNetwork.sendDockAlien(data.entity.id, data.alienUnitId);
      return;
    }

    // Station docking path
    if (data.stationId) {
      this.mmoNetwork.sendDockAtStation(data.entity.id, data.stationId);
      return;
    }

    // Planet docking path
    if (data.planetId) {
      this.mmoNetwork.sendDockUnit(data.entity.id, data.planetId);
    }
  }

  private onActionUndock(data: { entity: any }): void {
    console.log('[MMOGame] Undock action requested:', data.entity);

    // Verify it's a unit
    if (!('unitType' in data.entity) && !('type' in data.entity)) {
      console.warn('[MMOGame] Undock action called on non-unit entity');
      return;
    }

    // Send undock request to server
    this.mmoNetwork.sendUndockUnit(data.entity.id);

    console.log('[MMOGame] Undock request sent for unit:', data.entity.id);
  }

  private centerOnPlayerShip(): void {
    console.log('[MMOGame] Center on player ship requested');

    // Get player's units
    const playerUnits = this.mmoState.getPlayerUnits();

    if (playerUnits.length === 0) {
      console.warn('[MMOGame] No player units found to center on');
      return;
    }

    // Center on the first unit (or selected unit if available)
    const targetUnit = playerUnits[0];
    const worldPos = axialToPixel(targetUnit.position.q, targetUnit.position.r);

    // Center camera once — follow only engages during movement
    this.cameras.main.centerOn(worldPos.x, worldPos.y);

    console.log(`[MMOGame] Centering camera on unit at (${targetUnit.position.q}, ${targetUnit.position.r})`);
  }

  /**
   * Smooth-pan the camera to a unit's current hex position over the tick duration.
   * Uses camera.pan() instead of startFollow/stopFollow to avoid jank from
   * repeated engage/disengage cycles.
   */
  private panCameraToUnit(unit: MMOUnit): void {
    if (!this.cameraFollowEnabled) return;
    this.cameraFollowTarget = unit;
    const pixel = axialToPixel(unit.hexCoord.q, unit.hexCoord.r);
    this.cameras.main.pan(pixel.x, pixel.y, 850, 'Sine.easeInOut');
  }

  private disengageCameraFollow(): void {
    if (this.cameraFollowTarget) {
      this.cameras.main.panEffect.reset();
      this.cameraFollowTarget = null;
    }
  }

  private onToggleCameraFollow(): void {
    this.cameraFollowEnabled = !this.cameraFollowEnabled;
    if (!this.cameraFollowEnabled) {
      this.disengageCameraFollow();
    }
    EventBus.emit('camera-follow-changed', { enabled: this.cameraFollowEnabled });
  }

  /** Fleet sidebar: select a free-flying unit, pan camera to it */
  private onFleetSelectUnit(data: { unitId: string }): void {
    let unitSprite = this.unitSprites.get(data.unitId);

    // If sprite doesn't exist yet (hex not explored), force-render from state data
    if (!unitSprite) {
      const unitData = this.mmoState.getUnit(data.unitId);
      if (!unitData) return;
      // Ensure the hex is marked explored so sprite persists
      const hexKey = `${unitData.position.q},${unitData.position.r}`;
      this.mmoState.addExploredHex(hexKey);
      this.renderUnit(unitData);
      unitSprite = this.unitSprites.get(data.unitId);
      if (!unitSprite) return;
    }

    // Select the unit
    if (this.selectedEntity) {
      this.selectedEntity.setSelected(false);
    }
    this.selectedEntity = unitSprite;
    unitSprite.setSelected(true);
    EventBus.emit('mmo-entity-selection-resolved', { entity: unitSprite });

    // Center camera on the unit but don't follow — follow only during movement
    const worldPos = axialToPixel(unitSprite.hexCoord.q, unitSprite.hexCoord.r);
    this.cameras.main.centerOn(worldPos.x, worldPos.y);
  }

  /** Fleet sidebar: select a docked unit — pan to its dock location and activate the ship tab */
  private onFleetSelectDockedUnit(data: { unitId: string; dockedAtId: string }): void {
    // Pan to the planet/station where the unit is docked
    const planetSprite = this.planetSprites.get(data.dockedAtId);
    const stationSprite = this.stationSprites.get(data.dockedAtId);
    const target = planetSprite || stationSprite;

    if (!target) return;

    const coord = target instanceof MMOPlanet
      ? (target as any).hexCoord || (target as any).position
      : (target as any).hexCoord || (target as any).position;
    if (coord) {
      const worldPos = axialToPixel(coord.q, coord.r);
      this.cameras.main.centerOn(worldPos.x, worldPos.y);
    }

    // Select the planet/station
    if (this.selectedEntity) {
      this.selectedEntity.setSelected(false);
    }
    this.selectedEntity = target;
    target.setSelected(true);
    EventBus.emit('mmo-entity-selection-resolved', { entity: target });

    // After the CommandDock processes the selection, activate the docked ship tab
    // Small delay to let CommandDock rebuild with the new entity
    this.time.delayedCall(50, () => {
      EventBus.emit('fleet-activate-docked-tab', { unitId: data.unitId });
    });
  }

  /** Fleet sidebar: select a station, pan camera to it */
  private onFleetSelectStation(data: { stationId: string }): void {
    let stationSprite = this.stationSprites.get(data.stationId);

    // If sprite doesn't exist yet (hex not explored), force-render from state data
    if (!stationSprite) {
      const stationData = this.mmoState.getStation(data.stationId);
      if (!stationData) return;
      const hexKey = `${stationData.position.q},${stationData.position.r}`;
      this.mmoState.addExploredHex(hexKey);
      this.renderStation(stationData);
      stationSprite = this.stationSprites.get(data.stationId);
      if (!stationSprite) return;
    }

    const coord = (stationSprite as any).hexCoord || (stationSprite as any).position;
    if (coord) {
      const worldPos = axialToPixel(coord.q, coord.r);
      this.cameras.main.centerOn(worldPos.x, worldPos.y);
    }

    if (this.selectedEntity) {
      this.selectedEntity.setSelected(false);
    }
    this.selectedEntity = stationSprite;
    stationSprite.setSelected(true);
    EventBus.emit('mmo-entity-selection-resolved', { entity: stationSprite });
  }

  private onSelectPurchasedUnit(data: { unitId: string }): void {
    const trySelect = (attempts: number) => {
      const unitSprite = this.unitSprites.get(data.unitId);
      if (unitSprite) {
        // Deselect current selection
        if (this.selectedEntity) {
          this.selectedEntity.setSelected(false);
        }
        // Select the new unit and center camera
        this.selectedEntity = unitSprite;
        unitSprite.setSelected(true);
        EventBus.emit('mmo-entity-selection-resolved', { entity: unitSprite });
        const worldPos = axialToPixel(unitSprite.hexCoord.q, unitSprite.hexCoord.r);
        this.cameras.main.centerOn(worldPos.x, worldPos.y);
        console.log(`[MMOGame] Focused on purchased unit ${data.unitId} at (${unitSprite.hexCoord.q}, ${unitSprite.hexCoord.r})`);
      } else if (attempts < 10) {
        // Unit may not have arrived via tick update yet — retry
        this.time.delayedCall(500, () => trySelect(attempts + 1));
      }
    };
    trySelect(0);
  }

  private onNavigateToPosition(data: { q: number; r: number; unitId?: string; wreckId?: string }): void {
    const worldPos = axialToPixel(data.q, data.r);
    this.cameras.main.pan(worldPos.x, worldPos.y, 800, 'Sine.easeInOut');

    // After pan, try to select the entity at that position
    this.time.delayedCall(850, () => {
      if (data.unitId) {
        const unitSprite = this.unitSprites.get(data.unitId);
        if (unitSprite) {
          if (this.selectedEntity) this.selectedEntity.setSelected(false);
          this.selectedEntity = unitSprite;
          unitSprite.setSelected(true);
          EventBus.emit('mmo-entity-selection-resolved', { entity: unitSprite });
        }
      } else if (data.wreckId) {
        const wreckSprite = this.wreckSprites.get(data.wreckId);
        if (wreckSprite) {
          if (this.selectedEntity) this.selectedEntity.setSelected(false);
          this.selectedEntity = wreckSprite;
          wreckSprite.setSelected(true);
          EventBus.emit('mmo-entity-selection-resolved', { entity: wreckSprite });
        }
      }
    });
  }

  private onWorldState(payload: any): void {
    console.log('[MMOGame] World state received:', payload);

    // Clear loaded chunks tracking for fresh session
    this.loadedChunks.clear();

    // Update MMO state with world data
    this.mmoState.setWorldState(payload);

    // Mark initial chunks as loaded
    if (payload.chunks) {
      for (const chunk of payload.chunks) {
        this.loadedChunks.add(`${chunk.chunkX},${chunk.chunkY}`);
      }
      console.log(`[MMOGame] Marked ${payload.chunks.length} initial chunks as loaded`);
    }

    // Load alliance data if present
    if (payload.alliance) {
      this.mmoState.setAlliance(payload.alliance);
      console.log('[MMOGame] Alliance loaded:', payload.alliance.name, `[${payload.alliance.tag}]`);
    }

    // Load territory data if present
    if (payload.territories && payload.territories.length > 0) {
      this.mmoState.setTerritories(payload.territories);
    }

    // Re-render everything
    this.renderVisibleHexes();
    this.renderFogOfWar();
    this.renderTerritoryBorders();
    this.renderEntities();
    this.updateStackCounts();

    // Center camera on player's ship (important after death/respawn)
    this.time.delayedCall(200, () => {
      this.centerOnPlayerShip();
    });

    // Notify HUD that world is ready (triggers initial resource/progression requests)
    EventBus.emit(MMOEvents.WORLD_JOINED, payload);

    // Show "While You Were Away" recap via HUD scene (reconnect path)
    const offlineRecap = this.mmoState.consumeOfflineRecap();
    if (offlineRecap && offlineRecap.length > 0) {
      this.time.delayedCall(500, () => {
        EventBus.emit('show-offline-recap', offlineRecap);
      });
    }
  }

  private onChunkData(payload: any): void {
    if (!payload.chunks || !Array.isArray(payload.chunks)) return;

    console.log(`[MMOGame] Received ${payload.chunks.length} chunks from server`);

    for (const chunk of payload.chunks) {
      this.mmoState.updateChunk(chunk);
      this.loadedChunks.add(`${chunk.chunkX},${chunk.chunkY}`);
    }

    // Re-render to show newly loaded entities
    this.renderVisibleHexes();
    this.renderFogOfWar();
    this.renderEntities();
    this.updateStackCounts();
  }

  private onTickUpdate(payload: any): void {

    // Update fog of war from server if provided
    if (payload.fogOfWar && payload.currentVision) {
      this.mmoState.updateVision(payload.fogOfWar, payload.currentVision);
    }

    // Update units in state manager with new positions
    // Track which units are visible THIS tick for fog-snap detection
    const currentTickUnitIds = new Set<string>();
    if (payload.units && payload.units.length > 0) {
      const playerId = this.mmoState.getUserId();

      for (const unitData of payload.units) {
        const isMyUnit = unitData.ownerId === playerId;
        currentTickUnitIds.add(unitData.id);

        // Option 2: Fog snap — if this unit just entered visibility, force-snap its position
        // so it doesn't tween from a stale cached location
        if (!this.previousTickUnitIds.has(unitData.id)) {
          const sprite = this.unitSprites.get(unitData.id);
          if (sprite) {
            sprite.forceSnapPosition(unitData.position);
          }
        }

        // Update all units from tick data (own + other players visible in fog)
        this.mmoState.updateUnit(unitData);

        // If this is our unit and server didn't send fog data, mark locally
        if (isMyUnit && !payload.fogOfWar) {
          const pos = unitData.position;

          // Mark current hex as explored
          this.mmoState.addExploredHex(`${pos.q},${pos.r}`);

          // Mark surrounding hexes (vision range) as explored
          for (let dq = -2; dq <= 2; dq++) {
            for (let dr = -2; dr <= 2; dr++) {
              const hexKey = `${pos.q + dq},${pos.r + dr}`;
              this.mmoState.addExploredHex(hexKey);
            }
          }
        }
      }
    }
    this.previousTickUnitIds = currentTickUnitIds;

    // Check if the selected unit moved or had data change (HP/shields) before sprites update
    let selectedUnitMoved = false;
    let selectedUnitDataChanged = false;
    if (this.selectedEntity && payload.units) {
      const updatedData = payload.units.find((u: any) => u.id === this.selectedEntity!.id);
      if (updatedData && this.selectedEntity.hexCoord) {
        if (updatedData.position.q !== this.selectedEntity.hexCoord.q || updatedData.position.r !== this.selectedEntity.hexCoord.r) {
          selectedUnitMoved = true;
        }
        // Detect HP, shield, or ability cooldown changes for CommandDock refresh
        const sel = this.selectedEntity as any;
        if (updatedData.currentHp !== sel.currentHp || updatedData.shields !== sel.shields) {
          selectedUnitDataChanged = true;
        }
        if (!selectedUnitDataChanged && updatedData.abilities && sel.abilities) {
          for (let i = 0; i < updatedData.abilities.length; i++) {
            if (updatedData.abilities[i]?.currentCooldown !== sel.abilities?.[i]?.currentCooldown) {
              selectedUnitDataChanged = true;
              break;
            }
          }
        }
      }
    }

    // Remove destroyed units from state and scene
    if (payload.destroyedUnits && payload.destroyedUnits.length > 0) {
      for (const unitId of payload.destroyedUnits) {
        this.removeUnitFromClient(unitId);
      }
    }

    // Update asteroids from tick data
    if (payload.asteroids && payload.asteroids.length > 0) {
      for (const asteroidData of payload.asteroids) {
        this.mmoState.updateAsteroid(asteroidData);
      }
    }

    // Update planets from tick data (discovered via fog of war)
    if (payload.planets && payload.planets.length > 0) {
      for (const planetData of payload.planets) {
        this.mmoState.updatePlanet(planetData);
      }
    }

    // Update stations from tick data (deployed/relocated)
    if (payload.stations && payload.stations.length > 0) {
      for (const stationData of payload.stations) {
        this.mmoState.updateStation(stationData);
        this.renderStation(stationData);
        // Refresh CommandDock if this station is selected
        if (this.selectedEntity && this.selectedEntity.id === stationData.id) {
          EventBus.emit('mmo-entity-selected', { entity: this.selectedEntity });
        }
      }
    }

    // Update orbital platforms from tick data
    if ((payload as any).platforms && (payload as any).platforms.length > 0) {
      for (const platformData of (payload as any).platforms) {
        this.mmoState.updatePlatform(platformData);
      }
    }

    // Add newly created wrecks from combat
    if (payload.wrecks && payload.wrecks.length > 0) {
      for (const wreckData of payload.wrecks) {
        this.mmoState.addWreck(wreckData);
      }
    }

    // Update ambient entities from tick data
    if (payload.ambientEntities && payload.ambientEntities.length > 0) {
      for (const aeData of payload.ambientEntities) {
        this.mmoState.updateAmbientEntity(aeData);
      }
    }

    // Remove despawned ambient entities
    if (payload.despawnedAmbient && payload.despawnedAmbient.length > 0) {
      for (const entityId of payload.despawnedAmbient) {
        this.mmoState.removeAmbientEntity(entityId);
        const sprite = this.ambientSprites.get(entityId);
        if (sprite) {
          try { sprite.destroy(); } catch (_) { /* already destroyed */ }
          this.ambientSprites.delete(entityId);
        }
      }
    }

    // Update dropped relics from tick data
    if ((payload as any).droppedRelics) {
      const serverRelicIds = new Set<string>();
      for (const relicData of (payload as any).droppedRelics) {
        serverRelicIds.add(relicData.id);
        this.renderDroppedRelic(relicData);
      }
      // Remove relics no longer in the server's dropped list (picked up or despawned)
      for (const [relicId, sprite] of this.droppedRelicSprites) {
        if (!serverRelicIds.has(relicId)) {
          try { sprite.destroy(); } catch (_) { /* already destroyed */ }
          this.droppedRelicSprites.delete(relicId);
        }
      }
    }

    // Update loot drops from tick data
    if (payload.lootDrops && payload.lootDrops.length > 0) {
      const serverLootIds = new Set<string>();
      for (const lootData of payload.lootDrops) {
        serverLootIds.add(lootData.id);
        this.mmoState.updateLootDrop(lootData);
      }
      // Remove loot drops no longer present (claimed or expired)
      for (const [lootId, sprite] of this.lootDropSprites) {
        if (!serverLootIds.has(lootId)) {
          this.mmoState.removeLootDrop(lootId);
          try { sprite.destroy(); } catch (_) { /* already destroyed */ }
          this.lootDropSprites.delete(lootId);
          // Clear selection if this loot was selected
          if (this.selectedEntity && this.selectedEntity.id === lootId) {
            this.selectedEntity = null;
            EventBus.emit('mmo-entity-deselected');
          }
        }
      }
    }

    // Re-render hexes to show newly explored areas
    this.renderVisibleHexes();

    // Periodically rebuild the spatial index to fix any desync (every 30 ticks)
    const currentTick = this.mmoState.getTick();
    if (currentTick > 0 && currentTick % 30 === 0) {
      this.mmoState.rebuildSpatialIndex();
    }

    // Option 4: Periodic full position sync from server (every 60 ticks)
    // Server sends compact authoritative positions for ALL visible units
    if (payload.positionSync && payload.positionSync.length > 0) {
      for (const sync of payload.positionSync) {
        const sprite = this.unitSprites.get(sync.id);
        if (sprite) {
          if (sprite.movementOrder || sprite.isPositionAnimating()) continue;
          const serverPixel = axialToPixel(sync.q, sync.r);
          const driftDist = Math.hypot(serverPixel.x - sprite.x, serverPixel.y - sprite.y);
          if (driftDist > 30) { // Even mild drift gets corrected on sync ticks
            sprite.forceSnapPosition({ q: sync.q, r: sync.r });
          }
        }
      }
    }

    // Every 10 ticks, snap all unit sprites to their authoritative server positions
    // This prevents cumulative drift from interrupted movement tweens
    if (currentTick > 0 && currentTick % 10 === 0) {
      for (const [, sprite] of this.unitSprites) {
        if (sprite && 'hexCoord' in sprite) {
          const unit = sprite as MMOUnit;
          if (unit.movementOrder || unit.isPositionAnimating()) continue;
          const expectedPixel = axialToPixel(unit.hexCoord.q, unit.hexCoord.r);
          const driftDist = Math.hypot(expectedPixel.x - unit.x, expectedPixel.y - unit.y);
          if (driftDist > 50) { // More than ~half a hex drifted
            unit.setPosition(expectedPixel.x, expectedPixel.y);
            if ((unit as any).moveTween) {
              (unit as any).moveTween.destroy();
              (unit as any).moveTween = null;
            }
          }
        }
      }
    }

    // Skip expensive re-render during active combat animations to prevent freezing.
    // State updates above are still processed — only the visual re-render is deferred.
    if (!this.combatAnimating) {
      this.renderEntities();
    }

    // Update mining progress arcs on asteroids being mined by our units
    this.updateMiningProgressArcs();

    // Refresh CommandDock after sprites updated so MINE/DOCK buttons and HP reflect changes
    if ((selectedUnitMoved || selectedUnitDataChanged) && this.selectedEntity) {
      EventBus.emit('mmo-entity-selected', { entity: this.selectedEntity });
    }

    // Smooth-pan the camera alongside the selected unit when it moves.
    if (this.cameraFollowEnabled && selectedUnitMoved && this.selectedEntity instanceof MMOUnit) {
      const playerId = this.mmoState.getUserId();
      if ((this.selectedEntity as MMOUnit).ownerId === playerId) {
        this.panCameraToUnit(this.selectedEntity as MMOUnit);
      }
    }

    // Sync engagement state from tick update
    if (payload.engagedUnits) {
      this.syncEngagementState(new Set<string>(payload.engagedUnits));
    }

    // Update fog of war if vision changed
    this.renderFogOfWar();

    // Update movement destination markers
    if (payload.units && payload.units.length > 0) {
      for (const unitData of payload.units) {
        if (unitData.ownerId !== this.mmoState.getUserId()) continue;

        if (unitData.movementOrder) {
          const entry = this.movementMarkers.get(unitData.id);
          if (entry) {
            const destPixel = axialToPixel(unitData.movementOrder.q, unitData.movementOrder.r);
            entry.destPixel = destPixel;
            const ticks = unitData.movementOrder.turnsRemaining ?? 0;
            this.updateEtaLabel(entry.eta, destPixel, ticks);
          }
        } else {
          this.clearMovementMarker(unitData.id);
        }
      }
    }
  }

  private onUnitMoved(payload: any): void {
    if (!payload || !payload.unitId || !payload.targetHex) return;

    const unitSprite = this.unitSprites.get(payload.unitId);
    if (!unitSprite || unitSprite.ownerId !== this.mmoState.getUserId()) return;

    this.clearMovementMarker(payload.unitId);

    const gfx = this.add.graphics().setDepth(1.5);
    const eta = this.add.text(0, 0, '', {
      fontFamily: 'monospace',
      fontSize: '13px',
      color: '#00ffaa',
      stroke: '#000000',
      strokeThickness: 3,
    }).setDepth(1.5).setOrigin(0.5, 1);

    const fromPixel = { x: unitSprite.x, y: unitSprite.y };
    const destPixel = axialToPixel(payload.targetHex.q, payload.targetHex.r);
    // Rough ETA from hex distance — will be corrected by first tick update
    const ticks = payload.turnsRemaining
      ?? hexDistance(unitSprite.hexCoord, payload.targetHex);

    this.drawMovementMarker(gfx, fromPixel, destPixel);
    this.updateEtaLabel(eta, destPixel, ticks);

    this.movementMarkers.set(payload.unitId, { gfx, eta, destPixel });
  }

  private drawMovementMarker(
    gfx: Phaser.GameObjects.Graphics,
    from: { x: number; y: number },
    dest: { x: number; y: number },
  ): void {
    gfx.clear();

    // Line from unit to destination
    gfx.lineStyle(2, 0x00ffaa, 0.5);
    gfx.lineBetween(from.x, from.y, dest.x, dest.y);

    // Destination ring
    gfx.lineStyle(3, 0x00ffaa, 0.8);
    gfx.strokeCircle(dest.x, dest.y, 20);
    gfx.lineStyle(1, 0x00ffaa, 0.4);
    gfx.strokeCircle(dest.x, dest.y, 28);

    // Crosshair
    gfx.lineStyle(2, 0x00ffaa, 0.6);
    gfx.lineBetween(dest.x - 8, dest.y, dest.x + 8, dest.y);
    gfx.lineBetween(dest.x, dest.y - 8, dest.x, dest.y + 8);
  }

  private updateEtaLabel(
    label: Phaser.GameObjects.Text,
    dest: { x: number; y: number },
    ticks: number,
  ): void {
    if (ticks > 0) {
      label.setText(`~${ticks}s`);
      label.setPosition(dest.x, dest.y - 34);
      label.setVisible(true);
    } else {
      label.setVisible(false);
    }
  }

  private clearMovementMarker(unitId: string): void {
    const entry = this.movementMarkers.get(unitId);
    if (entry) {
      entry.gfx.destroy();
      entry.eta.destroy();
      this.movementMarkers.delete(unitId);
    }
  }

  private onCombatResult(data: any): void {
    console.log('[MMOGame] Combat result data:', data);
    // Render combat effects - data already contains the full event with payload
    this.onCombatResultReceived(data);
  }

  update(_time: number, _delta: number): void {
    // Keep screen-locked overlays (bg nebula, fixed starfield, vignette) sized
    // to the visible viewport regardless of camera zoom. No-op when zoom is
    // unchanged. Runs first so a zoom-tween mid-frame doesn't show a flash.
    this.syncFixedOverlaysToZoom();

    // Check if viewport changed
    const camera = this.cameras.main;
    const hasViewportChanged =
      Math.abs(camera.scrollX - this.lastCameraX) > 50 || Math.abs(camera.scrollY - this.lastCameraY) > 50;

    if (hasViewportChanged) {
      this.updateViewport();
      this.renderVisibleHexes();
      this.renderFogOfWar();
      this.renderTerritoryBorders();
      this.debounceChunkRequests();
    }

    // Viewport culling — hide sprites outside camera view
    this.updateSpriteCulling();

    // Redraw movement lines from the sprite's current visual position each frame
    this.updateMovementMarkerLines();

    // Highlight the hex under the mouse when in move, mine-target, or relocation mode
    if (this.isMovementMode || this.isRelocationMode || this.isStructureDeployMode) {
      this.drawMovementHoverHex();
    } else if (this.isMineMode) {
      this.drawMineHoverHex();
    }

    this.updateHoverHighlight();
  }

  private updateMovementMarkerLines(): void {
    if (this.movementMarkers.size === 0) return;
    for (const [unitId, entry] of this.movementMarkers) {
      const sprite = this.unitSprites.get(unitId);
      if (!sprite) continue;
      this.drawMovementMarker(entry.gfx, { x: sprite.x, y: sprite.y }, entry.destPixel);
    }
  }

  private updateHoverHighlight(): void {
    const pointer = this.input.activePointer;
    const camera = this.cameras.main;
    const worldPoint = camera.getWorldPoint(pointer.x, pointer.y);
    const hex = pixelToAxial(worldPoint.x, worldPoint.y);
    const hexKey = `${hex.q},${hex.r}`;

    if (hexKey === this.lastHoveredHexKey) return;
    this.lastHoveredHexKey = hexKey;
    this.hoverGraphics.clear();

    if (!this.mmoState.isHexExplored(hexKey)) return;

    const pixel = axialToPixel(hex.q, hex.r);
    const corners = this.getHexCorners(pixel.x, pixel.y);

    // Soft fill
    this.hoverGraphics.fillStyle(0x00ccff, 0.10);
    this.hoverGraphics.beginPath();
    this.hoverGraphics.moveTo(corners[0].x, corners[0].y);
    for (let i = 1; i < 6; i++) {
      this.hoverGraphics.lineTo(corners[i].x, corners[i].y);
    }
    this.hoverGraphics.closePath();
    this.hoverGraphics.fillPath();

    // Outer glow border
    this.hoverGraphics.lineStyle(4, 0x0088aa, 0.15);
    this.hoverGraphics.beginPath();
    this.hoverGraphics.moveTo(corners[0].x, corners[0].y);
    for (let i = 1; i < 6; i++) {
      this.hoverGraphics.lineTo(corners[i].x, corners[i].y);
    }
    this.hoverGraphics.closePath();
    this.hoverGraphics.strokePath();

    // Bright border
    this.hoverGraphics.lineStyle(1.5, 0x00ddff, 0.4);
    this.hoverGraphics.beginPath();
    this.hoverGraphics.moveTo(corners[0].x, corners[0].y);
    for (let i = 1; i < 6; i++) {
      this.hoverGraphics.lineTo(corners[i].x, corners[i].y);
    }
    this.hoverGraphics.closePath();
    this.hoverGraphics.strokePath();

  }

  private updateViewport(): void {
    const camera = this.cameras.main;

    // Calculate viewport bounds in world coordinates
    const left = camera.scrollX - VIEWPORT_PADDING * HEX_WIDTH;
    const right = camera.scrollX + camera.width / camera.zoom + VIEWPORT_PADDING * HEX_WIDTH;
    const top = camera.scrollY - VIEWPORT_PADDING * HEX_HEIGHT * ISO_Y_SCALE;
    const bottom = camera.scrollY + camera.height / camera.zoom + VIEWPORT_PADDING * HEX_HEIGHT * ISO_Y_SCALE;

    // Convert to hex coordinates
    const topLeft = pixelToAxial(left, top);
    const bottomRight = pixelToAxial(right, bottom);

    this.viewportBounds = {
      minQ: topLeft.q - 10,
      maxQ: bottomRight.q + 10,
      minR: topLeft.r - 10,
      maxR: bottomRight.r + 10,
    };

    this.lastCameraX = camera.scrollX;
    this.lastCameraY = camera.scrollY;

    // Update culling bounds (pixel-space with margin)
    const margin = 200;
    this.cullingBounds = {
      left: camera.scrollX - margin,
      right: camera.scrollX + camera.width / camera.zoom + margin,
      top: camera.scrollY - margin,
      bottom: camera.scrollY + camera.height / camera.zoom + margin,
    };
  }

  private debounceChunkRequests(): void {
    if (this.chunkRequestTimer) {
      clearTimeout(this.chunkRequestTimer);
    }

    this.chunkRequestTimer = setTimeout(() => {
      this.requestVisibleChunks();
    }, CHUNK_REQUEST_DEBOUNCE);
  }

  private requestVisibleChunks(): void {
    // Calculate which chunks are visible
    const { minQ, maxQ, minR, maxR } = this.viewportBounds;

    const minChunkX = Math.floor(minQ / CHUNK_SIZE);
    const maxChunkX = Math.floor(maxQ / CHUNK_SIZE);
    const minChunkY = Math.floor(minR / CHUNK_SIZE);
    const maxChunkY = Math.floor(maxR / CHUNK_SIZE);

    // Collect unloaded chunks
    const needed: Array<{ chunkX: number; chunkY: number }> = [];
    for (let cx = minChunkX; cx <= maxChunkX; cx++) {
      for (let cy = minChunkY; cy <= maxChunkY; cy++) {
        const chunkKey = `${cx},${cy}`;
        if (!this.loadedChunks.has(chunkKey)) {
          needed.push({ chunkX: cx, chunkY: cy });
          this.loadedChunks.add(chunkKey); // Mark as pending to avoid duplicate requests
        }
      }
    }

    if (needed.length > 0) {
      console.log(`[MMOGame] Requesting ${needed.length} unloaded chunks`);
      this.mmoNetwork.sendRequestChunks(needed);
    }
  }

  private renderVisibleHexes(): void {
    this.hexGraphics.clear();

    const { minQ, maxQ, minR, maxR } = this.viewportBounds;

    const drawnEdges = new Set<string>();
    const edges: { x1: number; y1: number; x2: number; y2: number }[] = [];

    for (let q = minQ; q <= maxQ; q++) {
      for (let r = minR; r <= maxR; r++) {
        if (!this.mmoState.isHexExplored(`${q},${r}`)) continue;

        const pixel = axialToPixel(q, r);
        const corners = this.getHexCorners(pixel.x, pixel.y);

        for (let i = 0; i < 6; i++) {
          const a = corners[i];
          const b = corners[(i + 1) % 6];
          const ax = Math.round(a.x); const ay = Math.round(a.y);
          const bx = Math.round(b.x); const by = Math.round(b.y);

          const edgeKey = ax < bx || (ax === bx && ay < by)
            ? `${ax},${ay}-${bx},${by}`
            : `${bx},${by}-${ax},${ay}`;

          if (!drawnEdges.has(edgeKey)) {
            drawnEdges.add(edgeKey);
            edges.push({ x1: a.x, y1: a.y, x2: b.x, y2: b.y });
          }
        }
      }
    }

    // Glow layer
    if (edges.length > 0) {
      this.hexGraphics.lineStyle(3.5, 0x0e5c7a, 0.10);
      this.hexGraphics.beginPath();
      for (const e of edges) {
        this.hexGraphics.moveTo(e.x1, e.y1);
        this.hexGraphics.lineTo(e.x2, e.y2);
      }
      this.hexGraphics.strokePath();
    }

    // Core edge layer
    if (edges.length > 0) {
      this.hexGraphics.lineStyle(1.0, 0x00ccff, 0.18);
      this.hexGraphics.beginPath();
      for (const e of edges) {
        this.hexGraphics.moveTo(e.x1, e.y1);
        this.hexGraphics.lineTo(e.x2, e.y2);
      }
      this.hexGraphics.strokePath();
    }
  }

  private renderFogOfWar(): void {
    this.fogGraphics.clear();

    const { minQ, maxQ, minR, maxR } = this.viewportBounds;

    for (let q = minQ; q <= maxQ; q++) {
      for (let r = minR; r <= maxR; r++) {
        const hexKey = `${q},${r}`;

        if (!this.mmoState.isHexExplored(hexKey)) {
          // Check adjacency for soft fog edges
          const neighbors = [
            {q: q+1, r: r}, {q: q-1, r: r},
            {q: q, r: r+1}, {q: q, r: r-1},
            {q: q+1, r: r-1}, {q: q-1, r: r+1}
          ];
          const adjacentToVisible = neighbors.some(n =>
            this.mmoState.isHexVisible(`${n.q},${n.r}`)
          );
          const adjacentToExplored = neighbors.some(n =>
            this.mmoState.isHexExplored(`${n.q},${n.r}`)
          );

          if (adjacentToVisible) {
            this.drawFog({ q, r }, 0x080818, 0.4);
          } else if (adjacentToExplored) {
            this.drawFog({ q, r }, 0x080818, 0.6);
          } else {
            this.drawFog({ q, r }, 0x080818, 0.75);
          }
        } else if (!this.mmoState.isHexVisible(hexKey)) {
          this.drawFog({ q, r }, 0x1a1a2e, 0.5);
        }
        // No fog if visible
      }
    }
  }

  /**
   * Draw stack counters for hexes with multiple units
   */
  private stackCounterTexts: Phaser.GameObjects.Text[] = [];

  private updateStackCounts(): void {
    this.stackCounterGraphics.clear();

    // Destroy previous counter texts
    for (const txt of this.stackCounterTexts) {
      txt.destroy();
    }
    this.stackCounterTexts.length = 0;

    const { minQ, maxQ, minR, maxR } = this.viewportBounds;

    // Count units per hex (exclude docked units — they're invisible)
    const unitCounts = new Map<string, number>();
    this.unitSprites.forEach((unit) => {
      if (unit.isDocked) return;
      const hexKey = `${unit.hexCoord.q},${unit.hexCoord.r}`;
      unitCounts.set(hexKey, (unitCounts.get(hexKey) || 0) + 1);
    });

    // Draw counters for stacked units
    unitCounts.forEach((count, hexKey) => {
      if (count > 1) {
        const [qStr, rStr] = hexKey.split(',');
        const q = parseInt(qStr);
        const r = parseInt(rStr);

        // Only draw if in viewport and visible
        if (q >= minQ && q <= maxQ && r >= minR && r <= maxR && this.mmoState.isHexVisible(hexKey)) {
          const pixel = axialToPixel(q, r);

          // Draw counter in upper right corner of hex
          const counterX = pixel.x + 20;
          const counterY = pixel.y - 25;

          // Background circle
          this.stackCounterGraphics.fillStyle(0x000000, 0.7);
          this.stackCounterGraphics.fillCircle(counterX, counterY, 10);

          // Border
          this.stackCounterGraphics.lineStyle(2, 0xffffff, 0.9);
          this.stackCounterGraphics.strokeCircle(counterX, counterY, 10);

          const counterText = this.add.text(counterX, counterY, count.toString(), {
            fontSize: '12px',
            color: '#ffffff',
            fontFamily: 'Arial',
            fontStyle: 'bold',
          });
          counterText.setOrigin(0.5);
          counterText.setDepth(16);
          this.stackCounterTexts.push(counterText);
        }
      }
    });
  }

  private drawFog(hex: HexCoord, color: number, alpha: number): void {
    const pixel = axialToPixel(hex.q, hex.r);
    const corners = this.getHexCorners(pixel.x, pixel.y);

    this.fogGraphics.fillStyle(color, alpha);
    this.fogGraphics.lineStyle(0, 0x000000, 0);

    this.fogGraphics.beginPath();
    this.fogGraphics.moveTo(corners[0].x, corners[0].y);
    for (let i = 1; i < 6; i++) {
      this.fogGraphics.lineTo(corners[i].x, corners[i].y);
    }
    this.fogGraphics.closePath();
    this.fogGraphics.fillPath();
  }

  /**
   * Handle territory update from server (periodic broadcast)
   */
  private onTerritoryUpdate(payload: any): void {
    if (payload.territories && Array.isArray(payload.territories)) {
      this.mmoState.setTerritories(payload.territories);
      this.renderTerritoryBorders();
    }
  }

  // Default colors for alliances without a custom color
  private static TERRITORY_COLORS: number[] = [
    0xff4444, // red
    0x4488ff, // blue
    0xffaa44, // orange
    0xaa44ff, // purple
    0x44ff88, // green
    0x44ffff, // cyan
    0xff44aa, // pink
    0xaaff44, // lime
  ];

  /**
   * Render alliance territory borders and fills on the hex grid.
   * Territories are drawn BEHIND entities but ABOVE the base hex grid.
   * Only hexes within the current camera viewport are rendered for performance.
   */
  private renderTerritoryBorders(): void {
    this.territoryGraphics.clear();

    const territories = this.mmoState.getTerritories();
    if (territories.length === 0) return;

    const { minQ, maxQ, minR, maxR } = this.viewportBounds;

    for (const territory of territories) {
      const hqHex: HexCoord = { q: territory.hqQ, r: territory.hqR };
      const radius = territory.radius;

      // Determine color: use alliance color if set, otherwise default palette
      let color: number;
      if (territory.color) {
        // Parse hex color string (e.g. "#4488ff")
        color = parseInt(territory.color.replace('#', ''), 16);
      } else {
        color = MMOGameScene.TERRITORY_COLORS[territory.allianceId % MMOGameScene.TERRITORY_COLORS.length];
      }

      // Build the ring (boundary) hex set for fast lookup
      const ringHexes = hexRing(hqHex, radius);
      const ringSet = new Set<string>();
      for (const h of ringHexes) {
        ringSet.add(`${h.q},${h.r}`);
      }

      // Draw territory hexes: light fill for interior, brighter ring for border
      for (let q = minQ; q <= maxQ; q++) {
        for (let r = minR; r <= maxR; r++) {
          const dist = hexDistance({ q, r }, hqHex);
          if (dist > radius) continue;

          const isRing = ringSet.has(`${q},${r}`);
          const pixel = axialToPixel(q, r);
          const corners = this.getHexCorners(pixel.x, pixel.y);

          if (isRing) {
            this.territoryGraphics.fillStyle(color, 0.2);
            this.territoryGraphics.lineStyle(2, color, 0.45);
          } else {
            this.territoryGraphics.fillStyle(color, 0.07);
            this.territoryGraphics.lineStyle(0, 0, 0);
          }

          this.territoryGraphics.beginPath();
          this.territoryGraphics.moveTo(corners[0].x, corners[0].y);
          for (let i = 1; i < 6; i++) {
            this.territoryGraphics.lineTo(corners[i].x, corners[i].y);
          }
          this.territoryGraphics.closePath();
          this.territoryGraphics.fillPath();
          if (isRing) this.territoryGraphics.strokePath();
        }
      }
    }
  }

  private getHexCorners(cx: number, cy: number): { x: number; y: number }[] {
    const corners: { x: number; y: number }[] = [];
    const size = HEX_WIDTH / 2.0;

    for (let i = 0; i < 6; i++) {
      const angle = (Math.PI / 180) * (60 * i);
      corners.push({
        x: cx + size * Math.cos(angle),
        y: cy + size * Math.sin(angle) * ISO_Y_SCALE,
      });
    }

    return corners;
  }

  private renderEntities(): void {
    const mmoState = this.mmoState;
    const playerId = mmoState.getUserId();

    // Track which entities should exist so we can prune sprites for entities
    // that left fog/explored during this render.
    const shouldExistUnits = new Set<string>();
    const shouldExistPlanets = new Set<string>();
    const shouldExistAsteroids = new Set<string>();
    const shouldExistStations = new Set<string>();
    const shouldExistPlatforms = new Set<string>();
    const shouldExistWrecks = new Set<string>();
    const shouldExistAmbient = new Set<string>();
    const shouldExistLootDrops = new Set<string>();

    // Iterate entity Maps directly instead of every explored hex. Number of
    // entities held in client state is bounded by the server's fog broadcast
    // (viewport-sized) — O(E), not O(N_explored). Deep-space players who have
    // explored thousands of hexes don't tank rendering.

    for (const unit of mmoState.getAllUnits()) {
      const hexKey = `${unit.position.q},${unit.position.r}`;
      const isPlayerUnit = unit.ownerId === playerId;
      if (!isPlayerUnit && !mmoState.isHexVisible(hexKey)) continue;
      if (isPlayerUnit && !mmoState.isHexExplored(hexKey)) continue;
      shouldExistUnits.add(unit.id);
      this.renderUnit(unit);
      const sprite = this.unitSprites.get(unit.id);
      if (sprite) sprite.setVisibility(mmoState.isHexVisible(hexKey), true);
    }

    for (const planet of mmoState.getAllPlanets()) {
      const hexKey = `${planet.position.q},${planet.position.r}`;
      if (!mmoState.isHexExplored(hexKey)) continue;
      shouldExistPlanets.add(planet.id);
      this.renderPlanet(planet);
      const sprite = this.planetSprites.get(planet.id);
      if (sprite) sprite.setVisibility(mmoState.isHexVisible(hexKey), true);
    }

    for (const asteroid of mmoState.getAllAsteroids()) {
      const hexKey = `${asteroid.position.q},${asteroid.position.r}`;
      if (!mmoState.isHexExplored(hexKey)) continue;
      shouldExistAsteroids.add(asteroid.id);
      this.renderAsteroid(asteroid);
      const sprite = this.asteroidSprites.get(asteroid.id);
      if (sprite) sprite.setVisibility(mmoState.isHexVisible(hexKey), true);
    }

    for (const station of mmoState.getAllStations()) {
      const hexKey = `${station.position.q},${station.position.r}`;
      if (!mmoState.isHexExplored(hexKey)) continue;
      shouldExistStations.add(station.id);
      this.renderStation(station);
      const sprite = this.stationSprites.get(station.id);
      if (sprite) sprite.setVisibility(mmoState.isHexVisible(hexKey), true);
    }

    for (const wreck of mmoState.getAllWrecks()) {
      const hexKey = `${wreck.position.q},${wreck.position.r}`;
      if (!mmoState.isHexExplored(hexKey)) continue;
      shouldExistWrecks.add(wreck.id);
      this.renderWreck(wreck);
      const sprite = this.wreckSprites.get(wreck.id);
      if (sprite) sprite.setVisibility(mmoState.isHexVisible(hexKey), true);
    }

    for (const ambient of mmoState.getAllAmbientEntities()) {
      const hexKey = `${ambient.position.q},${ambient.position.r}`;
      if (!mmoState.isHexExplored(hexKey)) continue;
      shouldExistAmbient.add(ambient.id);
      this.renderAmbientEntity(ambient);
      const sprite = this.ambientSprites.get(ambient.id);
      if (sprite) sprite.setVisibility(mmoState.isHexVisible(hexKey), true);
    }

    for (const loot of mmoState.getAllLootDrops()) {
      const hexKey = `${loot.position.q},${loot.position.r}`;
      if (!mmoState.isHexExplored(hexKey)) continue;
      shouldExistLootDrops.add(loot.id);
      this.renderLootDrop(loot);
      const sprite = this.lootDropSprites.get(loot.id);
      if (sprite) sprite.setVisibility(mmoState.isHexVisible(hexKey), true);
    }

    // Render orbital platforms (not in the per-hex spatial index — iterate all)
    for (const platform of mmoState.getAllPlatforms()) {
      const hexKey = `${platform.positionQ},${platform.positionR}`;
      if (!mmoState.isHexExplored(hexKey)) continue;
      shouldExistPlatforms.add(platform.id);
      if (this.platformSprites.has(platform.id)) {
        this.platformSprites.get(platform.id)!.updateFromServer(platform);
      } else {
        const sprite = new MMOOrbitalPlatform(this, platform);
        this.platformSprites.set(platform.id, sprite);
        this.add.existing(sprite);
      }
      const sprite = this.platformSprites.get(platform.id);
      if (sprite) sprite.setVisibility(mmoState.isHexVisible(hexKey), true);
    }

    // Safety net: ensure ALL player-owned units have sprites, even if the
    // unit Map iteration missed them above. Prevents ships disappearing if
    // the unit was in state but somehow not in the loop above.
    if (playerId) {
      for (const unit of mmoState.getPlayerUnits()) {
        if (!shouldExistUnits.has(unit.id)) {
          const hexKey = `${unit.position.q},${unit.position.r}`;
          if (mmoState.isHexExplored(hexKey)) {
            shouldExistUnits.add(unit.id);
            this.renderUnit(unit);
            const sprite = this.unitSprites.get(unit.id);
            if (sprite) sprite.setVisibility(mmoState.isHexVisible(hexKey), true);
          }
        }
      }
    }

    // Remove entities that are no longer visible/explored
    this.unitSprites.forEach((unit, id) => {
      if (!shouldExistUnits.has(id)) {
        unit.destroy();
        this.unitSprites.delete(id);
      }
    });

    this.planetSprites.forEach((planet, id) => {
      if (!shouldExistPlanets.has(id)) {
        planet.destroy();
        this.planetSprites.delete(id);
      }
    });

    this.asteroidSprites.forEach((asteroid, id) => {
      if (!shouldExistAsteroids.has(id)) {
        asteroid.destroy();
        this.asteroidSprites.delete(id);
      }
    });

    this.stationSprites.forEach((station, id) => {
      if (!shouldExistStations.has(id)) {
        station.destroy();
        this.stationSprites.delete(id);
      }
    });

    this.platformSprites.forEach((platform, id) => {
      if (!shouldExistPlatforms.has(id)) {
        platform.destroy();
        this.platformSprites.delete(id);
      }
    });

    this.wreckSprites.forEach((wreck, id) => {
      if (!shouldExistWrecks.has(id)) {
        wreck.destroy();
        this.wreckSprites.delete(id);
      }
    });

    this.ambientSprites.forEach((ambient, id) => {
      if (!shouldExistAmbient.has(id)) {
        ambient.destroy();
        this.ambientSprites.delete(id);
      }
    });

    this.lootDropSprites.forEach((loot, id) => {
      if (!shouldExistLootDrops.has(id)) {
        loot.destroy();
        this.lootDropSprites.delete(id);
      }
    });

  }

  private removeUnitFromClient(unitId: string): void {
    this.mmoState.removeUnit(unitId);

    const sprite = this.unitSprites.get(unitId);
    if (sprite) {
      try { sprite.destroy(); } catch (_) { /* already destroyed */ }
      this.unitSprites.delete(unitId);
    }

    if (this.selectedEntity && this.selectedEntity.id === unitId) {
      this.selectedEntity = null;
      EventBus.emit('mmo-entity-deselected');
    }

    if (this.cameraFollowTarget && this.cameraFollowTarget.id === unitId) {
      this.cameras.main.panEffect.reset();
      this.cameraFollowTarget = null;
    }

    this.clearMovementMarker(unitId);
  }

  private renderUnit(unit: any): void {
    // Check if unit sprite already exists
    if (this.unitSprites.has(unit.id)) {
      const existingUnit = this.unitSprites.get(unit.id)!;
      existingUnit.updateFromServer(unit);
      this.updateStackCounts();
      return;
    }

    const unitEntity = new MMOUnit(this, unit);

    // Set visibility based on fog of war
    const hexKey = `${unit.position.q},${unit.position.r}`;
    const isVisible = this.mmoState.isHexVisible(hexKey);
    const isExplored = this.mmoState.isHexExplored(hexKey);
    unitEntity.setVisibility(isVisible, isExplored);

    // Entity will emit 'mmo-entity-selected' event when clicked

    // Add directly to scene (not container) for proper input handling
    // Note: Container children don't receive input properly in Phaser 3
    this.add.existing(unitEntity);
    this.unitSprites.set(unit.id, unitEntity);
  }

  private renderPlanet(planet: any): void {
    // Check if planet sprite already exists
    if (this.planetSprites.has(planet.id)) {
      const existingPlanet = this.planetSprites.get(planet.id)!;
      existingPlanet.updateFromServer(planet);
      return;
    }

    // Create new MMOPlanet instance
    const planetEntity = new MMOPlanet(this, planet);

    // Set visibility based on fog of war
    const hexKey = `${planet.position.q},${planet.position.r}`;
    const isVisible = this.mmoState.isHexVisible(hexKey);
    const isExplored = this.mmoState.isHexExplored(hexKey);
    planetEntity.setVisibility(isVisible, isExplored);

    // Entity will emit 'mmo-entity-selected' event when clicked

    // Add directly to scene (not container) for proper input handling
    // Note: Container children don't receive input properly in Phaser 3
    this.add.existing(planetEntity);
    this.planetSprites.set(planet.id, planetEntity);
  }

  private renderAsteroid(asteroid: any): void {
    // Check if asteroid sprite already exists
    if (this.asteroidSprites.has(asteroid.id)) {
      const existingAsteroid = this.asteroidSprites.get(asteroid.id)!;
      existingAsteroid.updateFromServer(asteroid);
      return;
    }

    // Create new MMOAsteroid instance
    const asteroidEntity = new MMOAsteroid(this, asteroid);

    // Set visibility based on fog of war
    const hexKey = `${asteroid.position.q},${asteroid.position.r}`;
    const isVisible = this.mmoState.isHexVisible(hexKey);
    const isExplored = this.mmoState.isHexExplored(hexKey);
    asteroidEntity.setVisibility(isVisible, isExplored);

    // Add directly to scene (not container) for proper input handling
    // Note: Container children don't receive input properly in Phaser 3
    this.add.existing(asteroidEntity);
    this.asteroidSprites.set(asteroid.id, asteroidEntity);
  }

  private renderStation(station: any): void {
    if (this.stationSprites.has(station.id)) {
      const existingStation = this.stationSprites.get(station.id)!;
      existingStation.updateFromServer(station);
      return;
    }

    const stationEntity = new MMOStation(this, station);

    const hexKey = `${station.position.q},${station.position.r}`;
    const isVisible = this.mmoState.isHexVisible(hexKey);
    const isExplored = this.mmoState.isHexExplored(hexKey);
    stationEntity.setVisibility(isVisible, isExplored);

    this.add.existing(stationEntity);
    this.stationSprites.set(station.id, stationEntity);
  }

  private renderWreck(wreck: any): void {
    if (this.wreckSprites.has(wreck.id)) {
      const existingWreck = this.wreckSprites.get(wreck.id)!;
      existingWreck.updateFromServer(wreck);
      return;
    }

    const wreckEntity = new MMOWreck(this, wreck);

    const hexKey = `${wreck.position.q},${wreck.position.r}`;
    const isVisible = this.mmoState.isHexVisible(hexKey);
    const isExplored = this.mmoState.isHexExplored(hexKey);
    wreckEntity.setVisibility(isVisible, isExplored);

    this.add.existing(wreckEntity);
    this.wreckSprites.set(wreck.id, wreckEntity);
  }

  private renderAmbientEntity(ae: any): void {
    if (this.ambientSprites.has(ae.id)) {
      const existing = this.ambientSprites.get(ae.id)!;
      existing.updateFromServer(ae);
      return;
    }

    const aeEntity = new MMOAmbientEntity(this, ae);

    const hexKey = `${ae.position.q},${ae.position.r}`;
    const isVisible = this.mmoState.isHexVisible(hexKey);
    const isExplored = this.mmoState.isHexExplored(hexKey);
    aeEntity.setVisibility(isVisible, isExplored);

    this.add.existing(aeEntity);
    this.ambientSprites.set(ae.id, aeEntity);
  }

  private renderDroppedRelic(relicData: DroppedRelicData): void {
    if (this.droppedRelicSprites.has(relicData.id)) {
      const existing = this.droppedRelicSprites.get(relicData.id)!;
      existing.updateFromServer(relicData);
      return;
    }

    const relicEntity = new MMODroppedRelic(this, relicData);

    const hexKey = `${relicData.position.q},${relicData.position.r}`;
    const isVisible = this.mmoState.isHexVisible(hexKey);
    const isExplored = this.mmoState.isHexExplored(hexKey);
    relicEntity.setVisibility(isVisible, isExplored);

    this.add.existing(relicEntity);
    this.droppedRelicSprites.set(relicData.id, relicEntity);
  }

  private renderLootDrop(lootData: any): void {
    if (this.lootDropSprites.has(lootData.id)) {
      const existing = this.lootDropSprites.get(lootData.id)!;
      existing.updateFromServer(lootData);
      return;
    }

    const lootEntity = new MMOLootDrop(this, lootData);

    const hexKey = `${lootData.position.q},${lootData.position.r}`;
    const isVisible = this.mmoState.isHexVisible(hexKey);
    const isExplored = this.mmoState.isHexExplored(hexKey);
    lootEntity.setVisibility(isVisible, isExplored);

    this.add.existing(lootEntity);
    this.lootDropSprites.set(lootData.id, lootEntity);
  }

  /**
   * Handle entity selection event from entities
   */
  private handleEntitySelection(data: { entity: MMOUnit | MMOPlanet | MMOAsteroid | MMOStation | MMOOrbitalPlatform | MMOWreck | MMOAmbientEntity | MMODroppedRelic | MMOLootDrop }): void {
    let entity = data.entity;

    // If in movement/attack mode, convert the click to the actual hex position
    // (not the entity center, which can be wrong for multi-hex planets like Earth)
    if ((this.isMovementMode || this.isAttackMode || this.isMineMode) && 'hexCoord' in entity) {
      const pointer = this.input.activePointer;
      const worldPoint = this.cameras.main.getWorldPoint(pointer.x, pointer.y);
      const clickedHex = pixelToAxial(worldPoint.x, worldPoint.y);
      const hexKey = `${clickedHex.q},${clickedHex.r}`;

      if (this.isMovementMode) {
        this.handleMovementDestination(clickedHex, hexKey);
      } else if (this.isAttackMode) {
        this.handleAttackTarget(clickedHex, hexKey);
      } else if (this.isMineMode) {
        this.handleMineTarget(clickedHex, hexKey);
      }
      return;
    }

    // Check if there are multiple entities at this position
    if ('hexCoord' in entity) {
      const entitiesAtHex = this.mmoState.getEntitiesAt(entity.hexCoord);

      if (entitiesAtHex.length > 1) {
        const userId = this.mmoState.getUserId();
        const clickedOwnUnit = entity instanceof MMOUnit
          && entity.ownerId === userId
          && !entity.dockedAtPlanetId;

        if (!clickedOwnUnit) {
          // Priority: stations/planets > undocked player units > everything else.
          // If the player clicked directly on their own ship sprite, keep that
          // selection so crowded Earth/station stacks do not hijack the click.
          const stationHere = entitiesAtHex.find((e) => 'stationType' in e);
          const planetHere = entitiesAtHex.find((e) => 'buildings' in e);

          if (stationHere) {
            const stationSprite = this.stationSprites?.get(stationHere.id);
            if (stationSprite) entity = stationSprite;
          } else if (planetHere) {
            const planetSprite = this.planetSprites?.get(planetHere.id);
            if (planetSprite) entity = planetSprite;
          } else {
            const unitsAtHex = entitiesAtHex.filter((e) => 'attackRange' in e);
            const playerUnits = unitsAtHex.filter((u) => u.ownerId === userId);
            if (playerUnits.length > 0) {
              const playerUnit = this.unitSprites.get(playerUnits[0].id);
              if (playerUnit) entity = playerUnit;
            }
          }
        }
      }
    }

    // Only deselect if selecting a different entity
    if (this.selectedEntity && this.selectedEntity.id !== entity.id) {
      this.selectedEntity.setSelected(false);
    }

    // Select new entity
    this.selectedEntity = entity;
    entity.setSelected(true);

    // Camera follow only engages when the unit moves, not on selection.
    // Stop any active follow when switching selection targets.
    this.disengageCameraFollow();

    // Emit resolved selection so sidebar gets the correct (possibly swapped) entity
    EventBus.emit('mmo-entity-selection-resolved', { entity });
  }

  /**
   * Handle entity deselection event (e.g. from sidebar close button)
   */
  private handleEntityDeselection(): void {
    if (this.selectedEntity) {
      this.selectedEntity.setSelected(false);
      this.selectedEntity = null;
    }
    if (this.isMineMode) this.cancelMineMode();
    if (this.isAttackMode) this.cancelAttackMode();
    if (this.isMovementMode) this.cancelMovementMode();
    this.disengageCameraFollow();
  }

  // ===== ACTION MODE METHODS =====
  // Movement and attack mode implementations

  /**
   * Enter movement mode for a unit
   */
  private enterMovementMode(unit: MMOUnit): void {
    // If already in movement mode, toggle it off instead of re-entering
    // (prevents the 100ms click guard from expiring and auto-selecting a destination)
    if (this.isMovementMode) {
      this.cancelMovementMode();
      return;
    }

    if (this.isAttackMode) this.cancelAttackMode();
    if (this.isMineMode) this.cancelMineMode();

    this.isMovementMode = true;
    this.movementModeActivatedAt = Date.now();
    this.movementUnit = unit;

    // Build blocked hex set from planets, stations, and asteroids for hover/click validation
    this.blockedHexKeys = this.buildClientBlockedHexes();

    // Crosshair cursor signals "click anywhere to move"
    this.input.setDefaultCursor('crosshair');

    // Turn the selection ring red to match the MOVE button accent
    unit.setSelectionRingColor(0xff4444);

    EventBus.emit('mmo-movement-mode-started', { unit });
  }

  private buildClientBlockedHexes(): Set<string> {
    const blocked = new Set<string>();
    for (const p of this.mmoState.getAllPlanets()) {
      const size = getPlanetFootprint(p.isHomePlanet);
      for (const h of getFootprintHexes(p.position, size)) blocked.add(toHexKey(h));
    }
    for (const s of this.mmoState.getAllStations()) {
      const size = (STATION_FOOTPRINT[s.stationType] || 1) as FootprintSize;
      for (const h of getFootprintHexes(s.position, size)) blocked.add(toHexKey(h));
    }
    // Note: asteroids are NOT blocked — units can move to asteroid hexes to mine them.
    return blocked;
  }

  /**
   * Cancel movement mode
   */
  private cancelMovementMode(): void {
    if (!this.isMovementMode) return;

    // Restore the selection ring to default green before clearing the reference
    if (this.movementUnit) {
      this.movementUnit.setSelectionRingColor();
    }

    this.isMovementMode = false;
    this.movementUnit = null;

    // Clear hover highlight and reset cursor
    this.movementOverlay.clear();
    this.input.setDefaultCursor('default');

    EventBus.emit('mmo-movement-mode-cancelled');
  }

  // ===== RELOCATION MODE METHODS =====

  private enterStationRelocationMode({ stationId }: { stationId: string }): void {
    if (this.isMovementMode) this.cancelMovementMode();
    if (this.isAttackMode) this.cancelAttackMode();
    if (this.isMineMode) this.cancelMineMode();

    this.isRelocationMode = true;
    this.relocationModeActivatedAt = Date.now();
    this.relocatingEntityId = stationId;
    this.relocatingEntityType = 'station';

    // Build blocked hexes but exclude this station's own footprint so it can stay in place
    this.blockedHexKeys = this.buildClientBlockedHexes();
    const stationSprite = this.stationSprites.get(stationId);
    if (stationSprite) {
      const stationType = (stationSprite as any).stationType || 'waypoint';
      const size = (STATION_FOOTPRINT[stationType] || 1) as FootprintSize;
      for (const h of getFootprintHexes(stationSprite.hexCoord, size)) {
        this.blockedHexKeys.delete(toHexKey(h));
      }
    }

    this.input.setDefaultCursor('crosshair');
    this.showNotification('Click a hex to relocate your station (ESC to cancel)');
  }

  private enterPlatformRelocationMode({ platformId }: { platformId: string }): void {
    if (this.isMovementMode) this.cancelMovementMode();
    if (this.isAttackMode) this.cancelAttackMode();
    if (this.isMineMode) this.cancelMineMode();

    this.isRelocationMode = true;
    this.relocationModeActivatedAt = Date.now();
    this.relocatingEntityId = platformId;
    this.relocatingEntityType = 'platform';

    // Build blocked hexes but exclude this platform's own hex
    this.blockedHexKeys = this.buildClientBlockedHexes();
    const platformSprite = this.platformSprites.get(platformId);
    if (platformSprite) {
      this.blockedHexKeys.delete(toHexKey(platformSprite.hexCoord));
    }

    this.input.setDefaultCursor('crosshair');
    this.showNotification('Click a hex to relocate your platform (ESC to cancel)');
  }

  private cancelRelocationMode(): void {
    if (!this.isRelocationMode) return;
    this.isRelocationMode = false;
    this.relocatingEntityId = null;
    this.relocatingEntityType = null;
    this.movementOverlay.clear();
    this.input.setDefaultCursor('default');
    EventBus.emit('relocation-mode-cancelled');
  }

  private async handleRelocationTarget(hex: HexCoord): Promise<void> {
    if (this.blockedHexKeys.has(toHexKey(hex))) {
      this.showNotification('Cannot relocate there — hex is occupied');
      return;
    }

    const entityId = this.relocatingEntityId;
    const entityType = this.relocatingEntityType;
    if (!entityId || !entityType) return;

    // Cancel mode immediately so UI resets
    this.cancelRelocationMode();

    const monetization = ServiceLocator.get<any>(Services.MMO_MONETIZATION);
    if (!monetization) {
      this.showNotification('Relocation service unavailable');
      return;
    }

    try {
      let result: { success: boolean; error?: string };
      if (entityType === 'station') {
        result = await monetization.relocateStation(entityId, hex.q, hex.r);
      } else {
        result = await monetization.relocatePlatform(entityId, hex.q, hex.r);
      }

      if (result.success) {
        const label = entityType === 'station' ? 'Station' : 'Platform';
        this.showNotification(`${label} relocated to (${hex.q}, ${hex.r})`);
      } else {
        this.showNotification(result.error || 'Relocation failed');
      }
    } catch (err) {
      this.showNotification('Relocation failed — server error');
    }
  }

  // ===== STRUCTURE DEPLOY MODE METHODS =====

  private enterStructureDeployMode({ structureType }: { structureType: string }): void {
    if (this.isMovementMode) this.cancelMovementMode();
    if (this.isAttackMode) this.cancelAttackMode();
    if (this.isMineMode) this.cancelMineMode();
    if (this.isRelocationMode) this.cancelRelocationMode();

    this.isStructureDeployMode = true;
    this.structureDeployType = structureType;
    this.structureDeployActivatedAt = Date.now();

    // Build blocked hexes so player can see valid placement
    this.blockedHexKeys = this.buildClientBlockedHexes();

    this.input.setDefaultCursor('crosshair');
    const label = structureType.replace(/_/g, ' ');
    this.showNotification(`Click a hex to deploy ${label} (ESC to cancel)`);
  }

  private cancelStructureDeployMode(): void {
    if (!this.isStructureDeployMode) return;
    this.isStructureDeployMode = false;
    this.structureDeployType = null;
    this.movementOverlay.clear();
    this.input.setDefaultCursor('default');
  }

  private handleStructureDeployTarget(hex: HexCoord): void {
    if (this.blockedHexKeys.has(toHexKey(hex))) {
      this.showNotification('Cannot deploy there — hex is occupied');
      return;
    }

    const structureType = this.structureDeployType;
    if (!structureType) return;

    this.mmoNetwork.sendDeployStructure(structureType, hex.q, hex.r);
    this.cancelStructureDeployMode();
  }

  private onDeployStructureResult(payload: { success: boolean; error?: string; type?: string; q?: number; r?: number }): void {
    if (payload.success) {
      const label = (payload.type || 'structure').replace(/_/g, ' ');
      this.showNotification(`Deployed ${label} at (${payload.q}, ${payload.r})`, '#00FF88');
    } else {
      this.showNotification(payload.error || 'Structure deployment failed', '#FF4444');
    }
  }

  private onDesignateHQResult(payload: { success: boolean; error?: string }): void {
    if (payload.success) {
      this.showNotification('Station designated as alliance HQ', '#00FF88');
    } else {
      this.showNotification(payload.error || 'Failed to designate HQ', '#FF4444');
    }
  }

  private onSetTaxRateResult(payload: { success: boolean; error?: string; rate?: number }): void {
    if (payload.success) {
      this.showNotification(`Alliance tax rate set to ${payload.rate ?? 0}%`, '#00FF88');
    } else {
      this.showNotification(payload.error || 'Failed to set tax rate', '#FF4444');
    }
  }

  // ===== ZONE INTRUSION WARNING (Task 3) =====

  /**
   * Check if a movement destination is inside hostile alliance territory.
   * Shows an advisory warning (does not block movement).
   */
  private checkTerritoryIntrusionWarning(dest: HexCoord): void {
    const territories = this.mmoState.getTerritories();
    if (territories.length === 0) return;

    const playerAlliance = this.mmoState.getAlliance();
    const playerAllianceId = playerAlliance ? playerAlliance.id : null;

    for (const territory of territories) {
      // Skip own alliance territory
      if (playerAllianceId !== null && territory.allianceId === playerAllianceId) continue;

      const hqHex: HexCoord = { q: territory.hqQ, r: territory.hqR };
      const dist = hexDistance(hqHex, dest);
      if (dist <= territory.radius) {
        this.showNotification(`Warning: Entering ${territory.name} territory`, '#FFCC00', 3000);
        return; // Only show one warning
      }
    }
  }

  private onUnitDestroyedNotification(payload: {
    unitId?: string; unitType?: string; cause?: string;
    message?: string; upgradeHint?: string;
    killerName?: string; killerUnitType?: string;
    killingBlow?: { weaponName?: string; damage?: number };
    combatSummary?: { totalDamageReceived?: number; engagementDuration?: number; yourShieldsWereDown?: boolean };
  }): void {
    if (!payload) return;

    // Check if player has any remaining ships
    const remainingShips = this.mmoState.getPlayerUnits().filter(u => u.id !== payload.unitId);

    if (remainingShips.length > 0) {
      // Still have ships — just show a notification
      const deathMsg = payload.message || `Your ${payload.unitType || 'ship'} was destroyed`;
      this.showNotification(deathMsg, '#ff4444', 7000);
      if (payload.upgradeHint) {
        this.time.delayedCall(1500, () => {
          this.showNotification(payload.upgradeHint!, '#ffcc00', 8000);
        });
      }
      return;
    }

    // Last ship destroyed — show full death screen
    this.showDeathScreen(payload);
  }

  private showDeathScreen(payload: {
    unitType?: string; killerName?: string; killerUnitType?: string;
    killingBlow?: { weaponName?: string; damage?: number };
    combatSummary?: { totalDamageReceived?: number; engagementDuration?: number; yourShieldsWereDown?: boolean };
    upgradeHint?: string;
  }): void {
    // Create full-screen DOM overlay
    const overlay = document.createElement('div');
    overlay.id = 'death-screen';
    overlay.style.cssText = [
      'position:fixed', 'inset:0', 'z-index:99999',
      'background:rgba(0,0,0,0.92)',
      'display:flex', 'align-items:center', 'justify-content:center',
      'animation:fadeIn 0.5s ease-out',
    ].join(';');

    // Add fade-in animation
    const style = document.createElement('style');
    style.textContent = '@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } } @keyframes pulseGlow { 0%,100% { text-shadow: 0 0 20px rgba(255,0,0,0.5); } 50% { text-shadow: 0 0 40px rgba(255,0,0,0.8), 0 0 80px rgba(255,0,0,0.3); } }';
    overlay.appendChild(style);

    const panel = document.createElement('div');
    panel.style.cssText = [
      'text-align:center', 'max-width:500px', 'padding:40px',
      'font-family:"Share Tech Mono",monospace',
    ].join(';');

    // "SHIP DESTROYED" title
    const title = document.createElement('div');
    title.textContent = 'SHIP DESTROYED';
    title.style.cssText = 'font-family:"Orbitron",sans-serif;font-size:32px;font-weight:900;color:#ff2222;letter-spacing:6px;margin-bottom:24px;animation:pulseGlow 2s ease-in-out infinite;';
    panel.appendChild(title);

    // Ship type
    const shipLine = document.createElement('div');
    shipLine.textContent = `Your ${payload.unitType || 'ship'} has been lost.`;
    shipLine.style.cssText = 'font-size:16px;color:#cc8888;margin-bottom:20px;';
    panel.appendChild(shipLine);

    // Kill details
    if (payload.killerName || payload.killerUnitType) {
      const killerLine = document.createElement('div');
      killerLine.style.cssText = 'font-size:13px;color:#888;margin-bottom:6px;';
      killerLine.textContent = `Destroyed by: ${payload.killerName || payload.killerUnitType || 'Unknown'}`;
      panel.appendChild(killerLine);
    }

    if (payload.killingBlow?.weaponName) {
      const weaponLine = document.createElement('div');
      weaponLine.style.cssText = 'font-size:13px;color:#888;margin-bottom:6px;';
      weaponLine.textContent = `Killing blow: ${payload.killingBlow.weaponName} (${payload.killingBlow.damage || 0} damage)`;
      panel.appendChild(weaponLine);
    }

    if (payload.combatSummary?.totalDamageReceived) {
      const dmgLine = document.createElement('div');
      dmgLine.style.cssText = 'font-size:13px;color:#888;margin-bottom:16px;';
      dmgLine.textContent = `Total damage received: ${payload.combatSummary.totalDamageReceived}`;
      panel.appendChild(dmgLine);
    }

    // Divider
    const divider = document.createElement('div');
    divider.style.cssText = 'width:200px;height:1px;background:linear-gradient(90deg,transparent,#ff4444,transparent);margin:20px auto;';
    panel.appendChild(divider);

    // Respawn message
    const respawnMsg = document.createElement('div');
    respawnMsg.textContent = 'You have been given a new Sparrow MK1. Good luck!';
    respawnMsg.style.cssText = 'font-size:15px;color:#44ffaa;margin-bottom:8px;';
    panel.appendChild(respawnMsg);

    // Upgrade hint
    if (payload.upgradeHint) {
      const hint = document.createElement('div');
      hint.textContent = payload.upgradeHint;
      hint.style.cssText = 'font-size:12px;color:#ffcc44;margin-bottom:24px;font-style:italic;';
      panel.appendChild(hint);
    }

    // Continue button
    const btn = document.createElement('button');
    btn.textContent = 'CONTINUE';
    btn.style.cssText = [
      'font-family:"Orbitron",sans-serif', 'font-weight:700',
      'font-size:16px', 'letter-spacing:3px',
      'padding:14px 48px', 'cursor:pointer',
      'background:linear-gradient(180deg,#224422 0%,#112211 100%)',
      'border:2px solid #44ff66', 'color:#44ff66',
      'transition:all 0.2s', 'margin-top:16px',
    ].join(';');
    btn.onmouseover = () => { btn.style.background = 'linear-gradient(180deg,#336633 0%,#224422 100%)'; btn.style.boxShadow = '0 0 20px rgba(68,255,102,0.3)'; };
    btn.onmouseout = () => { btn.style.background = 'linear-gradient(180deg,#224422 0%,#112211 100%)'; btn.style.boxShadow = 'none'; };
    btn.onclick = () => {
      overlay.remove();
      // Rejoin world to pick up the respawned scout
      EventBus.emit('send_message', { type: 'mmo_join_world', payload: { worldId: 1 } });
    };
    panel.appendChild(btn);

    overlay.appendChild(panel);
    document.body.appendChild(overlay);
  }

  private onServerError(payload: { message?: string; code?: string; unitId?: string; targetId?: string }): void {
    if (!payload?.message) return;

    if (payload.code === 'UNIT_NOT_FOUND' && payload.message.includes('Target unit')) {
      if (payload.targetId) {
        this.removeUnitFromClient(payload.targetId);
      }
      this.showNotification('That target is gone — choose another visible enemy ship', '#ff8844', 3500);
      return;
    }

    if (payload.code === 'CANNOT_ATTACK_DOCKED') {
      this.showNotification('That ship is docked and protected. Pick an undocked target.', '#ff8844', 3500);
      return;
    }

    // Handle required component error from special asteroids — show modal popup
    if (payload.message.startsWith('MISSING_REQUIRED_COMPONENT:')) {
      const parts = payload.message.split(':');
      const componentId = parts[1];
      const componentName = parts[2] || 'Unknown Component';
      const componentCategory = parts[3] || '';
      this.showRequiredComponentPopup(componentName, componentId, componentCategory, payload.unitId);
      return;
    }

    if (
      payload.code === 'REPAIR_FAILED' &&
      payload.unitId &&
      (payload.message.includes('No iron available') || payload.message.includes('Not enough iron'))
    ) {
      void this.showShipRepairPurchasePrompt(payload.unitId, payload.message);
      return;
    }

    this.showNotification(payload.message);
  }

  private async showShipRepairPurchasePrompt(unitId: string, fallbackMessage: string): Promise<void> {
    const monetization = ServiceLocator.get<any>(Services.MMO_MONETIZATION);
    if (!monetization) {
      this.showNotification(fallbackMessage);
      return;
    }

    let quote: any;
    try {
      quote = await monetization.getShipRepairQuote(unitId);
    } catch {
      this.showNotification(fallbackMessage);
      return;
    }

    if (!quote?.success) {
      this.showNotification(quote?.error || fallbackMessage);
      return;
    }

    if (quote.missingIron <= 0) {
      this.showNotification('You have enough iron at this dock. Try repair again.');
      return;
    }

    this.closeShipRepairPurchasePrompt();

    const overlay = document.createElement('div');
    overlay.style.cssText = [
      'position:fixed',
      'inset:0',
      'display:flex',
      'align-items:center',
      'justify-content:center',
      'background:rgba(0,0,0,0.55)',
      'z-index:10000',
      'font-family:Arial,sans-serif',
    ].join(';');

    const panel = document.createElement('div');
    panel.style.cssText = [
      'width:min(420px,calc(100vw - 32px))',
      'background:#0d1628',
      'border:1px solid #2b9cff',
      'border-radius:8px',
      'box-shadow:0 20px 60px rgba(0,0,0,0.55)',
      'color:#eaf6ff',
      'padding:18px',
    ].join(';');
    overlay.appendChild(panel);

    const title = document.createElement('div');
    title.textContent = 'Buy Iron & Repair';
    title.style.cssText = 'font-size:18px;font-weight:700;margin-bottom:10px;color:#ffffff';
    panel.appendChild(title);

    const body = document.createElement('div');
    const costText = quote.allMineralsAvailable
      ? `${Number(quote.grandTotal || 0).toFixed(2)} ISD`
      : 'not enough iron listed';
    body.textContent = `Repair needs ${Math.ceil(quote.missingIron)} more iron. Auction cost: ${costText}.`;
    body.style.cssText = 'font-size:14px;line-height:1.45;color:#cfe8ff;margin-bottom:12px';
    panel.appendChild(body);

    const balance = document.createElement('div');
    balance.textContent = `Balance: ${Number(quote.playerIsdBalance || 0).toFixed(2)} ISD`;
    balance.style.cssText = 'font-size:12px;color:#9fc7dd;margin-bottom:16px';
    panel.appendChild(balance);

    const status = document.createElement('div');
    status.style.cssText = 'min-height:18px;font-size:12px;color:#ffcc66;margin-bottom:12px';
    if (!quote.canUseAuctionPurchase) {
      status.textContent = 'Auction iron is delivered to Earth. Dock this ship at Earth first.';
    } else if (!quote.allMineralsAvailable) {
      status.textContent = 'The auction house does not have enough buyable iron right now.';
    } else if (!quote.canAfford) {
      status.textContent = 'Not enough ISD for the auction purchase.';
    }
    panel.appendChild(status);

    const actions = document.createElement('div');
    actions.style.cssText = 'display:flex;gap:10px;justify-content:flex-end';
    panel.appendChild(actions);

    const cancel = document.createElement('button');
    cancel.textContent = 'Cancel';
    cancel.style.cssText = 'padding:9px 14px;border:1px solid #39516f;background:#152238;color:#d8e8ff;border-radius:6px;cursor:pointer';
    cancel.onclick = () => this.closeShipRepairPurchasePrompt();
    actions.appendChild(cancel);

    const buy = document.createElement('button');
    buy.textContent = 'Buy & Repair';
    buy.disabled = !quote.canUseAuctionPurchase || !quote.allMineralsAvailable || !quote.canAfford;
    buy.style.cssText = [
      'padding:9px 14px',
      'border:1px solid #35d16f',
      `background:${buy.disabled ? '#2a3a2f' : '#177339'}`,
      `color:${buy.disabled ? '#8aa895' : '#ffffff'}`,
      'border-radius:6px',
      `cursor:${buy.disabled ? 'default' : 'pointer'}`,
      'font-weight:700',
    ].join(';');
    buy.onclick = async () => {
      buy.disabled = true;
      status.textContent = 'Buying iron and repairing...';
      const result = await monetization.repairShipWithPurchase(unitId);
      if (!result?.success) {
        status.textContent = result?.error || 'Repair failed';
        buy.disabled = false;
        return;
      }
      this.closeShipRepairPurchasePrompt();
      if (result.repair) {
        this.onRepairResult(result.repair);
      }
      const spent = Number(result.totalIsdSpent || 0);
      this.showNotification(`Repair complete. Spent ${spent.toFixed(2)} ISD on iron.`);
    };
    actions.appendChild(buy);

    overlay.addEventListener('pointerdown', (event) => {
      if (event.target === overlay) this.closeShipRepairPurchasePrompt();
    });

    this.repairPurchaseOverlay = overlay;
    document.body.appendChild(overlay);
  }

  private closeShipRepairPurchasePrompt(): void {
    if (this.repairPurchaseOverlay) {
      this.repairPurchaseOverlay.remove();
      this.repairPurchaseOverlay = null;
    }
  }

  private showRequiredComponentPopup(componentName: string, componentId: string, componentCategory: string, shipId?: string): void {
    const hudScene = this.scene.get('MMOHUDScene') || this;
    const cam = hudScene.cameras.main;
    const cx = cam.width / 2;
    const cy = cam.height / 2;

    const popup = hudScene.add.container(cx, cy);
    popup.setDepth(9999);

    // Backdrop
    const bg = hudScene.add.graphics();
    bg.fillStyle(0x0a0e1a, 0.95);
    bg.fillRoundedRect(-160, -70, 320, 140, 8);
    bg.lineStyle(2, 0xffcc00, 0.6);
    bg.strokeRoundedRect(-160, -70, 320, 140, 8);
    popup.add(bg);

    // Title
    popup.add(hudScene.add.text(0, -50, 'Component Required', {
      fontSize: '16px', color: '#ffcc00', fontFamily: '"Share Tech Mono", monospace',
    }).setOrigin(0.5));

    // Message
    popup.add(hudScene.add.text(0, -20, `This asteroid requires\n${componentName}\nto mine.`, {
      fontSize: '13px', color: '#ccddee', fontFamily: '"Share Tech Mono", monospace',
      align: 'center', lineSpacing: 4,
    }).setOrigin(0.5));

    // Get Component button
    const btnBg = hudScene.add.graphics();
    btnBg.fillStyle(0x224422, 1);
    btnBg.fillRoundedRect(-70, 22, 140, 32, 4);
    btnBg.lineStyle(1, 0x44ff66, 0.4);
    btnBg.strokeRoundedRect(-70, 22, 140, 32, 4);
    popup.add(btnBg);

    const btnText = hudScene.add.text(0, 38, 'Get Component', {
      fontSize: '13px', color: '#44ff66', fontFamily: '"Share Tech Mono", monospace',
    }).setOrigin(0.5);
    popup.add(btnText);

    const btnZone = hudScene.add.zone(0, 38, 140, 32).setInteractive();
    btnZone.on('pointerover', () => { btnText.setColor('#88ffaa'); hudScene.input.setDefaultCursor('pointer'); });
    btnZone.on('pointerout', () => { btnText.setColor('#44ff66'); hudScene.input.setDefaultCursor('default'); });
    btnZone.on('pointerdown', () => {
      // Pass shipId so the component store pre-selects the active ship
      const activeShipId = shipId || (this.selectedEntity && 'attackRange' in this.selectedEntity ? this.selectedEntity.id : undefined);
      EventBus.emit('open-component-store', { componentId, componentName, componentCategory, shipId: activeShipId });
      popup.destroy();
    });
    popup.add(btnZone);

    // Close X
    const closeText = hudScene.add.text(148, -60, 'X', {
      fontSize: '14px', color: '#ff6666', fontFamily: '"Share Tech Mono", monospace',
    }).setOrigin(0.5).setInteractive();
    closeText.on('pointerdown', () => popup.destroy());
    closeText.on('pointerover', () => { closeText.setColor('#ff9999'); hudScene.input.setDefaultCursor('pointer'); });
    closeText.on('pointerout', () => { closeText.setColor('#ff6666'); hudScene.input.setDefaultCursor('default'); });
    popup.add(closeText);

    // Auto-dismiss after 15 seconds
    hudScene.time.delayedCall(15000, () => { if (popup.active) popup.destroy(); });
  }

  private showNotification(message: string, color?: string, duration?: number): void {
    // Use HUD scene so notification renders above all game elements
    const hudScene = this.scene.get('MMOHUDScene') || this;
    const camera = hudScene.cameras.main;
    const screenCenterX = camera.width / 2;
    const screenY = Math.round(camera.height * 0.3);

    const notification = hudScene.add.text(screenCenterX, screenY, message, {
      fontSize: '18px',
      color: color || '#00ff88',
      fontFamily: '"Share Tech Mono", monospace',
      backgroundColor: '#000000cc',
      padding: { x: 16, y: 8 },
    });

    notification.setOrigin(0.5, 0);
    notification.setDepth(2000);
    notification.setScrollFactor(0);

    const fadeDelay = duration ? Math.max(duration - 1000, 1000) : 2000;
    hudScene.tweens.add({
      targets: notification,
      alpha: 0,
      duration: 1000,
      delay: fadeDelay,
      onComplete: () => {
        notification.destroy();
      },
    });
  }

  /**
   * Show a celebratory popup when a golden asteroid is delivered.
   * Visual intensity scales with rarity: Gold < Platinum < Dark Matter.
   */
  private showDeliveryPopup(variant?: string, rewardStellar?: number, totalCount?: number): void {
    const camera = this.cameras.main;
    const screenW = camera.width;
    const screenH = camera.height;
    const centerX = screenW / 2;
    const centerY = screenH / 2;

    const FONT_H = "'Orbitron', 'Arial Black', sans-serif";
    const FONT_B = "'Share Tech Mono', 'Courier New', monospace";
    const v = variant || 'common_gold';

    // ── Per-variant theme ────────────────────────────────────────
    interface VTheme {
      icon: string; iconSize: string; iconColor: string;
      titleColor: string; labelColor: string; label: string;
      borderColor: number; accentColor: number; bgColor: number; bgAlpha: number;
      glowColor: number; separatorColor: string;
      starPulses: number; starScale: number;
      shakeDuration: number; shakeIntensity: number;
      sparkleCount: number; sparkleColors: number[];
      displayDuration: number;
      outerGlow: boolean;
    }

    const themes: Record<string, VTheme> = {
      common_gold: {
        icon: '\u2605', iconSize: '34px', iconColor: '#FFD700',
        titleColor: '#FFD700', labelColor: '#ffcc66', label: 'COMMON GOLD',
        borderColor: 0xffaa00, accentColor: 0xffcc00, bgColor: 0x0c1627, bgAlpha: 0.95,
        glowColor: 0xffcc00, separatorColor: '#3a5a2a',
        starPulses: 2, starScale: 1.25,
        shakeDuration: 0, shakeIntensity: 0,
        sparkleCount: 6, sparkleColors: [0xFFD700, 0xffcc00, 0xffee88],
        displayDuration: 4500,
        outerGlow: false,
      },
      rare_platinum: {
        icon: '\u2726', iconSize: '38px', iconColor: '#e0e8ff',
        titleColor: '#c0d8ff', labelColor: '#a0c0ff', label: 'RARE PLATINUM',
        borderColor: 0x6688cc, accentColor: 0x88aaee, bgColor: 0x0a1228, bgAlpha: 0.96,
        glowColor: 0x6688cc, separatorColor: '#3a4a7a',
        starPulses: 3, starScale: 1.4,
        shakeDuration: 150, shakeIntensity: 2,
        sparkleCount: 14, sparkleColors: [0xc0d8ff, 0x88aaee, 0xe0e8ff, 0x6688cc],
        displayDuration: 5500,
        outerGlow: true,
      },
      legendary_dark_matter: {
        icon: '\u2735', iconSize: '42px', iconColor: '#dd88ff',
        titleColor: '#dd88ff', labelColor: '#cc88ff', label: 'LEGENDARY DARK MATTER',
        borderColor: 0x8844cc, accentColor: 0xaa66ee, bgColor: 0x0e0820, bgAlpha: 0.97,
        glowColor: 0x8844cc, separatorColor: '#4a2a6a',
        starPulses: 5, starScale: 1.6,
        shakeDuration: 350, shakeIntensity: 4,
        sparkleCount: 24, sparkleColors: [0xdd88ff, 0xaa66ee, 0x8844cc, 0xff44ff, 0x6622aa],
        displayDuration: 6500,
        outerGlow: true,
      },
    };
    const t = themes[v] || themes.common_gold;

    // ── Screen shake for rare+ ──────────────────────────────────
    if (t.shakeDuration > 0) {
      this.cameras.main.shake(t.shakeDuration, t.shakeIntensity / 1000);
    }

    // ── Sparkle particles (behind popup) ────────────────────────
    const sparkleContainer = this.add.container(centerX, centerY);
    sparkleContainer.setDepth(1999);
    sparkleContainer.setScrollFactor(0);

    for (let i = 0; i < t.sparkleCount; i++) {
      const angle = (Math.PI * 2 * i) / t.sparkleCount + Math.random() * 0.3;
      const dist = 60 + Math.random() * 120;
      const size = 2 + Math.random() * 3;
      const color = t.sparkleColors[Math.floor(Math.random() * t.sparkleColors.length)];

      const dot = this.add.graphics();
      dot.fillStyle(color, 0.9);
      dot.fillCircle(0, 0, size);
      dot.setPosition(0, 0);
      dot.setAlpha(0);
      sparkleContainer.add(dot);

      this.tweens.add({
        targets: dot,
        x: Math.cos(angle) * dist,
        y: Math.sin(angle) * dist,
        alpha: { from: 0.9, to: 0 },
        scaleX: { from: 1, to: 0.2 },
        scaleY: { from: 1, to: 0.2 },
        duration: 800 + Math.random() * 600,
        delay: 200 + Math.random() * 400,
        ease: 'Power2',
      });
    }

    // Clean up sparkle container
    this.time.delayedCall(2000, () => { try { sparkleContainer.destroy(); } catch (_) { /* ok */ } });

    // ── Build content rows ──────────────────────────────────────
    const rows: { text: string; fontSize: string; color: string; font: string; bold?: boolean; gap?: number }[] = [];

    rows.push({ text: t.label, fontSize: '14px', color: t.labelColor, font: FONT_B, gap: 16 });
    rows.push({ text: '\u2500'.repeat(28), fontSize: '12px', color: t.separatorColor, font: FONT_B, gap: 12 });

    const rewardAmount = rewardStellar ?? 0;
    if (rewardAmount > 0) {
      rows.push({ text: `+${rewardAmount.toLocaleString()} $STELLAR`, fontSize: '24px', color: '#00FF88', font: FONT_H, bold: true, gap: 6 });
      rows.push({ text: 'credited to your balance', fontSize: '11px', color: '#6888a8', font: FONT_B, gap: 14 });
    } else {
      rows.push({ text: 'Reward processing...', fontSize: '16px', color: t.titleColor, font: FONT_H, bold: true, gap: 6 });
      rows.push({ text: 'queued \u2014 payout when pool is funded', fontSize: '11px', color: '#6888a8', font: FONT_B, gap: 14 });
    }

    if (totalCount !== undefined && totalCount > 0) {
      rows.push({ text: `Total deliveries: ${totalCount}`, fontSize: '13px', color: '#88aacc', font: FONT_B, gap: 0 });
    }

    // ── Compute dimensions ──────────────────────────────────────
    const backdropW = 400;
    const headerH = 95;
    let contentH = 0;
    for (const row of rows) contentH += parseInt(row.fontSize) + (row.gap ?? 0);
    const backdropH = headerH + contentH + 32;
    const halfW = backdropW / 2;
    const halfH = backdropH / 2;

    // ── Main container ──────────────────────────────────────────
    const container = this.add.container(centerX, centerY);
    container.setDepth(2000);
    container.setScrollFactor(0);
    container.setAlpha(0);

    // Outer glow (rare+)
    if (t.outerGlow) {
      const glow = this.add.graphics();
      glow.fillStyle(t.glowColor, 0.08);
      glow.fillRoundedRect(-halfW - 8, -halfH - 8, backdropW + 16, backdropH + 16, 16);
      container.add(glow);

      this.tweens.add({
        targets: glow,
        alpha: { from: 1, to: 0.3 },
        duration: 1200,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }

    // Background panel
    const bg = this.add.graphics();
    bg.fillStyle(t.bgColor, t.bgAlpha);
    bg.fillRoundedRect(-halfW, -halfH, backdropW, backdropH, 12);
    bg.lineStyle(2, t.borderColor, 0.9);
    bg.strokeRoundedRect(-halfW, -halfH, backdropW, backdropH, 12);
    container.add(bg);

    // Accent bar at top
    const accent = this.add.graphics();
    accent.fillStyle(t.accentColor, 1);
    accent.fillRoundedRect(-halfW, -halfH, backdropW, 4, { tl: 12, tr: 12, bl: 0, br: 0 });
    container.add(accent);

    // For dark matter: second inner border for extra depth
    if (v === 'legendary_dark_matter') {
      const inner = this.add.graphics();
      inner.lineStyle(1, 0xaa66ee, 0.3);
      inner.strokeRoundedRect(-halfW + 4, -halfH + 4, backdropW - 8, backdropH - 8, 10);
      container.add(inner);
    }

    // Icon
    const iconText = this.add.text(0, -halfH + 32, t.icon, {
      fontSize: t.iconSize, color: t.iconColor, fontFamily: 'Arial',
    }).setOrigin(0.5);
    container.add(iconText);

    // Title
    const titleText = this.add.text(0, -halfH + 66, 'ASTEROID DELIVERED!', {
      fontSize: '18px', color: t.titleColor, fontFamily: FONT_H, fontStyle: 'bold',
    }).setOrigin(0.5);
    container.add(titleText);

    // Content rows
    let y = -halfH + headerH;
    for (const row of rows) {
      const txt = this.add.text(0, y, row.text, {
        fontSize: row.fontSize, color: row.color, fontFamily: row.font,
        ...(row.bold ? { fontStyle: 'bold' } : {}),
      }).setOrigin(0.5);
      container.add(txt);
      y += parseInt(row.fontSize) + (row.gap ?? 0);
    }

    // ── Animations ──────────────────────────────────────────────

    // Entry animation: scale + fade
    container.setScale(0.5);
    this.tweens.add({
      targets: container,
      alpha: 1, scaleX: 1, scaleY: 1,
      duration: 500, ease: 'Back.easeOut',
    });

    // Icon pulse — more pulses & scale for rarer variants
    this.tweens.add({
      targets: iconText,
      scaleX: t.starScale, scaleY: t.starScale,
      duration: 500, yoyo: true, repeat: t.starPulses,
      ease: 'Sine.easeInOut',
    });

    // Dark matter: spinning icon
    if (v === 'legendary_dark_matter') {
      this.tweens.add({
        targets: iconText,
        angle: 360,
        duration: 2000,
        ease: 'Power1',
      });
    }

    // Platinum: shimmer effect on title (slight x oscillation)
    if (v === 'rare_platinum') {
      this.tweens.add({
        targets: titleText,
        alpha: { from: 1, to: 0.6 },
        duration: 400,
        yoyo: true,
        repeat: 3,
        ease: 'Sine.easeInOut',
      });
    }

    // Dark matter: continuous title color pulse via alpha
    if (v === 'legendary_dark_matter') {
      this.tweens.add({
        targets: titleText,
        alpha: { from: 1, to: 0.5 },
        duration: 300,
        yoyo: true,
        repeat: 6,
        ease: 'Sine.easeInOut',
      });
    }

    // Exit animation
    this.tweens.add({
      targets: container,
      alpha: 0, scaleX: 0.8, scaleY: 0.8,
      duration: 700, delay: t.displayDuration, ease: 'Power2',
      onComplete: () => container.destroy(),
    });
  }

  /**
   * Calculate distance between two hexes
   */
  private hexDistance(a: HexCoord, b: HexCoord): number {
    // Axial to cube coordinates conversion
    const aCube = { x: a.q, y: -a.q - a.r, z: a.r };
    const bCube = { x: b.q, y: -b.q - b.r, z: b.r };

    // Cube distance
    return Math.max(Math.abs(aCube.x - bCube.x), Math.abs(aCube.y - bCube.y), Math.abs(aCube.z - bCube.z));
  }

  /**
   * Get entity at a given world point (pixel coordinates).
   *
   * When multiple entities overlap (e.g. a ship docked on a planet) we use a
   * proximity heuristic: if the click is close to the center of a small entity
   * (unit / asteroid) we prefer it; otherwise we fall through to the larger
   * background entity (planet / station).  This lets the player click on the
   * ship when they aim precisely, and click on the planet everywhere else.
   */
  private getEntityAtWorldPoint(worldX: number, worldY: number): MMOUnit | MMOPlanet | MMOAsteroid | MMOStation | MMOOrbitalPlatform | MMOWreck | MMOAmbientEntity | MMOLootDrop | MMODroppedRelic | null {
    const allEntities: (MMOUnit | MMOPlanet | MMOAsteroid | MMOStation | MMOOrbitalPlatform | MMOWreck | MMOAmbientEntity | MMOLootDrop | MMODroppedRelic)[] = [
      ...Array.from(this.unitSprites.values()),            // depth 10
      ...Array.from(this.wreckSprites.values()),           // depth 8
      ...Array.from(this.stationSprites.values()),         // depth 7
      ...Array.from(this.platformSprites.values()),        // depth 7
      ...Array.from(this.asteroidSprites.values()),        // depth 5
      ...Array.from(this.droppedRelicSprites.values()),    // depth ~4
      ...Array.from(this.lootDropSprites.values()),        // depth 4
      ...Array.from(this.ambientSprites.values()),         // depth 2-6
      ...Array.from(this.planetSprites.values()),          // depth 0
    ];

    // Collect every entity whose hitbox contains the click point
    const hits: (MMOUnit | MMOPlanet | MMOAsteroid | MMOStation | MMOOrbitalPlatform | MMOWreck | MMOAmbientEntity | MMOLootDrop | MMODroppedRelic)[] = [];

    for (const entity of allEntities) {
      if (!entity.input?.enabled) continue;

      const hitArea = entity.input.hitArea as Phaser.Geom.Rectangle;
      if (!hitArea) continue;

      const left   = entity.x + hitArea.x;
      const right  = entity.x + hitArea.x + hitArea.width;
      const top    = entity.y + hitArea.y;
      const bottom = entity.y + hitArea.y + hitArea.height;

      if (worldX >= left && worldX <= right && worldY >= top && worldY <= bottom) {
        hits.push(entity);
      }
    }

    if (hits.length === 0) return null;

    // "Small" entities (units, asteroids) are only preferred when the click is
    // within a tight radius of their visual center (30 px).  Otherwise the
    // larger background entity (planet / station) wins.
    const TIGHT_RADIUS = 30;
    // Earth (0,0) has an enormous ~420px hitbox that sits under dozens of ships
    // and asteroids. To stop it from hijacking ambiguous taps near the capital,
    // we only accept Earth when the tap is within a tight "core" radius of its
    // visual center. Outside that core, nearby small entities win as usual.
    const EARTH_CORE_RADIUS = 90;

    const isEarth = (e: unknown): e is MMOPlanet =>
      e instanceof MMOPlanet && e.hexCoord.q === 0 && e.hexCoord.r === 0;

    if (hits.length === 1) {
      const only = hits[0];
      if (isEarth(only)) {
        const dx = worldX - only.x;
        const dy = worldY - only.y;
        if (Math.sqrt(dx * dx + dy * dy) > EARTH_CORE_RADIUS) return null;
      }
      return only;
    }

    // Multiple overlapping entities — pick the best one.
    // Sort: highest depth first
    hits.sort((a, b) => b.depth - a.depth);

    for (const entity of hits) {
      const dx = worldX - entity.x;
      const dy = worldY - entity.y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      // Units, asteroids, loot drops & relics are "small" — only pick if click is near center
      if (entity instanceof MMOUnit || entity instanceof MMOAsteroid
          || entity instanceof MMOLootDrop || entity instanceof MMODroppedRelic) {
        if (dist <= TIGHT_RADIUS) return entity;
        continue; // skip — click was in hitbox but far from visual center
      }

      // Earth is a giant planet — only accept when clicking near its visual core.
      if (isEarth(entity)) {
        if (dist <= EARTH_CORE_RADIUS) return entity;
        continue; // skip — tap was inside Earth's huge hitbox but far from its core
      }

      // Other stations & planets: accept them at any distance inside hitbox
      return entity;
    }

    // Fallback: return the highest-depth hit, but never Earth if we reached
    // this point (means nothing else qualified AND Earth wasn't in its core).
    const fallback = hits[0];
    if (isEarth(fallback)) return null;
    return fallback;
  }

  /**
   * Draw a single highlighted hex under the mouse pointer during move or relocation mode.
   * Called every frame from update() — only active when isMovementMode or isRelocationMode is true.
   */
  private drawMovementHoverHex(): void {
    this.movementOverlay.clear();
    if (!this.isMovementMode && !this.isRelocationMode && !this.isStructureDeployMode) return;

    const pointer = this.input.activePointer;
    const worldPoint = this.cameras.main.getWorldPoint(pointer.x, pointer.y);
    const hex = pixelToAxial(worldPoint.x, worldPoint.y);
    const pixel = axialToPixel(hex.q, hex.r);
    const corners = this.getHexCorners(pixel.x, pixel.y);

    const isBlocked = this.blockedHexKeys.has(toHexKey(hex));
    const fillColor = isBlocked ? 0xff4444 : 0x00ffaa;
    const fillAlpha = isBlocked ? 0.1 : 0.15;
    const strokeAlpha = isBlocked ? 0.4 : 0.6;

    this.movementOverlay.fillStyle(fillColor, fillAlpha);
    this.movementOverlay.lineStyle(2, fillColor, strokeAlpha);

    this.movementOverlay.beginPath();
    this.movementOverlay.moveTo(corners[0].x, corners[0].y);
    for (let i = 1; i < 6; i++) {
      this.movementOverlay.lineTo(corners[i].x, corners[i].y);
    }
    this.movementOverlay.closePath();
    this.movementOverlay.fillPath();
    this.movementOverlay.strokePath();
  }

  /**
   * Handle movement destination selection
   */
  private handleMovementDestination(hex: HexCoord, _hexKey: string): void {
    // Click on the unit's own hex cancels move mode
    if (this.movementUnit && hex.q === this.movementUnit.hexCoord.q && hex.r === this.movementUnit.hexCoord.r) {
      this.cancelMovementMode();
      return;
    }

    // Reject clicks on hexes occupied by planets or stations
    if (this.blockedHexKeys.has(toHexKey(hex))) {
      this.showNotification('Cannot move there — hex is occupied');
      return;
    }

    if (this.movementUnit) {
      const unitType = this.movementUnit.unitType?.replace('unit_', '').replace(/_/g, ' ') || 'Unit';

      // Task 3: Zone intrusion warning — check if destination is in hostile alliance territory
      this.checkTerritoryIntrusionWarning(hex);

      this.mmoNetwork.sendMoveUnit(this.movementUnit.id, hex);
      this.showNotification(`${unitType} moving to (${hex.q}, ${hex.r})`);

      // Immediate visible acknowledgement at the target hex. Previously a move
      // tap was silent between the finger-lift and the server's next tick
      // (~500ms), so it was easy to think "nothing happened" and tap again.
      // The ping proves the command was accepted client-side even if the
      // server later reroutes (see onMovementRerouted).
      this.pingDestinationHex(hex);

      EventBus.emit('mmo-movement-queued', {
        unitId: this.movementUnit.id,
        destination: hex,
      });
    }

    this.cancelMovementMode();
  }

  /**
   * Brief flash at a hex to acknowledge a destination tap. Draws an expanding
   * cyan ring that fades out over ~450ms, then destroys itself. Used to give
   * immediate visual feedback on MOVE taps so players don't think the tap
   * was ignored while they wait for the server tick.
   */
  private pingDestinationHex(hex: HexCoord): void {
    const { x, y } = axialToPixel(hex.q, hex.r);
    const ring = this.add.graphics();
    ring.lineStyle(3, 0x00ffff, 0.95);
    ring.strokeCircle(x, y, 22);
    ring.setDepth(50);

    this.tweens.add({
      targets: ring,
      alpha: { from: 1, to: 0 },
      scale: { from: 0.7, to: 1.6 },
      duration: 450,
      ease: 'Cubic.easeOut',
      onComplete: () => ring.destroy(),
    });
  }

  /**
   * Handler for server `mmo_movement_rerouted` — fires when the originally
   * tapped hex was occupied at tick time (a planet, station, or another ship
   * drifted into it between the client send and server execution). Before
   * this handler existed the reroute was silent: the ship would simply end
   * up one hex away from where the player tapped and it looked like a bug.
   */
  private onMovementRerouted(payload: {
    unitId: string;
    originalTarget: HexCoord;
    actualTarget: HexCoord;
    reason?: string;
    source?: 'manual' | 'return_to_base';
  }): void {
    if (!payload) return;
    const o = payload.originalTarget;
    const a = payload.actualTarget;
    if (payload.source !== 'return_to_base') {
      this.showNotification(`Destination (${o.q},${o.r}) was blocked — rerouted to (${a.q},${a.r})`);
    }
    // Ping both hexes so the player sees where the reroute landed
    this.pingDestinationHex(a);
  }

  // ===== ATTACK MODE METHODS =====

  /**
   * Enter attack mode for a unit
   */
  private enterAttackMode(unit: MMOUnit): void {
    if (this.isMovementMode) this.cancelMovementMode();
    if (this.isMineMode) this.cancelMineMode();

    this.isAttackMode = true;
    this.attackingUnit = unit;

    // Calculate valid attack targets
    this.validAttackTargets = this.calculateAttackTargets(unit);

    if (this.validAttackTargets.size === 0) {
      const targetSummary = this.getVisibleAttackTargetSummary(unit);
      if (targetSummary.attackable === 0) {
        const message = targetSummary.protected > 0
          ? 'Visible enemy ships are docked and protected — pick an undocked target'
          : 'No visible enemy targets — move closer or scout ahead';
        this.showNotification(message, '#ff8844', 3500);
        this.isAttackMode = false;
        this.attackingUnit = null;
        this.input.setDefaultCursor('default');
        return;
      }
      this.showNotification('No enemies in weapon range — click a visible enemy to pursue', '#ff8844', 3500);
    }

    // Draw attack overlay
    this.drawAttackOverlay();

    // Set initial cursor for attack mode
    this.input.setDefaultCursor('not-allowed');

    // Emit event for UI to show instructions
    EventBus.emit('mmo-attack-mode-started', { unit });
  }

  /**
   * Cancel attack mode
   */
  private cancelAttackMode(): void {
    if (!this.isAttackMode) {
      return;
    }

    console.log('[MMOGame] Cancelling attack mode');

    this.isAttackMode = false;
    this.attackingUnit = null;
    this.validAttackTargets.clear();

    // Clear attack overlay
    this.attackOverlay.clear();

    // Reset cursor to default
    this.input.setDefaultCursor('default');

    // Emit event for UI
    EventBus.emit('mmo-attack-mode-cancelled');
  }

  /**
   * Calculate valid attack targets for a unit
   */
  private calculateAttackTargets(unit: MMOUnit): Map<string, AttackTarget> {
    const validTargets = new Map<string, AttackTarget>();

    // Use the unit's server-provided attackRange (not static UNIT_STATS which is incomplete)
    const attackRange = unit.attackRange || 1;
    if (attackRange === 0) {
      console.log('[MMOGame] Unit cannot attack (attackRange = 0)');
      return validTargets;
    }

    // Get player ID to identify enemies
    const playerId = this.mmoState.getUserId();
    if (!playerId) {
      console.warn('[MMOGame] Player ID not found');
      return validTargets;
    }

    // Get all hexes within attack range
    const currentPos = unit.hexCoord;

    for (let q = -attackRange; q <= attackRange; q++) {
      for (let r = -attackRange; r <= attackRange; r++) {
        const targetHex = { q: currentPos.q + q, r: currentPos.r + r };

        // Calculate cube distance
        const distance = this.hexDistance(currentPos, targetHex);

        if (distance >= 0 && distance <= attackRange) {
          const hexKey = `${targetHex.q},${targetHex.r}`;

          // Only include visible hexes
          if (!this.mmoState.isHexVisible(hexKey)) {
            continue;
          }

          // Check for enemy entities at this hex
          const entities = this.mmoState.getEntitiesAt(targetHex);

          entities.forEach((entity) => {
            // Only units can be attacked — check for unit-specific properties
            // (ownerId + attackRange distinguishes units from planets/asteroids/stations)
            if (!('ownerId' in entity) || !('attackRange' in entity || 'attack' in entity)) {
              return;
            }

            const targetUnit = entity as { id: string; ownerId: string; dockedAtPlanetId?: string | null };

            // Skip docked units (they cannot be attacked)
            if (targetUnit.dockedAtPlanetId) {
              return;
            }

            // Check if entity is an enemy
            if (targetUnit.ownerId && targetUnit.ownerId !== playerId) {
              validTargets.set(hexKey, {
                entityId: targetUnit.id,
                position: targetHex,
              });
            }
          });
        }
      }
    }

    console.log(`[MMOGame] Found ${validTargets.size} valid attack targets (range: ${attackRange})`);
    return validTargets;
  }

  private toAttackTarget(entity: unknown): AttackTarget | null {
    const unit = entity as {
      id?: string;
      ownerId?: string;
      attackRange?: number;
      attack?: number;
      dockedAtPlanetId?: string | null;
      hexCoord?: HexCoord;
      position?: HexCoord;
    } | null;
    if (!unit?.id || !unit.ownerId) return null;
    if (!('attackRange' in unit) && !('attack' in unit)) return null;

    const playerId = this.mmoState.getUserId();
    if (!playerId || unit.ownerId === playerId || unit.dockedAtPlanetId) return null;

    const position = unit.hexCoord ?? unit.position;
    if (!position) return null;
    if (!this.mmoState.isHexVisible(toHexKey(position))) return null;

    return { entityId: unit.id, position };
  }

  private isProtectedEnemyUnit(entity: unknown): boolean {
    const unit = entity as {
      id?: string;
      ownerId?: string;
      attackRange?: number;
      attack?: number;
      dockedAtPlanetId?: string | null;
      hexCoord?: HexCoord;
      position?: HexCoord;
    } | null;
    if (!unit?.id || !unit.ownerId) return false;
    if (!('attackRange' in unit) && !('attack' in unit)) return false;

    const playerId = this.mmoState.getUserId();
    if (!playerId || unit.ownerId === playerId || !unit.dockedAtPlanetId) return false;

    const position = unit.hexCoord ?? unit.position;
    return !!position && this.mmoState.isHexVisible(toHexKey(position));
  }

  private getVisibleAttackTargetSummary(attacker: MMOUnit): { attackable: number; protected: number } {
    let attackable = 0;
    let protectedCount = 0;

    for (const unit of this.mmoState.getAllUnits()) {
      if (unit.id === attacker.id) continue;
      if (this.toAttackTarget(unit)) {
        attackable++;
      } else if (this.isProtectedEnemyUnit(unit)) {
        protectedCount++;
      }
    }

    return { attackable, protected: protectedCount };
  }

  private findAttackTargetAtHex(hex: HexCoord): AttackTarget | null {
    for (const entity of this.mmoState.getEntitiesAt(hex)) {
      const target = this.toAttackTarget(entity);
      if (target) return target;
    }
    return null;
  }

  private resolveAttackTargetAtPointer(
    pointer: Phaser.Input.Pointer,
    hex: HexCoord,
    hexKey: string,
  ): AttackTarget | null {
    const worldPoint = this.cameras.main.getWorldPoint(pointer.x, pointer.y);
    const hitEntity = this.getEntityAtWorldPoint(worldPoint.x, worldPoint.y);
    const clickedTarget = this.toAttackTarget(hitEntity);
    if (clickedTarget) return clickedTarget;

    return this.validAttackTargets.get(hexKey) ?? this.findAttackTargetAtHex(hex);
  }

  private hasProtectedAttackTargetAtPointer(pointer: Phaser.Input.Pointer, hex: HexCoord): boolean {
    const worldPoint = this.cameras.main.getWorldPoint(pointer.x, pointer.y);
    const hitEntity = this.getEntityAtWorldPoint(worldPoint.x, worldPoint.y);
    if (this.isProtectedEnemyUnit(hitEntity)) return true;

    return this.mmoState.getEntitiesAt(hex).some((entity) => this.isProtectedEnemyUnit(entity));
  }

  /**
   * Draw attack overlay highlighting valid targets
   */
  private drawAttackOverlay(): void {
    this.attackOverlay.clear();

    this.validAttackTargets.forEach((target) => {
      const hex = target.position;

      const pixel = axialToPixel(hex.q, hex.r);
      const corners = this.getHexCorners(pixel.x, pixel.y);

      // Red highlight for valid attack targets
      this.attackOverlay.fillStyle(0xff0000, 0.2);
      this.attackOverlay.lineStyle(3, 0xff0000, 0.9);

      this.attackOverlay.beginPath();
      this.attackOverlay.moveTo(corners[0].x, corners[0].y);
      for (let i = 1; i < 6; i++) {
        this.attackOverlay.lineTo(corners[i].x, corners[i].y);
      }
      this.attackOverlay.closePath();
      this.attackOverlay.fillPath();
      this.attackOverlay.strokePath();

      // Draw crosshair in center of hex
      const crosshairSize = 10;
      this.attackOverlay.lineStyle(2, 0xff0000, 1.0);
      this.attackOverlay.beginPath();
      this.attackOverlay.moveTo(pixel.x - crosshairSize, pixel.y);
      this.attackOverlay.lineTo(pixel.x + crosshairSize, pixel.y);
      this.attackOverlay.moveTo(pixel.x, pixel.y - crosshairSize);
      this.attackOverlay.lineTo(pixel.x, pixel.y + crosshairSize);
      this.attackOverlay.strokePath();
    });
  }

  /**
   * Handle attack target selection
   */
  private handleAttackTarget(hex: HexCoord, hexKey: string): void {
    // Check if this is a valid target
    const target = this.resolveAttackTargetAtPointer(this.input.activePointer, hex, hexKey);
    if (!target) {
      console.log('[MMOGame] Invalid attack target:', hexKey);
      const message = this.hasProtectedAttackTargetAtPointer(this.input.activePointer, hex)
        ? 'That ship is docked and protected. Pick an undocked target.'
        : 'No enemy target there — tap an enemy ship or use Cancel';
      this.showNotification(message, '#ff8844', 2500);
      return;
    }

    // Send attack command to server
    if (this.attackingUnit) {
      console.log(`[MMOGame] Attacking: ${this.attackingUnit.id} -> ${target.entityId} at (${hex.q}, ${hex.r})`);

      this.mmoNetwork.sendAttack(this.attackingUnit.id, target.entityId, target.position);

      // Pursuit confirmation message
      this.showNotification('Aye aye Commander! Pursuing the enemy — weapons hot!', '#ff8844', 4000);

      // Visual feedback
      EventBus.emit('mmo-attack-queued', {
        attackerId: this.attackingUnit.id,
        targetId: target.entityId,
        position: target.position,
      });
    }

    // Exit attack mode
    this.cancelAttackMode();
  }

  // ===== MINE HELPERS =====

  /**
   * Collect all mineable (non-depleted) asteroids at and adjacent to a position.
   * Returns a Map keyed by hex key for use as valid mine targets.
   */
  private findMineableAsteroids(unitPos: HexCoord): Map<string, { asteroidId: string; position: HexCoord }> {
    const targets = new Map<string, { asteroidId: string; position: HexCoord }>();
    const hexesToCheck = [unitPos, ...hexNeighbors(unitPos)];

    for (const hex of hexesToCheck) {
      for (const entity of this.mmoState.getEntitiesAt(hex)) {
        if (!('resourceRichness' in entity)) continue;
        const a = entity as any;
        if (a.asteroidType && a.asteroidType !== 'regular' && a.asteroidType !== 'golden' && a.asteroidType !== 'special') continue;
        if (this.isAsteroidDepleted(entity)) continue;
        targets.set(toHexKey(a.position), { asteroidId: a.id, position: a.position });
      }
    }

    return targets;
  }

  // ===== MINE-TARGET MODE METHODS =====

  /**
   * Enter mine-target mode — player picks which adjacent asteroid to mine.
   * Only entered when 2+ asteroids are within mining range.
   */
  private enterMineMode(unit: MMOUnit, targets: Map<string, { asteroidId: string; position: HexCoord }>): void {
    if (this.isMovementMode) this.cancelMovementMode();
    if (this.isAttackMode) this.cancelAttackMode();

    this.isMineMode = true;
    this.miningUnit = unit;
    this.validMineTargets = targets;

    this.drawMineOverlay();
    this.input.setDefaultCursor('not-allowed');
    this.showNotification('Select an asteroid to mine');

    EventBus.emit('mmo-mine-mode-started', { unit });
  }

  private cancelMineMode(): void {
    if (!this.isMineMode) return;

    this.isMineMode = false;
    this.miningUnit = null;
    this.validMineTargets.clear();
    this.mineOverlay.clear();
    this.movementOverlay.clear();
    this.input.setDefaultCursor('default');

    EventBus.emit('mmo-mine-mode-cancelled');
  }

  private drawMineOverlay(): void {
    this.mineOverlay.clear();

    this.validMineTargets.forEach((target) => {
      const pixel = axialToPixel(target.position.q, target.position.r);
      const corners = this.getHexCorners(pixel.x, pixel.y);

      this.mineOverlay.fillStyle(0xffaa00, 0.15);
      this.mineOverlay.lineStyle(2, 0xffaa00, 0.7);

      this.mineOverlay.beginPath();
      this.mineOverlay.moveTo(corners[0].x, corners[0].y);
      for (let i = 1; i < 6; i++) {
        this.mineOverlay.lineTo(corners[i].x, corners[i].y);
      }
      this.mineOverlay.closePath();
      this.mineOverlay.fillPath();
      this.mineOverlay.strokePath();
    });
  }

  /**
   * Draw a bright hover hex under the cursor during mine-target mode.
   * Valid targets get a bright white-gold highlight; invalid hexes get a dim red.
   * Called every frame from update().
   */
  private drawMineHoverHex(): void {
    this.movementOverlay.clear();
    if (!this.isMineMode) return;

    const pointer = this.input.activePointer;
    const worldPoint = this.cameras.main.getWorldPoint(pointer.x, pointer.y);
    const hex = pixelToAxial(worldPoint.x, worldPoint.y);

    if (!this.validMineTargets.has(toHexKey(hex))) return;

    const pixel = axialToPixel(hex.q, hex.r);
    const corners = this.getHexCorners(pixel.x, pixel.y);

    this.movementOverlay.fillStyle(0xffffff, 0.25);
    this.movementOverlay.lineStyle(3, 0xffdd44, 0.9);

    this.movementOverlay.beginPath();
    this.movementOverlay.moveTo(corners[0].x, corners[0].y);
    for (let i = 1; i < 6; i++) {
      this.movementOverlay.lineTo(corners[i].x, corners[i].y);
    }
    this.movementOverlay.closePath();
    this.movementOverlay.fillPath();
    this.movementOverlay.strokePath();
  }

  private handleMineTarget(_hex: HexCoord, hexKey: string): void {
    const target = this.validMineTargets.get(hexKey);
    if (!target) {
      this.cancelMineMode();
      return;
    }

    if (this.miningUnit) {
      this.mmoNetwork.sendMineAsteroid(this.miningUnit.id, target.asteroidId);
    }

    this.cancelMineMode();
  }

  // ==================== COMBAT EFFECTS ====================

  /**
   * Render beam weapon effect
   */
  private renderBeamEffect(fromPos: HexCoord, toPos: HexCoord, color: string): void {
    const fromPixel = axialToPixel(fromPos.q, fromPos.r);
    const toPixel = axialToPixel(toPos.q, toPos.r);

    // Create beam graphics
    const beam = this.add.graphics();
    beam.setDepth(100);

    // Parse color
    const colorNum = parseInt(color.replace('#', ''), 16);

    // Draw beam line
    beam.lineStyle(3, colorNum, 1);
    beam.lineBetween(fromPixel.x, fromPixel.y, toPixel.x, toPixel.y);

    // Glow effect
    beam.lineStyle(6, colorNum, 0.3);
    beam.lineBetween(fromPixel.x, fromPixel.y, toPixel.x, toPixel.y);

    // Fade out and destroy
    this.tweens.add({
      targets: beam,
      alpha: 0,
      duration: 300,
      onComplete: () => beam.destroy(),
    });
  }

  /**
   * Render missile/projectile effect
   */
  private renderMissileEffect(fromPos: HexCoord, toPos: HexCoord, color: string): void {
    const fromPixel = axialToPixel(fromPos.q, fromPos.r);
    const toPixel = axialToPixel(toPos.q, toPos.r);

    // Create projectile
    const projectile = this.add.circle(fromPixel.x, fromPixel.y, 4, parseInt(color.replace('#', ''), 16));
    projectile.setDepth(100);

    // Create trail
    const trail = this.add.graphics();
    trail.setDepth(99);

    // Animate projectile
    this.tweens.add({
      targets: projectile,
      x: toPixel.x,
      y: toPixel.y,
      duration: 500,
      ease: 'Cubic.easeIn',
      onUpdate: () => {
        // Draw trail
        trail.clear();
        trail.lineStyle(2, parseInt(color.replace('#', ''), 16), 0.5);
        trail.lineBetween(fromPixel.x, fromPixel.y, projectile.x, projectile.y);
      },
      onComplete: () => {
        projectile.destroy();
        trail.destroy();
        // Impact explosion
        this.renderExplosion(toPos, color);
      },
    });
  }

  // Track active explosion count to cap concurrent particle effects
  private activeExplosions: number = 0;
  private static readonly MAX_CONCURRENT_EXPLOSIONS = 4;

  /**
   * Throttled screen shake — prevents overlapping shakes during rapid combat
   */
  private throttledShake(duration: number, intensity: number): void {
    const now = this.time.now;
    const cooldown = duration + 100; // Wait for previous shake to finish + buffer
    if (now - this.lastShakeTime < cooldown) return;
    this.lastShakeTime = now;
    this.cameras.main.shake(duration, intensity);
  }

  /**
   * Render explosion effect (capped to avoid GPU overload from many simultaneous explosions)
   */
  private renderExplosion(pos: HexCoord, color: string): void {
    const pixel = axialToPixel(pos.q, pos.r);
    const tint = parseInt(color.replace('#', ''), 16);

    // Cap concurrent particle emitters — still show flash even if capped
    if (this.activeExplosions < MMOGameScene.MAX_CONCURRENT_EXPLOSIONS) {
      this.activeExplosions++;

      const particles = this.add.particles(pixel.x, pixel.y, 'particle', {
        speed: { min: 50, max: 150 },
        angle: { min: 0, max: 360 },
        scale: { start: 0.5, end: 0 },
        alpha: { start: 1, end: 0 },
        lifespan: 500,
        quantity: 8, // Reduced from 15 to 8 particles
        tint,
        blendMode: 'ADD',
      });

      particles.setDepth(100);

      this.time.delayedCall(600, () => {
        particles.destroy();
        this.activeExplosions--;
      });
    }

    // Flash effect (cheap — always shown)
    const flash = this.add.circle(pixel.x, pixel.y, 30, tint, 0.8);
    flash.setDepth(99);

    this.tweens.add({
      targets: flash,
      alpha: 0,
      scale: 2,
      duration: 300,
      onComplete: () => flash.destroy(),
    });
  }

  /**
   * Render floating damage number
   */
  private renderDamageNumber(pos: HexCoord, damage: number, color: string = '#ff3333', critical: boolean = false): void {
    const pixel = axialToPixel(pos.q, pos.r);
    const fontSize = critical ? '32px' : '20px';
    const strokeThickness = critical ? 4 : 3;

    const damageText = this.add.text(pixel.x, pixel.y - 20, `-${damage}`, {
      fontSize,
      color,
      fontFamily: 'Arial',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness,
    });
    damageText.setOrigin(0.5);
    damageText.setDepth(101);

    if (critical) {
      // Scale punch effect for critical hits
      damageText.setScale(1.4);
      this.tweens.add({
        targets: damageText,
        scale: 1,
        duration: 200,
        ease: 'Back.easeOut',
      });
    }

    // Animate upward and fade
    this.tweens.add({
      targets: damageText,
      y: pixel.y - (critical ? 80 : 60),
      alpha: 0,
      duration: DAMAGE_NUMBER_DURATION,
      ease: 'Cubic.easeOut',
      onComplete: () => damageText.destroy(),
    });
  }

  /**
   * Render floating text label (for MISS, subsystem hits, etc.)
   */
  private renderFloatingLabel(
    pos: HexCoord, label: string, color: string,
    offsetY: number = 0, fontSize: string = '16px',
  ): void {
    const pixel = axialToPixel(pos.q, pos.r);

    const text = this.add.text(pixel.x, pixel.y - 20 + offsetY, label, {
      fontSize,
      color,
      fontFamily: 'Arial',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 3,
    });
    text.setOrigin(0.5);
    text.setDepth(101);

    this.tweens.add({
      targets: text,
      y: pixel.y - 60 + offsetY,
      alpha: 0,
      duration: DAMAGE_NUMBER_DURATION,
      ease: 'Cubic.easeOut',
      onComplete: () => text.destroy(),
    });
  }

  /**
   * Render flash effect on hit
   */
  private renderFlashEffect(pos: HexCoord, color: number): void {
    const pixel = axialToPixel(pos.q, pos.r);

    const flash = this.add.circle(pixel.x, pixel.y, 25, color, 0.6);
    flash.setDepth(102);

    this.tweens.add({
      targets: flash,
      alpha: 0,
      duration: 200,
      onComplete: () => flash.destroy(),
    });
  }

  /**
   * Handle combat result from server
   */
  private onCombatResultReceived(result: any): void {
    console.log('[MMOGame] Combat result received:', result);

    // Ignore "queued" responses or invalid data - wait for actual combat result
    if (!result || result.queued) {
      return;
    }

    // ── Accumulate stats for battle report ──────────────────────
    const accKey = [result.attackerId, result.defenderId].sort().join('|');
    let acc = this.combatAccumulators.get(accKey);
    if (!acc) {
      const myId = this.mmoState.getUserId();
      const atkOwner = this.unitSprites.get(result.attackerId)?.ownerId;
      const defOwner = this.unitSprites.get(result.defenderId)?.ownerId;
      const atkName = result.attackerName || 'Unknown';
      const defName = result.defenderName || 'Unknown';
      acc = {
        attackerName: (atkOwner === myId || (!atkOwner && result.attackerId.includes(myId || ''))) ? `Your ${atkName}` : `Enemy ${atkName}`,
        defenderName: (defOwner === myId || (!defOwner && result.defenderId.includes(myId || ''))) ? `Your ${defName}` : `Enemy ${defName}`,
        attackerId: result.attackerId,
        defenderId: result.defenderId,
        attackerStats: createEmptySideStats(),
        defenderStats: createEmptySideStats(),
        rounds: 0,
      };
      this.combatAccumulators.set(accKey, acc);
    }
    accumulateCombatResult(acc, result);

    // Get unit sprites
    const attackerSprite = this.unitSprites.get(result.attackerId);
    const defenderSprite = this.unitSprites.get(result.defenderId);

    if (!attackerSprite || !defenderSprite) {
      // Combat outside player's fog of war - no sprites to animate
      return;
    }

    const attackerPos = attackerSprite.hexCoord;
    const defenderPos = defenderSprite.hexCoord;

    // ── Set combat animation guard ──────────────────────────────
    this.combatAnimating = true;
    // Clear any previous timers from earlier combat rounds
    for (const timer of this.combatAnimationTimers) {
      timer.remove(false);
    }
    this.combatAnimationTimers = [];

    // Helper: schedule a delayedCall and track it for cleanup
    const scheduleEffect = (delayMs: number, callback: () => void): void => {
      const timer = this.time.delayedCall(delayMs, callback);
      this.combatAnimationTimers.push(timer);
    };

    // Helper: render one side's weapon effects (capped to MAX_VISUAL_EFFECTS_PER_SIDE)
    const renderSideEffects = (
      events: any[],
      fromPos: HexCoord,
      toPos: HexCoord,
      beamColor: string,
      damageColor: string,
      flashColor: number,
      targetSprite: MMOUnit,
      startDelay: number,
    ): number => {
      let sideDelay = startDelay;
      const totalEvents = events.length;
      const cappedEvents = events.slice(0, MAX_VISUAL_EFFECTS_PER_SIDE);
      const batchedCount = totalEvents - cappedEvents.length;

      // Render individual effects for capped subset
      cappedEvents.forEach((event: any) => {
        const weaponName = event.weaponUsed.toLowerCase();
        const isMissile = weaponName.includes('missile') || weaponName.includes('torpedo');
        const weaponDelay = isMissile ? MISSILE_WEAPON_DELAY : BEAM_WEAPON_DELAY;
        const damageDelay = isMissile ? MISSILE_DAMAGE_DELAY : BEAM_DAMAGE_DELAY;
        const currentDelay = sideDelay;

        scheduleEffect(currentDelay, () => {
          if (
            weaponName.includes('beam') ||
            weaponName.includes('laser') ||
            weaponName.includes('plasma') ||
            weaponName.includes('ion')
          ) {
            this.renderBeamEffect(fromPos, toPos, beamColor);
          } else if (isMissile) {
            this.renderMissileEffect(fromPos, toPos, '#ffaa00');
          } else {
            this.renderBeamEffect(fromPos, toPos, '#ffffff');
          }

          if (event.hit && event.damage > 0) {
            scheduleEffect(currentDelay + damageDelay, () => {
              const isCritical = !!event.subsystemHit;
              this.renderDamageNumber(toPos, event.damage, isCritical ? '#ffcc00' : damageColor, isCritical);

              if (event.subsystemHit) {
                const subsysLabel = event.componentName
                  ? `${event.componentName} hit!`
                  : event.subsystemHit.toUpperCase() + ' HIT!';
                this.renderFloatingLabel(toPos, subsysLabel, '#ffaa00', -50, '20px');
              }

              this.renderFlashEffect(toPos, flashColor);

              if (event.shieldDamage > 0 && targetSprite) {
                targetSprite.flashShield(event.shieldDamage >= (targetSprite.currentShields + event.shieldDamage));
              }

              const pid = this.mmoState.getUserId();
              if (pid && targetSprite && targetSprite.ownerId === pid) {
                this.throttledShake(100, 0.003);
              }
            });
          } else if (!event.hit) {
            scheduleEffect(currentDelay + damageDelay, () => {
              this.renderFloatingLabel(toPos, 'MISS', '#aaaaaa');
            });
          }
        });

        sideDelay += weaponDelay;
      });

      // Batch remaining effects into a single combined visual
      if (batchedCount > 0) {
        const batchedDamage = events.slice(MAX_VISUAL_EFFECTS_PER_SIDE);
        const totalBatchDmg = batchedDamage.reduce((sum: number, e: any) => sum + (e.hit ? (e.damage || 0) : 0), 0);
        const batchHits = batchedDamage.filter((e: any) => e.hit).length;
        const batchMisses = batchedCount - batchHits;
        const batchDelay = sideDelay;

        scheduleEffect(batchDelay, () => {
          // Show a single combined beam for all batched weapons
          this.renderBeamEffect(fromPos, toPos, beamColor);

          if (totalBatchDmg > 0) {
            scheduleEffect(batchDelay + BEAM_DAMAGE_DELAY, () => {
              this.renderDamageNumber(toPos, totalBatchDmg, damageColor, false);
              this.renderFlashEffect(toPos, flashColor);

              if (targetSprite) {
                const pid = this.mmoState.getUserId();
                if (pid && targetSprite.ownerId === pid) {
                  this.throttledShake(100, 0.003);
                }
              }
            });
          }
          if (batchMisses > 0) {
            scheduleEffect(batchDelay + BEAM_DAMAGE_DELAY, () => {
              this.renderFloatingLabel(toPos, `${batchMisses}x MISS`, '#aaaaaa');
            });
          }
        });

        sideDelay += BEAM_WEAPON_DELAY;
      }

      return sideDelay;
    };

    // Render attacker's attacks
    let delay = renderSideEffects(
      result.attackerEvents || [],
      attackerPos, defenderPos,
      '#ff3333', '#ff3333', 0xff0000,
      defenderSprite, 0,
    );

    // Render defender's counter-attacks
    delay = renderSideEffects(
      result.defenderEvents || [],
      defenderPos, attackerPos,
      '#3333ff', '#3366ff', 0x3333ff,
      attackerSprite, delay,
    );

    // Show destruction effects + stronger screen shake
    const playerId = this.mmoState.getUserId();
    if (result.attackerDestroyed) {
      scheduleEffect(delay, () => {
        this.renderExplosion(attackerPos, '#ff6600');
        if (playerId && attackerSprite && attackerSprite.ownerId === playerId) {
          this.throttledShake(300, 0.01);
        }
      });
    }

    if (result.defenderDestroyed) {
      scheduleEffect(delay, () => {
        this.renderExplosion(defenderPos, '#ff6600');
        if (playerId && defenderSprite && defenderSprite.ownerId === playerId) {
          this.throttledShake(300, 0.01);
        }
      });
    }

    // Release combat animation guard after all effects finish + buffer
    const totalAnimDuration = delay + 500;
    scheduleEffect(totalAnimDuration, () => {
      this.combatAnimating = false;
      this.combatAnimationTimers = [];
      // Do a single re-render now that combat visuals are done
      this.renderEntities();
    });
  }

  // ===== FACING MODE METHODS =====

  private onActionAbility(data: { entity: any; abilityId: number }): void {
    console.log('[MMOGame] Ability action requested:', data.abilityId, 'for unit:', data.entity.id);
    this.mmoNetwork.sendActivateAbility(data.entity.id, data.abilityId);
  }

  // ===== ABILITY/EFFECT EVENT HANDLERS =====

  private onAbilityActivated(payload: any): void {
    console.log('[MMOGame] Ability activated:', payload);
    if (payload.abilityName) {
      this.showNotification(`${payload.abilityName} activated!`);
    }

    // Update ability cooldown in state manager (match by name since server sends abilityName not abilityTypeId)
    const stateUnit = this.mmoState.getUnit(payload.unitId);
    if (stateUnit && stateUnit.abilities && payload.abilityName) {
      const ability = stateUnit.abilities.find(a => a.name === payload.abilityName);
      if (ability) {
        ability.currentCooldown = payload.cooldownTicks || ability.cooldownTicks;
      }
    }

    // Also update the sprite's ability cooldowns so CommandDock reflects the change
    const unitSprite = this.unitSprites.get(payload.unitId);
    if (unitSprite && (unitSprite as any).abilities && payload.abilityName) {
      const spriteAbility = (unitSprite as any).abilities.find((a: any) => a.name === payload.abilityName);
      if (spriteAbility) {
        spriteAbility.currentCooldown = payload.cooldownTicks || spriteAbility.cooldownTicks;
      }
    }

    // Ability flash on the unit sprite
    if (unitSprite) {
      const abilityColors: Record<string, number> = {
        EVASIVE: 0x44ff44,
        SHIELD_BOOSTED: 0x4488ff,
        SPEED_BOOSTED: 0xffff00,
        SENSOR_BOOSTED: 0x00ffff,
        MISSILE_BARRAGE: 0xff4444,
        BROADSIDE: 0xff8800,
      };
      const effectType = payload.effectType || payload.abilityName?.toUpperCase().replace(/\s+/g, '_') || '';
      const color = abilityColors[effectType] || 0xffffff;
      unitSprite.flashAbility(color);
    }

    // Refresh sidebar if the unit is selected
    if (this.selectedEntity && this.selectedEntity.id === payload.unitId) {
      EventBus.emit('mmo-entity-selected', { entity: this.selectedEntity });
    }
  }

  private onRelicDiscovered(relic: any): void {
    const tierColors: Record<number, number> = { 1: 0xffffff, 2: 0x44ff44, 3: 0x4488ff, 4: 0xaa44ff, 5: 0xffcc00 };
    const color = tierColors[relic.tier] || 0xffffff;
    const hexColor = '#' + color.toString(16).padStart(6, '0');

    // Create a special notification for relic discovery
    const camera = this.cameras.main;
    const centerX = camera.scrollX + camera.width / 2;
    const topY = camera.scrollY + 60;

    const notification = this.add.text(centerX, topY, `RELIC DISCOVERED: ${relic.name} (Tier ${relic.tier})`, {
      fontSize: '22px',
      color: hexColor,
      fontFamily: 'Arial',
      backgroundColor: '#000000cc',
      padding: { x: 20, y: 10 },
    });

    notification.setOrigin(0.5, 0);
    notification.setDepth(1000);
    notification.setScrollFactor(0);

    this.tweens.add({
      targets: notification,
      alpha: 0,
      duration: 1500,
      delay: 4500,
      onComplete: () => {
        notification.destroy();
      },
    });
  }

  private onRelicPickedUp(payload: any): void {
    if (payload?.success && payload.relicId) {
      // Remove the sprite for the picked up relic
      const sprite = this.droppedRelicSprites.get(payload.relicId);
      if (sprite) {
        try { sprite.destroy(); } catch (_) { /* already destroyed */ }
        this.droppedRelicSprites.delete(payload.relicId);
      }
      this.showNotification('Relic picked up!');
    } else if (payload?.error) {
      this.showNotification(payload.error);
    }
  }

  private onResearchCompleted(payload: any): void {
    if (payload && payload.technologyName) {
      this.showNotification(`Research Complete: ${payload.technologyName}`);
    }
  }

  private onActionClaimWreck(data: { unitId: string; wreckId: string }): void {
    this.mmoNetwork.sendClaimWreck(data.unitId, data.wreckId);
  }


  private onStatusEffectApplied(payload: any): void {
    console.log('[MMOGame] Status effect applied:', payload);

    const effect = {
      effectType: payload.effectType,
      remainingTicks: payload.remainingTicks,
      appliedAt: Date.now(), // Track when applied to handle expired/applied race condition
    };

    // Update the unit sprite's active effects (replace existing same-type, don't push duplicates)
    const unitSprite = this.unitSprites.get(payload.unitId);
    if (unitSprite) {
      if (!unitSprite.activeEffects) {
        unitSprite.activeEffects = [];
      }
      unitSprite.activeEffects = unitSprite.activeEffects.filter(
        (e: any) => e.effectType !== payload.effectType,
      );
      unitSprite.activeEffects.push(effect);
    }

    // Update state manager's unit activeEffects (replace existing same-type)
    const stateUnit = this.mmoState.getUnit(payload.unitId);
    if (stateUnit) {
      if (!stateUnit.activeEffects) {
        stateUnit.activeEffects = [];
      }
      stateUnit.activeEffects = stateUnit.activeEffects.filter(
        (e: any) => e.effectType !== payload.effectType,
      );
      stateUnit.activeEffects.push(effect);
    }
  }

  private onStatusEffectExpired(payload: any): void {
    console.log('[MMOGame] Status effect expired:', payload);

    // When re-activating an ability, server sends expired (old) right after applied (new).
    // Skip removal if the effect was just applied within the last 2 seconds.
    const filterExpired = (effects: any[]) => {
      return effects.filter((e: any) => {
        if (e.effectType !== payload.effectType) return true;
        // If this effect was applied very recently, it's the fresh re-applied one - keep it
        if (e.appliedAt && (Date.now() - e.appliedAt) < 2000) return true;
        return false;
      });
    };

    // Update the unit sprite's active effects
    const unitSprite = this.unitSprites.get(payload.unitId);
    if (unitSprite && unitSprite.activeEffects) {
      unitSprite.activeEffects = filterExpired(unitSprite.activeEffects);
    }

    // Update state manager's unit activeEffects
    const stateUnit = this.mmoState.getUnit(payload.unitId);
    if (stateUnit && stateUnit.activeEffects) {
      stateUnit.activeEffects = filterExpired(stateUnit.activeEffects);
    }
  }

  private isInCullingBounds(x: number, y: number): boolean {
    const { left, right, top, bottom } = this.cullingBounds;
    return x >= left && x <= right && y >= top && y <= bottom;
  }

  private updateSpriteCulling(): void {
    // Toggle visibility of sprites based on whether they're inside the camera viewport
    this.unitSprites.forEach((sprite) => {
      sprite.visible = this.isInCullingBounds(sprite.x, sprite.y);
    });
    this.planetSprites.forEach((sprite) => {
      sprite.visible = this.isInCullingBounds(sprite.x, sprite.y);
    });
    this.asteroidSprites.forEach((sprite) => {
      const inBounds = this.isInCullingBounds(sprite.x, sprite.y);
      sprite.visible = inBounds;
      // Pause decorative tweens on off-screen asteroids to save CPU
      sprite.setTweenActive(inBounds);
    });
    this.stationSprites.forEach((sprite) => {
      sprite.visible = this.isInCullingBounds(sprite.x, sprite.y);
    });
    this.platformSprites.forEach((sprite) => {
      sprite.visible = this.isInCullingBounds(sprite.x, sprite.y);
    });
  }

  private onEngagementStarted(payload: { entityA: string; entityB: string }): void {
    this.engagedUnitIds.add(payload.entityA);
    this.engagedUnitIds.add(payload.entityB);
    this.applyEngagementVisual(payload.entityA, true);
    this.applyEngagementVisual(payload.entityB, true);
  }

  private onEngagementEnded(payload: { entityA: string; entityB: string; reason: string }): void {
    // Don't simply remove from set — the entity may have other engagements.
    // The authoritative state comes from syncEngagementState() on tick updates.
    // For immediate visual feedback, we still remove here (will be corrected next tick).
    this.engagedUnitIds.delete(payload.entityA);
    this.engagedUnitIds.delete(payload.entityB);
    this.applyEngagementVisual(payload.entityA, false);
    this.applyEngagementVisual(payload.entityB, false);

    // Show battle report if player was involved
    const playerId = this.mmoState.getUserId();
    if (playerId) {
      const accKey = [payload.entityA, payload.entityB].sort().join('|');
      const acc = this.combatAccumulators.get(accKey);
      if (acc) {
        // Check if the player owns either unit
        const playerUnitIds = new Set<string>();
        this.unitSprites.forEach((sprite, id) => {
          if (sprite.ownerId === playerId) playerUnitIds.add(id);
        });
        if (playerUnitIds.has(payload.entityA) || playerUnitIds.has(payload.entityB)) {
          // Show battle report as a non-blocking notification instead of a modal dialog
          // The modal overlay blocks all input and confuses players
          const playerIsAttacker = playerUnitIds.has(acc.attackerId);
          const myStats = playerIsAttacker ? acc.attackerStats : acc.defenderStats;
          const enemyStats = playerIsAttacker ? acc.defenderStats : acc.attackerStats;
          const enemyName = playerIsAttacker ? acc.defenderName : acc.attackerName;
          const result = myStats.destroyed ? 'DEFEATED' : enemyStats.destroyed ? 'VICTORY' : 'DISENGAGED';
          const color = myStats.destroyed ? '#ff4444' : enemyStats.destroyed ? '#44ff44' : '#ffcc00';
          const dmgDealt = myStats.totalDamage;
          const dmgTaken = enemyStats.totalDamage;
          this.showNotification(
            `${result} vs ${enemyName} — Dealt: ${dmgDealt} dmg, Taken: ${dmgTaken} dmg (${acc.rounds} rounds)`,
            color, 6000,
          );
        }
        this.combatAccumulators.delete(accKey);
      }
    }
  }

  private syncEngagementState(serverEngagedIds: Set<string>): void {
    // Disengage units no longer in the server's list
    for (const id of this.engagedUnitIds) {
      if (!serverEngagedIds.has(id)) {
        this.applyEngagementVisual(id, false);
      }
    }
    // Engage units that are in the server's list
    for (const id of serverEngagedIds) {
      this.applyEngagementVisual(id, true);
    }
    this.engagedUnitIds = serverEngagedIds;
  }

  private applyEngagementVisual(entityId: string, engaged: boolean): void {
    const unitSprite = this.unitSprites.get(entityId);
    if (unitSprite) {
      unitSprite.setEngaged(engaged);
      return;
    }
    const stationSprite = this.stationSprites.get(entityId);
    if (stationSprite) {
      stationSprite.setEngaged(engaged);
    }
  }

  private onMemorySweep = (): void => {
    // Destroy sprites that are far outside the viewport to free GPU memory
    const camera = this.cameras.main;
    const sweepMargin = camera.width * 3; // 3x viewport width
    const bounds = {
      left: camera.scrollX - sweepMargin,
      right: camera.scrollX + camera.width / camera.zoom + sweepMargin,
      top: camera.scrollY - sweepMargin,
      bottom: camera.scrollY + camera.height / camera.zoom + sweepMargin,
    };

    let destroyed = 0;

    const sweepMap = (map: Map<string, Phaser.GameObjects.Container>) => {
      map.forEach((sprite, id) => {
        if (sprite === this.selectedEntity) return;
        if (sprite.x < bounds.left || sprite.x > bounds.right || sprite.y < bounds.top || sprite.y > bounds.bottom) {
          sprite.destroy();
          map.delete(id);
          destroyed++;
        }
      });
    };

    // Sweep all sprite types (units and planets included now for long sessions)
    sweepMap(this.unitSprites as Map<string, Phaser.GameObjects.Container>);
    sweepMap(this.planetSprites as Map<string, Phaser.GameObjects.Container>);
    sweepMap(this.asteroidSprites as Map<string, Phaser.GameObjects.Container>);
    sweepMap(this.stationSprites as Map<string, Phaser.GameObjects.Container>);
    sweepMap(this.platformSprites as Map<string, Phaser.GameObjects.Container>);

    // Unload distant chunks from state manager
    this.unloadDistantChunks();

    if (destroyed > 0) {
      console.log(`[MMOGame] Memory sweep: destroyed ${destroyed} off-screen sprites`);
    }
  };

  private unloadDistantChunks(): void {
    const { minQ, maxQ, minR, maxR } = this.viewportBounds;

    // Visible chunk range with +2 buffer
    const buffer = 2;
    const minChunkX = Math.floor(minQ / CHUNK_SIZE) - buffer;
    const maxChunkX = Math.floor(maxQ / CHUNK_SIZE) + buffer;
    const minChunkY = Math.floor(minR / CHUNK_SIZE) - buffer;
    const maxChunkY = Math.floor(maxR / CHUNK_SIZE) + buffer;

    let unloaded = 0;
    const chunksToRemove: string[] = [];

    this.loadedChunks.forEach((chunkKey) => {
      const [cx, cy] = chunkKey.split(',').map(Number);
      if (cx < minChunkX || cx > maxChunkX || cy < minChunkY || cy > maxChunkY) {
        chunksToRemove.push(chunkKey);
      }
    });

    for (const chunkKey of chunksToRemove) {
      const [cx, cy] = chunkKey.split(',').map(Number);
      this.mmoState.unloadChunk(cx, cy);
      this.loadedChunks.delete(chunkKey);
      unloaded++;
    }

    if (unloaded > 0) {
      console.log(`[MMOGame] Unloaded ${unloaded} distant chunks`);
    }
  }

  private onMemoryCritical = (): void => {
    // Aggressive cleanup — destroy ALL off-screen sprites
    const camera = this.cameras.main;
    const bounds = {
      left: camera.scrollX,
      right: camera.scrollX + camera.width / camera.zoom,
      top: camera.scrollY,
      bottom: camera.scrollY + camera.height / camera.zoom,
    };

    let destroyed = 0;

    // Sweep all sprite types except the currently selected entity
    const sweepMap = (map: Map<string, Phaser.GameObjects.Container>) => {
      map.forEach((sprite, id) => {
        if (sprite === this.selectedEntity) return;
        if (sprite.x < bounds.left || sprite.x > bounds.right || sprite.y < bounds.top || sprite.y > bounds.bottom) {
          sprite.destroy();
          map.delete(id);
          destroyed++;
        }
      });
    };

    sweepMap(this.unitSprites as Map<string, Phaser.GameObjects.Container>);
    sweepMap(this.planetSprites as Map<string, Phaser.GameObjects.Container>);
    sweepMap(this.asteroidSprites as Map<string, Phaser.GameObjects.Container>);
    sweepMap(this.stationSprites as Map<string, Phaser.GameObjects.Container>);
    sweepMap(this.platformSprites as Map<string, Phaser.GameObjects.Container>);

    // Clear loaded chunks to force re-request when camera moves
    this.loadedChunks.clear();

    console.warn(`[MMOGame] CRITICAL memory sweep: destroyed ${destroyed} sprites, cleared chunk cache`);
  };

  shutdown(): void {
    console.log('[MMOGame] Shutting down...');
    this.removeNetworkHandlers();
    this.removeActionHandlers();

    // Clean up keyboard listeners added in setupInput()
    if (this.input.keyboard) {
      this.input.keyboard.removeAllListeners();
    }

    if (this.chunkRequestTimer) {
      clearTimeout(this.chunkRequestTimer);
    }

    // Clean up document-level event listener
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = null;
    }

    // Clean up combat accumulators (prevents stale data if combat ends without signal)
    this.combatAccumulators.clear();

    // Destroy remaining mining popups
    this.activeMiningPopups.forEach(p => p.destroy());
    this.activeMiningPopups.length = 0;
    this.closeShipRepairPurchasePrompt();

    // Note: Sidebar is managed by MMOHUDScene
  }
}
